import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth, useJobTracker } from '../App';
import AutoSkillCard from '../components/AutoSkillCard';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const COOLDOWN_SECONDS = 0;

// ── Scoring-Anzeige ──
function ScoringDisplay({ scoring, extractedData }) {
  if (!scoring) return null;

  const categories = [
    { key: 'bonitaet', label: 'Bonität (SCHUFA)', max: 25, color: 'var(--brand-gold)' },
    { key: 'einkommen', label: 'Einkommen', max: 25, color: 'var(--brand-success)' },
    { key: 'beschaeftigung', label: 'Beschäftigung', max: 20, color: 'var(--brand-info)' },
    { key: 'dokumente', label: 'Dokumente', max: 15, color: 'var(--brand-navy-50)' },
    { key: 'gesamteindruck', label: 'Gesamteindruck', max: 15, color: 'var(--brand-navy-70)' },
  ];

  const gesamt = scoring.gesamt || 0;
  const scoreColor = gesamt >= 75 ? 'var(--brand-success)' : gesamt >= 50 ? 'var(--brand-warning)' : 'var(--brand-danger)';
  const scoreLabel = gesamt >= 75 ? 'Gut' : gesamt >= 50 ? 'Mittel' : 'Kritisch';

  return (
    <div style={{
      background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)',
      overflow: 'hidden', marginTop: 16,
    }}>
      <div style={{
        padding: '12px 20px', background: 'var(--brand-cream)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Bewerber-Scoring
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-headline)', color: scoreColor }}>
            {gesamt}
          </span>
          <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>/100</span>
          <span style={{
            padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: gesamt >= 75 ? 'var(--brand-success-bg)' : gesamt >= 50 ? 'var(--brand-warning-bg)' : 'var(--brand-danger-bg)',
            color: scoreColor,
          }}>{scoreLabel}</span>
        </div>
      </div>
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {categories.map(cat => {
          const value = scoring[cat.key] || 0;
          const pct = Math.round((value / cat.max) * 100);
          return (
            <div key={cat.key}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--brand-text)' }}>{cat.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)' }}>{value}/{cat.max}</span>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3 }}>
                <div style={{
                  width: `${pct}%`, height: '100%', borderRadius: 3,
                  background: cat.color, transition: 'width 0.5s ease',
                }} />
              </div>
            </div>
          );
        })}
      </div>
      {/* Empfehlung + Nachforderungen */}
      {extractedData && (
        <div style={{ padding: '0 20px 16px' }}>
          {extractedData.empfehlung && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 8,
              background: extractedData.empfehlung === 'ZUSAGE' ? 'var(--brand-success-bg)' :
                         extractedData.empfehlung === 'ABSAGE' ? 'var(--brand-danger-bg)' :
                         'var(--brand-gold-10, rgba(196,151,90,0.1))',
              border: `1px solid ${extractedData.empfehlung === 'ZUSAGE' ? 'var(--brand-success)' :
                         extractedData.empfehlung === 'ABSAGE' ? 'var(--brand-danger)' :
                         'var(--brand-gold)'}`,
            }}>
              <div style={{
                fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                color: extractedData.empfehlung === 'ZUSAGE' ? 'var(--brand-success)' :
                       extractedData.empfehlung === 'ABSAGE' ? 'var(--brand-danger)' :
                       'var(--brand-gold)',
              }}>
                {(extractedData.empfehlung || '').replace(/_/g, ' ')}
              </div>
            </div>
          )}
          {extractedData.nachforderungen && extractedData.nachforderungen.length > 0 && extractedData.nachforderungen[0] !== '' && (
            <div style={{
              padding: '10px 14px', borderRadius: 6,
              background: '#FFF8E7', border: '1px solid #F0D060',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8B6914', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Nachzufordernde Unterlagen
              </div>
              {extractedData.nachforderungen.map((n, i) => (
                <div key={i} style={{ fontSize: 12, color: '#5C4610', lineHeight: 1.5, paddingLeft: 12, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0 }}>-</span> {n}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline-Formularfeld (stabile Referenz, kein Fokusverlust) ──
function InlineField({ label, required, value, onChange, error, type, placeholder, maxLength, mono }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: 'var(--brand-gold)', marginLeft: 2 }}>*</span>}
      </label>
      <input
        type={type || 'text'} value={value} onChange={onChange} placeholder={placeholder || ''} maxLength={maxLength}
        style={{
          width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
          border: error ? '2px solid var(--brand-danger)' : '1px solid var(--brand-warm-gray)',
          fontFamily: mono ? 'monospace' : 'var(--font-body)',
          background: error ? 'var(--brand-danger-bg)' : '#fff',
          boxSizing: 'border-box',
          minHeight: 44,
        }}
      />
      {error && <div style={{ fontSize: 11, color: 'var(--brand-danger)', marginTop: 3 }}>{error}</div>}
    </div>
  );
}

// ── Digitale Signatur (Skribble) Bereich ──
function ImmowareContactButton({ executionId, token }) {
  const [status, setStatus] = React.useState('idle'); // idle, running, done, error
  const [progress, setProgress] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const pollRef = React.useRef(null);

  // Prüfe ob bereits angelegt (aus Execution-Daten)
  React.useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/skills/executions/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const contact = data.result?.immoware_contact;
          if (contact) {
            setStatus('done');
            setResult(contact);
          }
        }
      } catch (e) {}
    };
    if (executionId) check();
  }, [executionId, token]);

  const handleCreate = async () => {
    setStatus('running');
    setError(null);
    try {
      const res = await fetch(`${API_URL}/immoware/mieteranlage/${executionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Fehler');
        setStatus('error');
        return;
      }
      // Polling starten — nutzt den Mieteranlage-Progress-Endpoint
      const jobId = data.job_id;
      setProgress({ progress: 0, message: 'Mieteranlage wird gestartet...' });
      pollRef.current = setInterval(async () => {
        try {
          const pr = await fetch(`${API_URL}/immoware/mieteranlage/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (pr.ok) {
            const job = await pr.json();
            setProgress({ progress: job.progress, message: job.message });
            if (job.status === 'done') {
              clearInterval(pollRef.current);
              setStatus('done');
              setResult(job.result);
            } else if (job.status === 'error') {
              clearInterval(pollRef.current);
              setStatus('error');
              setError(job.message);
            }
          }
        } catch (e) {}
      }, 2000);
    } catch (e) {
      setError(e.message);
      setStatus('error');
    }
  };

  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        height: 1, background: 'var(--brand-warm-gray)', marginBottom: 16,
      }} />

      {status === 'idle' && (
        <div>
          <div style={{ fontSize: 14, color: 'var(--brand-navy)', fontWeight: 500, marginBottom: 8 }}>
            Mieteranlage in Immoware24
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 12 }}>
            Legt den Mieter komplett in Immoware24 an: Kontakt mit Kontaktdaten,
            SEPA-Mandat, Mietverhaeltnis in der Einheit und Dokumente in der Mieterakte.
          </div>
          <button onClick={handleCreate} style={{
            padding: '10px 20px', fontSize: 12, fontWeight: 600,
            background: 'var(--brand-navy)', color: 'var(--brand-gold)',
            border: 'none', borderRadius: 4, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
          }}>Mieteranlage starten</button>
        </div>
      )}

      {status === 'running' && progress && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{progress.message}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{progress.progress}%</span>
          </div>
          <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${progress.progress}%`, height: '100%',
              background: 'var(--brand-gold)', borderRadius: 3,
              transition: 'width 0.8s ease',
            }} />
          </div>
        </div>
      )}

      {status === 'done' && result && (
        <div style={{
          padding: '12px 16px', background: 'var(--brand-success-bg)',
          border: '1px solid var(--brand-success)', borderRadius: 6,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-success)', marginBottom: 4 }}>
            {result.summary || `Mieter angelegt: ${result.contact_name} (ID ${result.contact_id})`}
          </div>
          {result.aenderungen && result.aenderungen.length > 0 && (
            <ul style={{ fontSize: 11, color: 'var(--brand-text)', margin: '4px 0 0 16px', padding: 0 }}>
              {result.aenderungen.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}
          {result.dokumente_abgelegt && result.dokumente_abgelegt.length > 0 && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--brand-text-muted)' }}>
              {result.dokumente_abgelegt.length} Dokument{result.dokumente_abgelegt.length !== 1 ? 'e' : ''} in Mieterakte abgelegt
            </div>
          )}
        </div>
      )}

      {status === 'error' && error && (
        <div style={{
          padding: '10px 14px', background: 'var(--brand-danger-bg)',
          border: '1px solid var(--brand-danger)', borderRadius: 6,
          color: 'var(--brand-danger)', fontSize: 12,
        }}>
          {error}
          <button onClick={() => { setStatus('idle'); setError(null); }} style={{
            marginLeft: 12, padding: '4px 10px', fontSize: 11,
            background: 'transparent', border: '1px solid var(--brand-danger)',
            color: 'var(--brand-danger)', borderRadius: 3, cursor: 'pointer',
          }}>Erneut versuchen</button>
        </div>
      )}
    </div>
  );
}

function SigningSection({ executionId, extractedData, token }) {
  const [dsFiles, setDsFiles] = useState([]);
  const [dsStatus, setDsStatus] = useState('none'); // none, uploaded, sending, sent, signed, error
  const [dsRequest, setDsRequest] = useState(null); // { id, status }
  const [dsError, setDsError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signatureLevel, setSignatureLevel] = useState('qes');

  // Mieterdaten-Formular State
  const [tenantFormStatus, setTenantFormStatus] = useState(null); // null, pending, submitted, not_created
  const [tenantFormFilledBy, setTenantFormFilledBy] = useState(null); // sachbearbeiter, mieter
  const [tenantFormUrl, setTenantFormUrl] = useState('');
  const [tenantFormData, setTenantFormData] = useState(null);
  const [tenantFormLoading, setTenantFormLoading] = useState(false);
  const [tenantLinkCopied, setTenantLinkCopied] = useState(false);

  // Sachbearbeiter-Inline-Formular State
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [inlineFormSubmitting, setInlineFormSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false); // true wenn Bearbeiten nach Submit

  // Inline-Formular Felder Mieter 1
  const [formEmailMieter1, setFormEmailMieter1] = useState('');
  const [formTelefonMieter1, setFormTelefonMieter1] = useState('');
  const [formNotfallNameMieter1, setFormNotfallNameMieter1] = useState('');
  const [formNotfallTelefonMieter1, setFormNotfallTelefonMieter1] = useState('');
  const [formIbanMieter1, setFormIbanMieter1] = useState('');
  const [formBicMieter1, setFormBicMieter1] = useState('');
  const [formKontoinhaberMieter1, setFormKontoinhaberMieter1] = useState('');
  // Inline-Formular Felder Mieter 2
  const [formEmailMieter2, setFormEmailMieter2] = useState('');
  const [formTelefonMieter2, setFormTelefonMieter2] = useState('');
  const [formIbanMieter2, setFormIbanMieter2] = useState('');
  const [formBicMieter2, setFormBicMieter2] = useState('');
  const [formKontoinhaberMieter2, setFormKontoinhaberMieter2] = useState('');
  // Allgemein
  const [formNameKlingelschild, setFormNameKlingelschild] = useState(extractedData?.mieter_name || '');
  const [formAnmerkungen, setFormAnmerkungen] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // E-Mail-Felder für Empfänger
  const [mieter1Email, setMieter1Email] = useState(extractedData?.mieter_email || '');
  const [mieter2Email, setMieter2Email] = useState('');
  const [vermieterEmail, setVermieterEmail] = useState('ts@fleet-immobilien.de');

  const mieter1Name = extractedData?.mieter_name || 'Mieter 1';
  const mieter2Name = extractedData?.mieter2_name || '';
  const hasMieter2 = !!(mieter2Name && mieter2Name.trim());
  const eigentuemerName = extractedData?.eigentuemer || '';

  // Tenant form status beim Laden abfragen
  useEffect(() => {
    if (!executionId) return;
    fetch(`${API_URL}/tenant-form/status/${executionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setTenantFormStatus(data.status);
        setTenantFormFilledBy(data.filled_by || null);
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (data.submitted_data) {
          setTenantFormData(data.submitted_data);
          if (data.submitted_data.email_mieter1) setMieter1Email(data.submitted_data.email_mieter1);
          if (data.submitted_data.email_mieter2) setMieter2Email(data.submitted_data.email_mieter2);
        }
      })
      .catch(() => {});
  }, [executionId, token]);

  // Signing status beim Laden abfragen
  useEffect(() => {
    if (!executionId) return;
    fetch(`${API_URL}/signing/status/${executionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        if (data.signing_status === 'sent' || data.signing_status === 'signed') {
          setDsStatus(data.signing_status);
          setDsRequest({ id: data.request_id, status: data.request_status });
        } else if (data.signing_status === 'uploaded') {
          setDsFiles(data.files || []);
          setDsStatus('uploaded');
        }
        if (data.mieter1_email) setMieter1Email(data.mieter1_email);
        if (data.mieter2_email) setMieter2Email(data.mieter2_email);
      })
      .catch(() => {});
  }, [executionId, token]);

  // ── Mieterdaten-Formular Aktionen ──

  const handleCreateTenantForm = async (mode) => {
    setTenantFormLoading(true);
    setDsError(null);
    try {
      const res = await fetch(`${API_URL}/tenant-form/create/${executionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus(data.status || 'pending');
        setTenantFormFilledBy(data.filled_by || mode.replace('_link', ''));
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (mode === 'sachbearbeiter') {
          setShowInlineForm(true);
          setFormNameKlingelschild(extractedData?.mieter_name || '');
        }
      } else {
        setDsError(data.detail || 'Fehler beim Erstellen des Formulars');
      }
    } catch (err) {
      setDsError(err.message);
    }
    setTenantFormLoading(false);
  };

  const handleRefreshTenantForm = async () => {
    try {
      const res = await fetch(`${API_URL}/tenant-form/status/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus(data.status);
        setTenantFormFilledBy(data.filled_by || null);
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (data.submitted_data) {
          setTenantFormData(data.submitted_data);
          if (data.submitted_data.email_mieter1) setMieter1Email(data.submitted_data.email_mieter1);
          if (data.submitted_data.email_mieter2) setMieter2Email(data.submitted_data.email_mieter2);
        }
      }
    } catch (err) {
      console.warn('Tenant-Form Status-Abfrage fehlgeschlagen:', err.message);
    }
  };

  // Auto-Polling: Alle 10s prüfen ob Mieter das Formular ausgefüllt hat (SigningSection)
  useEffect(() => {
    if (tenantFormStatus !== 'pending' || tenantFormFilledBy !== 'mieter' || !executionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/tenant-form/status/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'submitted') {
          setTenantFormStatus('submitted');
          setTenantFormFilledBy(data.filled_by || 'mieter');
          if (data.submitted_data) {
            setTenantFormData(data.submitted_data);
            if (data.submitted_data.email_mieter1) setMieter1Email(data.submitted_data.email_mieter1);
            if (data.submitted_data.email_mieter2) setMieter2Email(data.submitted_data.email_mieter2);
          }
        } else if (data.status === 'expired') {
          setTenantFormStatus('expired');
        }
      } catch (err) { /* Netzwerkfehler ignorieren, nächster Poll versucht es erneut */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [tenantFormStatus, tenantFormFilledBy, executionId, token]);

  const handleCopyTenantLink = () => {
    navigator.clipboard.writeText(tenantFormUrl).then(() => {
      setTenantLinkCopied(true);
      setTimeout(() => setTenantLinkCopied(false), 2000);
    });
  };

  // Inline-Formular Validierung
  const validateInlineForm = () => {
    const errs = {};
    if (!formEmailMieter1.trim()) errs.emailMieter1 = 'Erforderlich';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmailMieter1.trim())) errs.emailMieter1 = 'Ungueltig';
    if (!formTelefonMieter1.trim()) errs.telefonMieter1 = 'Erforderlich';
    if (!formNotfallNameMieter1.trim()) errs.notfallNameMieter1 = 'Erforderlich';
    if (!formNotfallTelefonMieter1.trim()) errs.notfallTelefonMieter1 = 'Erforderlich';
    if (!formNameKlingelschild.trim()) errs.nameKlingelschild = 'Erforderlich';

    if (formIbanMieter1.trim()) {
      const clean = formIbanMieter1.trim().replace(/\s/g, '');
      if (!clean.startsWith('DE') || clean.length !== 22 || !/^[A-Za-z0-9]+$/.test(clean))
        errs.ibanMieter1 = 'Ungueltige IBAN (DE, 22 Zeichen)';
    }

    if (hasMieter2) {
      if (!formEmailMieter2.trim()) errs.emailMieter2 = 'Erforderlich';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmailMieter2.trim())) errs.emailMieter2 = 'Ungueltig';
      if (!formTelefonMieter2.trim()) errs.telefonMieter2 = 'Erforderlich';
    }
    if (formIbanMieter2.trim()) {
      const clean = formIbanMieter2.trim().replace(/\s/g, '');
      if (!clean.startsWith('DE') || clean.length !== 22 || !/^[A-Za-z0-9]+$/.test(clean))
        errs.ibanMieter2 = 'Ungueltige IBAN';
    }

    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const buildFormPayload = () => ({
    email_mieter1: formEmailMieter1.trim(),
    telefon_mieter1: formTelefonMieter1.trim(),
    notfallkontakt_name_mieter1: formNotfallNameMieter1.trim(),
    notfallkontakt_telefon_mieter1: formNotfallTelefonMieter1.trim(),
    name_klingelschild: formNameKlingelschild.trim(),
    iban_mieter1: formIbanMieter1.trim().replace(/\s/g, '') || null,
    bic_mieter1: formBicMieter1.trim() || null,
    kontoinhaber_mieter1: formKontoinhaberMieter1.trim() || null,
    anmerkungen: formAnmerkungen.trim() || null,
    email_mieter2: formEmailMieter2.trim() || null,
    telefon_mieter2: formTelefonMieter2.trim() || null,
    iban_mieter2: formIbanMieter2.trim().replace(/\s/g, '') || null,
    bic_mieter2: formBicMieter2.trim() || null,
    kontoinhaber_mieter2: formKontoinhaberMieter2.trim() || null,
  });

  const handleInlineFormSubmit = async () => {
    if (!validateInlineForm()) return;
    setInlineFormSubmitting(true);
    setDsError(null);

    const url = editMode
      ? `${API_URL}/tenant-form/update/${executionId}`
      : `${API_URL}/tenant-form/submit/${executionId}`;
    const method = editMode ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildFormPayload()),
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus('submitted');
        setTenantFormData(data.submitted_data);
        setShowInlineForm(false);
        setEditMode(false);
        if (data.submitted_data?.email_mieter1) setMieter1Email(data.submitted_data.email_mieter1);
        if (data.submitted_data?.email_mieter2) setMieter2Email(data.submitted_data.email_mieter2);
      } else {
        setDsError(data.detail || 'Fehler beim Speichern');
      }
    } catch (err) {
      setDsError(err.message);
    }
    setInlineFormSubmitting(false);
  };

  const handleEditTenantData = () => {
    if (tenantFormData) {
      setFormEmailMieter1(tenantFormData.email_mieter1 || '');
      setFormTelefonMieter1(tenantFormData.telefon_mieter1 || '');
      setFormNotfallNameMieter1(tenantFormData.notfallkontakt_name_mieter1 || '');
      setFormNotfallTelefonMieter1(tenantFormData.notfallkontakt_telefon_mieter1 || '');
      setFormIbanMieter1(tenantFormData.iban_mieter1 || '');
      setFormBicMieter1(tenantFormData.bic_mieter1 || '');
      setFormKontoinhaberMieter1(tenantFormData.kontoinhaber_mieter1 || '');
      setFormEmailMieter2(tenantFormData.email_mieter2 || '');
      setFormTelefonMieter2(tenantFormData.telefon_mieter2 || '');
      setFormNotfallNameMieter2(tenantFormData.notfallkontakt_name_mieter2 || '');
      setFormNotfallTelefonMieter2(tenantFormData.notfallkontakt_telefon_mieter2 || '');
      setFormIbanMieter2(tenantFormData.iban_mieter2 || '');
      setFormNameKlingelschild(tenantFormData.name_klingelschild || '');
      setFormAnmerkungen(tenantFormData.anmerkungen || '');
    }
    setEditMode(true);
    setShowInlineForm(true);
  };

  const handleSwitchToSelfFill = () => {
    setShowInlineForm(true);
    setFormNameKlingelschild(prev => prev || extractedData?.mieter_name || '');
  };

  const handleBackToChoice = () => {
    setShowInlineForm(false);
    setEditMode(false);
    setFormErrors({});
    setTenantFormStatus(null);
    setTenantFormFilledBy(null);
    setTenantFormUrl('');
  };

  // ── Signing Aktionen ──

  const handleUpload = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);
    setDsError(null);

    const fd = new FormData();
    Array.from(selectedFiles).forEach(f => fd.append('files', f));

    try {
      const res = await fetch(`${API_URL}/signing/upload/${executionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setDsFiles(data.files || []);
        setDsStatus('uploaded');
      } else {
        setDsError(data.detail || 'Upload fehlgeschlagen');
      }
    } catch (err) {
      setDsError(err.message);
    }
    setUploading(false);
  };

  const handleSend = async () => {
    if (!mieter1Email) { setDsError('E-Mail-Adresse von Mieter 1 ist erforderlich.'); return; }
    setDsStatus('sending');
    setDsError(null);

    try {
      const res = await fetch(`${API_URL}/signing/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          execution_id: executionId,
          mieter1_email: mieter1Email,
          mieter2_email: mieter2Email || null,
          vermieter_email: vermieterEmail || null,
          signature_level: signatureLevel,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setDsStatus('sent');
        setDsRequest({ id: data.request_id, status: 'OPEN' });
      } else {
        setDsStatus('uploaded');
        setDsError(data.detail || 'Skribble-Fehler');
      }
    } catch (err) {
      setDsStatus('uploaded');
      setDsError(err.message);
    }
  };

  const handleCheckStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/signing/request-status/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDsRequest({ id: data.request_id, status: data.status });
        if (data.status === 'SIGNED') setDsStatus('signed');
      }
    } catch (err) {}
  };

  const statusLabels = {
    OPEN: 'Offen \u2014 warte auf Unterschriften',
    SIGNED: 'Vollstaendig unterschrieben',
    WITHDRAWN: 'Zurueckgezogen',
  };

  const statusColors = {
    OPEN: 'var(--brand-gold)',
    SIGNED: 'var(--brand-success)',
    WITHDRAWN: 'var(--brand-text-muted)',
  };

  const tenantDataIsComplete = tenantFormStatus === 'submitted';

  return (
    <div style={{
      marginTop: 20, background: '#fff', borderRadius: 8,
      border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
        fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        Digitale Signatur (Skribble)
      </div>

      <div style={{ padding: 20 }}>

        {/* ═══ SCHRITT 1: Mieterdaten ergänzen ═══ */}
        {dsStatus === 'none' && !tenantDataIsComplete && !showInlineForm && (
          <div style={{ marginBottom: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--brand-navy)', marginBottom: 8, fontWeight: 500 }}>
              Mieterdaten ergaenzen
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
              Vor dem Signaturversand muessen Mieterdaten (E-Mail, Telefon, Notfallkontakt, IBAN etc.) ergaenzt werden.
              Diese werden im finalen Dokument mitunterschrieben.
            </div>

            {/* Noch nicht erstellt: Zwei Optionen */}
            {(!tenantFormStatus || tenantFormStatus === 'not_created') && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => handleCreateTenantForm('sachbearbeiter')} disabled={tenantFormLoading} style={{
                  flex: 1, padding: '14px 16px', fontSize: 13, fontWeight: 600,
                  background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                  border: 'none', borderRadius: 4, cursor: tenantFormLoading ? 'wait' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'var(--font-body)',
                  opacity: tenantFormLoading ? 0.6 : 1,
                }}>
                  Selbst ausfuellen
                </button>
                <button onClick={() => handleCreateTenantForm('mieter_link')} disabled={tenantFormLoading} style={{
                  flex: 1, padding: '14px 16px', fontSize: 13, fontWeight: 600,
                  background: '#fff', color: 'var(--brand-navy)',
                  border: '2px solid var(--brand-navy)', borderRadius: 4,
                  cursor: tenantFormLoading ? 'wait' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'var(--font-body)',
                  opacity: tenantFormLoading ? 0.6 : 1,
                }}>
                  Link an Mieter senden
                </button>
              </div>
            )}

            {/* Mieter-Link erstellt, warte auf Mieter */}
            {tenantFormStatus === 'pending' && tenantFormFilledBy === 'mieter' && (
              <div>
                <div style={{
                  padding: '12px 16px', background: 'var(--brand-cream)', borderRadius: 6,
                  border: '1px solid var(--brand-warm-gray)', marginBottom: 12,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Formular-Link
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="text" value={tenantFormUrl} readOnly style={{
                      flex: 1, padding: '8px 10px', fontSize: 11, fontFamily: 'monospace',
                      border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
                      background: '#fff', color: 'var(--brand-text)',
                    }} />
                    <button onClick={handleCopyTenantLink} style={{
                      padding: '8px 16px', fontSize: 11, fontWeight: 600,
                      background: tenantLinkCopied ? 'var(--brand-success)' : 'var(--brand-gold)',
                      color: tenantLinkCopied ? '#fff' : 'var(--brand-navy)',
                      border: 'none', borderRadius: 4, cursor: 'pointer',
                      fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
                      minWidth: 100, transition: 'all 0.2s',
                    }}>
                      {tenantLinkCopied ? 'Kopiert!' : 'Link kopieren'}
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <div style={{
                    display: 'inline-block', padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                    background: 'rgba(196,151,90,0.15)', color: 'var(--brand-gold)', marginBottom: 8,
                  }}>
                    Ausstehend
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 12 }}>
                    Der Mieter hat den Formular-Link noch nicht ausgefuellt.
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button onClick={handleRefreshTenantForm} style={{
                      padding: '8px 20px', fontSize: 12, fontWeight: 500,
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                    }}>Aktualisieren</button>
                    <button onClick={handleSwitchToSelfFill} style={{
                      padding: '8px 20px', fontSize: 12, fontWeight: 500,
                      background: 'transparent', border: '1px solid var(--brand-gold)',
                      color: 'var(--brand-gold)', borderRadius: 4, cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                    }}>Doch selbst ausfuellen</button>
                    <button onClick={handleBackToChoice} style={{
                      padding: '8px 20px', fontSize: 12, fontWeight: 500,
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                      fontFamily: 'var(--font-body)',
                    }}>Zurueck</button>
                  </div>
                </div>
              </div>
            )}

            {/* Sachbearbeiter-Modus pending (sollte direkt Formular zeigen) */}
            {tenantFormStatus === 'pending' && tenantFormFilledBy === 'sachbearbeiter' && !showInlineForm && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowInlineForm(true)} style={{
                  flex: 1, padding: '12px 24px', fontSize: 13, fontWeight: 600,
                  background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                }}>
                  Mieterdaten eintragen
                </button>
                <button onClick={handleBackToChoice} style={{
                  padding: '12px 16px', fontSize: 13, background: 'transparent',
                  border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
                  borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                }}>Zurueck</button>
              </div>
            )}

            {/* Formular abgelaufen */}
            {tenantFormStatus === 'expired' && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
                  Formular abgelaufen
                </div>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 12 }}>
                  Der Link ist nicht mehr gueltig.
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ Sachbearbeiter Inline-Formular ═══ */}
        {showInlineForm && (
          <div style={{
            background: 'var(--brand-cream)', borderRadius: 6, padding: 20,
            border: '1px solid var(--brand-warm-gray)', marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {editMode ? 'Mieterdaten bearbeiten' : 'Mieterdaten eintragen'}
            </div>

            {/* Mieter 1 */}
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
              Mieter 1 {mieter1Name ? `\u2014 ${mieter1Name}` : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <InlineField label="E-Mail" required type="email" value={formEmailMieter1}
                onChange={e => { setFormEmailMieter1(e.target.value); setMieter1Email(e.target.value); setFormErrors(p => ({...p, emailMieter1: undefined})); }}
                placeholder="email@beispiel.de" error={formErrors.emailMieter1} />
              <InlineField label="Telefon" required type="tel" value={formTelefonMieter1}
                onChange={e => { setFormTelefonMieter1(e.target.value); setFormErrors(p => ({...p, telefonMieter1: undefined})); }}
                placeholder="+49 170 1234567" error={formErrors.telefonMieter1} />
              <InlineField label="Notfallkontakt Name" required value={formNotfallNameMieter1}
                onChange={e => { setFormNotfallNameMieter1(e.target.value); setFormErrors(p => ({...p, notfallNameMieter1: undefined})); }}
                placeholder="Name" error={formErrors.notfallNameMieter1} />
              <InlineField label="Notfallkontakt Telefon" required type="tel" value={formNotfallTelefonMieter1}
                onChange={e => { setFormNotfallTelefonMieter1(e.target.value); setFormErrors(p => ({...p, notfallTelefonMieter1: undefined})); }}
                placeholder="+49 170 1234567" error={formErrors.notfallTelefonMieter1} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8, marginBottom: 4 }}>Bankverbindung (SEPA-Lastschrift)</div>
            <InlineField label="IBAN" value={formIbanMieter1}
              onChange={e => { setFormIbanMieter1(e.target.value.toUpperCase()); setFormErrors(p => ({...p, ibanMieter1: undefined})); }}
              placeholder="DE..." maxLength={27} mono error={formErrors.ibanMieter1} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <InlineField label="Kontoinhaber" value={formKontoinhaberMieter1}
                onChange={e => setFormKontoinhaberMieter1(e.target.value)}
                placeholder="Vor- und Nachname" />
              <InlineField label="BIC" value={formBicMieter1}
                onChange={e => setFormBicMieter1(e.target.value.toUpperCase())}
                placeholder="z.B. COBADEFFXXX" maxLength={11} mono />
            </div>

            {/* Mieter 2 */}
            {hasMieter2 && (<>
              <div style={{ height: 1, background: 'var(--brand-warm-gray)', margin: '12px 0 16px' }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                Mieter 2 \u2014 {mieter2Name}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <InlineField label="E-Mail" required type="email" value={formEmailMieter2}
                  onChange={e => { setFormEmailMieter2(e.target.value); setMieter2Email(e.target.value); setFormErrors(p => ({...p, emailMieter2: undefined})); }}
                  placeholder="email@beispiel.de" error={formErrors.emailMieter2} />
                <InlineField label="Telefon" required type="tel" value={formTelefonMieter2}
                  onChange={e => { setFormTelefonMieter2(e.target.value); setFormErrors(p => ({...p, telefonMieter2: undefined})); }}
                  placeholder="+49 170 1234567" error={formErrors.telefonMieter2} />
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8, marginBottom: 4 }}>Bankverbindung Mieter 2</div>
              <InlineField label="IBAN" value={formIbanMieter2}
                onChange={e => { setFormIbanMieter2(e.target.value.toUpperCase()); setFormErrors(p => ({...p, ibanMieter2: undefined})); }}
                placeholder="DE..." maxLength={27} mono error={formErrors.ibanMieter2} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <InlineField label="Kontoinhaber" value={formKontoinhaberMieter2}
                  onChange={e => setFormKontoinhaberMieter2(e.target.value)}
                  placeholder="Vor- und Nachname" />
                <InlineField label="BIC" value={formBicMieter2}
                  onChange={e => setFormBicMieter2(e.target.value.toUpperCase())}
                  placeholder="z.B. COBADEFFXXX" maxLength={11} mono />
              </div>
            </>)}

            {/* Allgemein */}
            <div style={{ height: 1, background: 'var(--brand-warm-gray)', margin: '12px 0 16px' }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
              Allgemein
            </div>
            <InlineField label="Name auf dem Klingelschild" required value={formNameKlingelschild}
              onChange={e => { setFormNameKlingelschild(e.target.value); setFormErrors(p => ({...p, nameKlingelschild: undefined})); }}
              placeholder="Name auf dem Klingelschild" error={formErrors.nameKlingelschild} />
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 3 }}>
                Anmerkungen
              </label>
              <textarea value={formAnmerkungen} onChange={e => setFormAnmerkungen(e.target.value)}
                placeholder="Besondere Wuensche oder Hinweise (optional)"
                style={{
                  width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 4,
                  border: '1px solid var(--brand-warm-gray)', fontFamily: 'var(--font-body)',
                  resize: 'vertical', minHeight: 60, boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleInlineFormSubmit} disabled={inlineFormSubmitting} style={{
                flex: 1, padding: '12px 20px', fontSize: 13, fontWeight: 600,
                background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                border: 'none', borderRadius: 4, cursor: inlineFormSubmitting ? 'wait' : 'pointer',
                textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                opacity: inlineFormSubmitting ? 0.6 : 1,
              }}>
                {inlineFormSubmitting ? 'Wird gespeichert...' : editMode ? 'Aenderungen speichern' : 'Daten speichern'}
              </button>
              <button onClick={editMode ? () => { setShowInlineForm(false); setEditMode(false); setFormErrors({}); } : handleBackToChoice} style={{
                padding: '12px 16px', fontSize: 13, background: 'transparent',
                border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
                borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}>
                {editMode ? 'Abbrechen' : 'Zurueck'}
              </button>
            </div>
          </div>
        )}

        {/* ═══ Mieterdaten eingegangen ═══ */}
        {tenantDataIsComplete && tenantFormData && !showInlineForm && (
          <div style={{ marginBottom: dsStatus === 'none' ? 20 : 0 }}>
            <div style={{
              padding: '12px 16px', background: 'var(--brand-success-bg, #F0FDF4)',
              border: '1px solid var(--brand-success)', borderRadius: 6, marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: 'var(--brand-success-bg)', color: 'var(--brand-success)',
                    border: '1px solid var(--brand-success)',
                  }}>Eingegangen</span>
                  <span style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
                    {tenantFormFilledBy === 'sachbearbeiter' ? '(Sachbearbeiter)' : '(Mieter)'}
                  </span>
                </div>
                <button onClick={handleEditTenantData} style={{
                  padding: '4px 12px', fontSize: 11, fontWeight: 500,
                  background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                  color: 'var(--brand-gold)', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                }}>Bearbeiten</button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--brand-text)' }}>
                {/* Allgemein */}
                {tenantFormData.name_klingelschild && (
                  <div style={{ marginBottom: 8, padding: '6px 10px', background: '#fff', borderRadius: 4, border: '1px solid var(--brand-warm-gray)' }}>
                    <strong>Klingelschild:</strong> {tenantFormData.name_klingelschild}
                  </div>
                )}

                {/* Mieter 1 */}
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Mieter 1</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 8 }}>
                  {tenantFormData.email_mieter1 && <div><strong>E-Mail:</strong> {tenantFormData.email_mieter1}</div>}
                  {tenantFormData.telefon_mieter1 && <div><strong>Telefon:</strong> {tenantFormData.telefon_mieter1}</div>}
                  {tenantFormData.notfallkontakt_name_mieter1 && <div><strong>Notfallkontakt:</strong> {tenantFormData.notfallkontakt_name_mieter1} ({tenantFormData.notfallkontakt_telefon_mieter1})</div>}
                  {tenantFormData.iban_mieter1 && <div><strong>IBAN:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{tenantFormData.iban_mieter1}</span></div>}
                  {tenantFormData.kontoinhaber_mieter1 && <div><strong>Kontoinhaber:</strong> {tenantFormData.kontoinhaber_mieter1}</div>}
                  {tenantFormData.bic_mieter1 && <div><strong>BIC:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{tenantFormData.bic_mieter1}</span></div>}
                </div>

                {/* Mieter 2 */}
                {(tenantFormData.email_mieter2 || tenantFormData.telefon_mieter2) && (<>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Mieter 2</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 8 }}>
                    {tenantFormData.email_mieter2 && <div><strong>E-Mail:</strong> {tenantFormData.email_mieter2}</div>}
                    {tenantFormData.telefon_mieter2 && <div><strong>Telefon:</strong> {tenantFormData.telefon_mieter2}</div>}
                    {tenantFormData.notfallkontakt_name_mieter2 && <div><strong>Notfallkontakt:</strong> {tenantFormData.notfallkontakt_name_mieter2} ({tenantFormData.notfallkontakt_telefon_mieter2 || ''})</div>}
                    {tenantFormData.iban_mieter2 && <div><strong>IBAN:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{tenantFormData.iban_mieter2}</span></div>}
                    {tenantFormData.kontoinhaber_mieter2 && <div><strong>Kontoinhaber:</strong> {tenantFormData.kontoinhaber_mieter2}</div>}
                    {tenantFormData.bic_mieter2 && <div><strong>BIC:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{tenantFormData.bic_mieter2}</span></div>}
                  </div>
                </>)}

                {/* Anmerkungen */}
                {tenantFormData.anmerkungen && (
                  <div style={{ marginTop: 4, fontStyle: 'italic', color: 'var(--brand-text-muted)' }}>
                    <strong>Anmerkungen:</strong> {tenantFormData.anmerkungen}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ SCHRITT 2: Dokumente hochladen ═══ */}
        {dsStatus === 'none' && tenantDataIsComplete && !showInlineForm && (
          <div>
            <div style={{ fontSize: 14, color: 'var(--brand-navy)', marginBottom: 12, fontWeight: 500 }}>
              Schritt 1: Finale Dokumente hochladen
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
              Laden Sie den finalen Mietvertrag und die Anlage als PDF hoch.
              Diese werden via Skribble zur digitalen Unterschrift versendet.
            </div>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '24px 16px', border: '2px dashed var(--brand-warm-gray)', borderRadius: 8,
              background: 'var(--brand-cream)', cursor: uploading ? 'wait' : 'pointer',
              transition: 'border-color 0.2s',
            }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--brand-gold)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--brand-warm-gray)'; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--brand-warm-gray)'; handleUpload({ target: { files: e.dataTransfer.files } }); }}
            >
              <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }}>+</div>
              <div style={{ fontSize: 13, color: 'var(--brand-navy)', fontWeight: 500 }}>
                {uploading ? 'Wird hochgeladen...' : 'Mietvertrag & Anlage hochladen'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>PDF oder DOCX</div>
              <input type="file" accept=".pdf,.docx" multiple onChange={handleUpload}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
              />
            </label>
          </div>
        )}

        {/* Hochgeladene Dateien */}
        {dsFiles.length > 0 && dsStatus !== 'sent' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Hochgeladene Dokumente
            </div>
            {dsFiles.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                background: 'var(--brand-cream)', borderRadius: 4, marginBottom: 4, fontSize: 12,
              }}>
                {/* Sortier-Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                  <button disabled={i === 0} onClick={() => {
                    const arr = [...dsFiles];
                    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
                    setDsFiles(arr);
                    fetch(`${API_URL}/signing/reorder/${executionId}`, {
                      method: 'PUT',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ order: arr.map(x => x.filename) }),
                    }).catch(() => {});
                  }} style={{
                    width: 18, height: 14, padding: 0, border: 'none', cursor: i === 0 ? 'default' : 'pointer',
                    background: 'transparent', color: i === 0 ? 'var(--brand-warm-gray)' : 'var(--brand-navy)',
                    fontSize: 10, lineHeight: 1, fontFamily: 'var(--font-body)',
                  }}>▲</button>
                  <button disabled={i === dsFiles.length - 1} onClick={() => {
                    const arr = [...dsFiles];
                    [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
                    setDsFiles(arr);
                    fetch(`${API_URL}/signing/reorder/${executionId}`, {
                      method: 'PUT',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ order: arr.map(x => x.filename) }),
                    }).catch(() => {});
                  }} style={{
                    width: 18, height: 14, padding: 0, border: 'none', cursor: i === dsFiles.length - 1 ? 'default' : 'pointer',
                    background: 'transparent', color: i === dsFiles.length - 1 ? 'var(--brand-warm-gray)' : 'var(--brand-navy)',
                    fontSize: 10, lineHeight: 1, fontFamily: 'var(--font-body)',
                  }}>▼</button>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  padding: '1px 6px', background: f.type === 'mietvertrag' ? 'var(--brand-gold)' : f.type === 'sepa' ? '#4A90D9' : f.type === 'datenbogen' ? '#7B68EE' : 'var(--brand-navy)',
                  color: f.type === 'mietvertrag' ? 'var(--brand-navy)' : '#fff', borderRadius: 2 }}>
                  {f.type}
                </span>
                <span style={{ flex: 1, color: 'var(--brand-navy)' }}>{f.filename}</span>
                <button onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/signing/file/${executionId}/${i}`, {
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (!res.ok) return;
                    const blob = await res.blob();
                    window.open(URL.createObjectURL(blob), '_blank');
                  } catch (err) {}
                }} style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-gold)', background: 'transparent', cursor: 'pointer', padding: '2px 8px', border: '1px solid var(--brand-gold)', borderRadius: 3, fontFamily: 'var(--font-body)' }}
                >Vorschau</button>
              </div>
            ))}
            {dsStatus === 'uploaded' && (
              <label style={{ fontSize: 11, color: 'var(--brand-gold)', cursor: 'pointer', marginTop: 4, display: 'inline-block', fontWeight: 500 }}>
                + Weitere Dateien hinzufügen
                <input type="file" accept=".pdf,.docx" multiple onChange={handleUpload}
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
              </label>
            )}
          </div>
        )}

        {/* Empfänger-Konfiguration */}
        {dsStatus === 'uploaded' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Empfänger
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 100, fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)' }}>Mieter 1 *</div>
                <div style={{ flex: 1, fontSize: 12, color: 'var(--brand-text)' }}>{mieter1Name}</div>
                <input type="email" value={mieter1Email} onChange={e => setMieter1Email(e.target.value)}
                  placeholder="E-Mail-Adresse"
                  style={{ width: 220, padding: '6px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)' }}
                />
              </div>
              {mieter2Name && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 100, fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)' }}>Mieter 2</div>
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--brand-text)' }}>{mieter2Name}</div>
                  <input type="email" value={mieter2Email} onChange={e => setMieter2Email(e.target.value)}
                    placeholder="E-Mail-Adresse"
                    style={{ width: 220, padding: '6px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 100, fontSize: 12, fontWeight: 500, color: 'var(--brand-navy)' }}>Vermieter *</div>
                <div style={{ flex: 1, fontSize: 12, color: 'var(--brand-text)' }}>
                  One Dot Immo GmbH
                  {eigentuemerName && <span style={{ color: 'var(--brand-text-muted)', fontSize: 11 }}> (i.V. {eigentuemerName})</span>}
                </div>
                <input type="email" value={vermieterEmail} onChange={e => setVermieterEmail(e.target.value)}
                  style={{ width: 220, padding: '6px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Signatur-Standard */}
        {dsStatus === 'uploaded' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Signatur-Standard
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['ees', 'fes', 'qes'].map(level => (
                <button key={level} onClick={() => setSignatureLevel(level)}
                  style={{
                    flex: 1, padding: '10px 12px', fontSize: 12, fontWeight: 600,
                    background: signatureLevel === level ? 'var(--brand-navy)' : '#fff',
                    color: signatureLevel === level ? 'var(--brand-gold)' : 'var(--brand-text)',
                    border: `1px solid ${signatureLevel === level ? 'var(--brand-navy)' : 'var(--brand-warm-gray)'}`,
                    borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                    textAlign: 'center',
                  }}
                >
                  <div>{level.toUpperCase()}</div>
                  <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, opacity: 0.8 }}>
                    {level === 'ees' ? 'Einfach' : level === 'fes' ? 'Fortgeschritten' : 'Qualifiziert'}
                  </div>
                </button>
              ))}
            </div>
            {signatureLevel === 'qes' && (
              <div style={{ fontSize: 11, color: 'var(--brand-success)', marginTop: 6, fontWeight: 500 }}>
                QES = Hoechste Rechtssicherheit. Empfohlen fuer Mietvertraege mit Kuendigungsverzicht.
              </div>
            )}
          </div>
        )}

        {/* Direktversand */}
        {dsStatus === 'uploaded' && (
          <div style={{ borderTop: '1px solid var(--brand-warm-gray)', paddingTop: 16 }}>
            <div style={{
              padding: '8px 12px', background: 'var(--brand-success-bg)', border: '1px solid var(--brand-success)',
              borderRadius: 4, marginBottom: 14, fontSize: 12, color: 'var(--brand-success)', fontWeight: 600,
            }}>
              Bereit zum Versand. Sachbearbeiter können direkt versenden.
            </div>
            <button onClick={handleSend} style={{
              padding: '14px 28px', fontSize: 14, fontWeight: 600, width: '100%',
              background: 'var(--brand-gold)', color: 'var(--brand-navy)',
              border: 'none', borderRadius: 4, cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}>Zur Unterschrift senden ({signatureLevel.toUpperCase()})</button>
          </div>
        )}

        {/* Sending */}
        {dsStatus === 'sending' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 14, color: 'var(--brand-gold)', fontWeight: 500 }}>Wird an Skribble gesendet...</div>
          </div>
        )}

        {/* Gesendet / Unterschrieben */}
        {(dsStatus === 'sent' || dsStatus === 'signed') && dsRequest && (
          <div>
            <div style={{
              padding: '14px 16px',
              background: 'var(--brand-success-bg)',
              border: '1px solid var(--brand-success)',
              borderRadius: 6, marginBottom: 12,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-success)', marginBottom: 4 }}>
                {dsStatus === 'signed' ? 'Mietvertrag vollstaendig unterschrieben' : 'Mietvertrag zur Unterschrift versendet'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--brand-text)' }}>
                Skribble-ID: <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{dsRequest.id}</span>
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: 'var(--brand-cream)', borderRadius: 6,
            }}>
              <div>
                <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Status: </span>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: statusColors[dsRequest.status] || 'var(--brand-text)',
                }}>
                  {statusLabels[dsRequest.status] || dsRequest.status}
                </span>
              </div>
              <button onClick={handleCheckStatus} style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 500,
                background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}>Aktualisieren</button>
            </div>
          </div>
        )}

        {/* ═══ Kontakt in Immoware24 anlegen ═══ */}
        {dsStatus === 'signed' && (
          <ImmowareContactButton executionId={executionId} token={token} />
        )}

        {/* Fehler */}
        {dsError && (
          <div style={{
            marginTop: 12, padding: '10px 14px', background: 'var(--brand-danger-bg)',
            border: '1px solid var(--brand-danger)', borderRadius: 6,
            color: 'var(--brand-danger)', fontSize: 12,
          }}>
            {dsError}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Objekt-Auswahl Komponente ──
function ObjektSelector({ token, selected, onSelect, disabled, filterMieteOnly, onObjekteLoaded, mahnHistory }) {
  const [objekte, setObjekte] = useState([]);
  const [search, setSearch] = useState('');
  const [filterArt, setFilterArt] = useState(filterMieteOnly ? 'Mietverwaltung' : 'alle');

  useEffect(() => {
    fetch(`${API_URL}/admin/objekte/aktiv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.objekte) { setObjekte(data.objekte); if (onObjekteLoaded) onObjekteLoaded(data.objekte); } })
      .catch(() => {});
  }, [token]);

  // Bei filterMieteOnly: nur Mietverwaltung-Objekte anzeigen
  const basisObjekte = useMemo(() => {
    if (filterMieteOnly) return objekte.filter(o => o.verwaltungsart === 'Mietverwaltung');
    return objekte;
  }, [objekte, filterMieteOnly]);

  // Verwaltungsarten aus Daten ableiten
  const verwaltungsarten = useMemo(() => {
    const arts = [...new Set(basisObjekte.map(o => o.verwaltungsart).filter(Boolean))].sort();
    return arts;
  }, [basisObjekte]);

  // Gefilterte Objekte
  const filtered = useMemo(() => {
    return basisObjekte.filter(o => {
      if (filterArt !== 'alle' && o.verwaltungsart !== filterArt) return false;
      if (search) {
        const s = search.toLowerCase();
        return o.objekt_nr.includes(s) || (o.adresse || '').toLowerCase().includes(s);
      }
      return true;
    });
  }, [objekte, filterArt, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filteredIds = useMemo(() => filtered.map(o => o.immoware_id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedSet.has(id));

  const toggle = (id) => {
    if (selectedSet.has(id)) onSelect(selected.filter(s => s !== id));
    else onSelect([...selected, id]);
  };

  const selectFiltered = () => {
    if (allFilteredSelected) {
      // Deselect all filtered
      onSelect(selected.filter(s => !filteredIds.includes(s)));
    } else {
      // Select all filtered (merge with existing)
      const merged = [...new Set([...selected, ...filteredIds])];
      onSelect(merged);
    }
  };

  const selectByArt = (art) => {
    const ids = basisObjekte.filter(o => o.verwaltungsart === art).map(o => o.immoware_id);
    const allSelected = ids.every(id => selectedSet.has(id));
    if (allSelected) onSelect(selected.filter(s => !ids.includes(s)));
    else onSelect([...new Set([...selected, ...ids])]);
  };

  return (
    <div>
      {/* Schnellauswahl-Buttons */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <button type="button" disabled={disabled}
          onClick={() => onSelect(basisObjekte.map(o => o.immoware_id))}
          style={{
            padding: '6px 14px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
            background: selected.length === basisObjekte.length ? 'var(--brand-navy)' : 'transparent',
            color: selected.length === basisObjekte.length ? 'var(--brand-gold)' : 'var(--brand-navy)',
            border: '1px solid var(--brand-navy)', fontFamily: 'var(--font-body)',
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}
        >Alle ({basisObjekte.length})</button>
        {verwaltungsarten.map(art => {
          const count = basisObjekte.filter(o => o.verwaltungsart === art).length;
          const ids = basisObjekte.filter(o => o.verwaltungsart === art).map(o => o.immoware_id);
          const allSel = ids.every(id => selectedSet.has(id));
          const label = art === 'Mietverwaltung' ? 'Miete' : art === 'WEG-Verwaltung' ? 'WEG' : art === 'WEG mit SE-Verwaltung' ? 'WEG+SE' : art;
          return (
            <button key={art} type="button" disabled={disabled}
              onClick={() => selectByArt(art)}
              style={{
                padding: '6px 14px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
                background: allSel ? 'var(--brand-gold-20)' : 'transparent',
                color: 'var(--brand-navy)', border: '1px solid var(--brand-warm-gray)',
                fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px',
              }}
            >{label} ({count})</button>
          );
        })}
        {selected.length > 0 && (
          <button type="button" disabled={disabled}
            onClick={() => onSelect([])}
            style={{
              padding: '6px 14px', fontSize: 11, fontWeight: 500, borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
              background: 'transparent', color: 'var(--brand-danger)', border: '1px solid var(--brand-danger-bg)',
              fontFamily: 'var(--font-body)',
            }}
          >Auswahl aufheben</button>
        )}
      </div>

      {/* Auswahl-Info */}
      <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 8 }}>
        {selected.length} von {basisObjekte.length} Objekten ausgewählt
      </div>

      {/* Such- und Filter-Leiste */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suche nach Nr. oder Adresse..."
          style={{
            flex: 1, padding: '7px 10px', fontSize: 12, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 4, fontFamily: 'var(--font-body)',
          }}
        />
        <select value={filterArt} onChange={e => setFilterArt(e.target.value)}
          style={{
            padding: '7px 10px', fontSize: 12, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 4, background: '#fff', fontFamily: 'var(--font-body)',
          }}
        >
          <option value="alle">Alle Verwaltungsarten</option>
          {verwaltungsarten.map(art => <option key={art} value={art}>{art}</option>)}
        </select>
      </div>

      {/* Tabelle */}
      <div style={{
        border: '1px solid var(--brand-warm-gray)', borderRadius: 6, overflow: 'hidden',
        maxHeight: 320, overflowY: 'auto',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--brand-cream)', position: 'sticky', top: 0, zIndex: 1 }}>
              <th style={{ padding: '8px 10px', width: 32, textAlign: 'center' }}>
                <input type="checkbox" checked={allFilteredSelected} onChange={selectFiltered} disabled={disabled} />
              </th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nr.</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Adresse</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Art</th>
              {mahnHistory && <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Letzter Mahnlauf</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.immoware_id}
                onClick={() => !disabled && toggle(o.immoware_id)}
                style={{
                  borderTop: '1px solid var(--brand-warm-gray)',
                  background: selectedSet.has(o.immoware_id) ? 'var(--brand-gold-10)' : '#fff',
                  cursor: disabled ? 'default' : 'pointer',
                  transition: 'background 0.1s',
                }}
              >
                <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                  <input type="checkbox" checked={selectedSet.has(o.immoware_id)} readOnly disabled={disabled} />
                </td>
                <td style={{ padding: '6px 10px', fontWeight: 600, color: 'var(--brand-navy)' }}>{o.objekt_nr}</td>
                <td style={{ padding: '6px 10px', color: 'var(--brand-text-muted)' }}>{o.adresse || '—'}</td>
                <td style={{ padding: '6px 10px' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 3,
                    background: o.verwaltungsart === 'Mietverwaltung' ? 'var(--brand-gold-10)' :
                               o.verwaltungsart === 'WEG-Verwaltung' ? 'var(--brand-info-bg)' : 'var(--brand-cream)',
                    color: o.verwaltungsart === 'Mietverwaltung' ? 'var(--brand-gold-dark)' :
                          o.verwaltungsart === 'WEG-Verwaltung' ? 'var(--brand-info)' : 'var(--brand-text-muted)',
                  }}>
                    {o.verwaltungsart === 'Mietverwaltung' ? 'Miete' : o.verwaltungsart === 'WEG-Verwaltung' ? 'WEG' : o.verwaltungsart === 'WEG mit SE-Verwaltung' ? 'WEG+SE' : o.verwaltungsart || '—'}
                  </span>
                </td>
                {mahnHistory && (
                  <td style={{ padding: '6px 10px', fontSize: 11, color: mahnHistory[o.immoware_id] ? 'var(--brand-text)' : 'var(--brand-text-light)' }}>
                    {mahnHistory[o.immoware_id] || '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Kontaktdaten / SEPA / Datenschutz: Drag & Drop Upload ──
const SEPA_DOKUMENTTYPEN = [
  { name: 'Datenschutzrechtliche Einwilligung (Anlage 1)', icon: '🔐' },
  { name: 'E-Mail-Zustimmung / Zustellweg (Anlage 2)', icon: '📧' },
  { name: 'SEPA-Lastschriftmandat', icon: '🏦' },
  { name: 'Kontaktdatenblatt', icon: '👤' },
  { name: 'E-Mail mit Kontaktänderung', icon: '✉️' },
  { name: 'Freitext / Notiz', icon: '📝' },
];

function SEPAUpload({ files, onFilesChange, disabled }) {
  const [dragOver, setDragOver] = useState(false);
  const fileList = files['files'] ? Array.from(files['files']) : [];

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      const currentFiles = files['files'] ? Array.from(files['files']) : [];
      const merged = [...currentFiles, ...dropped];
      const dt = new DataTransfer();
      merged.forEach(f => dt.items.add(f));
      onFilesChange({ files: dt.files });
    }
  };

  const handleFileInput = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) {
      const currentFiles = files['files'] ? Array.from(files['files']) : [];
      const merged = [...currentFiles, ...selected];
      const dt = new DataTransfer();
      merged.forEach(f => dt.items.add(f));
      onFilesChange({ files: dt.files });
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    const updated = fileList.filter((_, i) => i !== index);
    const dt = new DataTransfer();
    updated.forEach(f => dt.items.add(f));
    onFilesChange({ files: dt.files });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const extColor = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return { bg: 'var(--brand-danger-bg)', color: 'var(--brand-danger)' };
    if (['jpg', 'jpeg', 'png'].includes(ext)) return { bg: 'var(--brand-success-bg)', color: 'var(--brand-success)' };
    if (['eml', 'msg'].includes(ext)) return { bg: '#FFF3E0', color: '#E65100' };
    if (ext === 'txt') return { bg: '#E8EAF6', color: '#283593' };
    return { bg: 'var(--brand-info-bg)', color: 'var(--brand-info)' };
  };

  return (
    <div>
      {/* So funktioniert es — 1/2/3 */}
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
        border: '1px solid var(--brand-warm-gray)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
        }}>So funktioniert es</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--brand-text)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>1</span>
            <span>Dokument, E-Mail oder Freitext hochladen (PDF, Foto, .eml/.msg, .txt)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>2</span>
            <span>Extrahierte Daten pruefen, ggf. Zustellweg anpassen</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>3</span>
            <span>Kontakt wird automatisch in Immoware24 aktualisiert; PDFs/Scans werden beim Kontakt abgelegt</span>
          </div>
        </div>
      </div>

      {/* Was kann gescannt werden */}
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
        border: '1px solid var(--brand-warm-gray)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10,
        }}>Erkannte Dokumenttypen</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
          {SEPA_DOKUMENTTYPEN.map(doc => (
            <div key={doc.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>{doc.icon}</span>
              <span style={{ color: 'var(--brand-text)' }}>{doc.name}</span>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--brand-warm-gray)',
          fontSize: 11, color: 'var(--brand-text-muted)', lineHeight: 1.5,
        }}>
          Dokumente, E-Mails oder Textdateien hochladen.
          Claude erkennt automatisch welche Daten enthalten sind — auch Adressaenderungen aus E-Mails — und pflegt alles in immoware24 ein.
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('sepa-file-input').click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
          borderRadius: 8, padding: '2rem', textAlign: 'center',
          background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <input
          id="sepa-file-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.eml,.msg,.txt"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: 32, marginBottom: 8 }}>
          {dragOver ? '\uD83D\uDCE5' : '\uD83D\uDD10'}
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>
          {dragOver ? 'Dateien hier ablegen' : 'Dateien hierher ziehen'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
          oder klicken zum Auswaehlen — PDF, JPG, PNG, E-Mail (.eml), Text
        </div>
      </div>

      {/* Dateiliste */}
      {fileList.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>
            {fileList.length} Datei{fileList.length !== 1 ? 'en' : ''} ausgewaehlt
          </div>
          {fileList.map((f, i) => {
            const ec = extColor(f.name);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', background: '#fff', borderRadius: 4,
                border: '1px solid var(--brand-warm-gray)', marginBottom: 4,
                fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: ec.bg, color: ec.color,
                    textTransform: 'uppercase', flexShrink: 0,
                  }}>
                    {f.name.split('.').pop()}
                  </span>
                  <span style={{ color: 'var(--brand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <span style={{ color: 'var(--brand-text-light)', flexShrink: 0 }}>{formatSize(f.size)}</span>
                </div>
                {!disabled && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--brand-text-light)',
                      cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1, marginLeft: 8,
                    }}
                    onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                    onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                  >&times;</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── SEPA Review Panel: Extrahierte Daten prüfen und bestätigen ──
function SEPAReviewPanel({ result, editData, setEditData, onConfirm }) {
  const data = editData || result.extracted_data || {};
  const unsicher = result.unsicher || [];
  const hinweise = result.hinweise || {};
  const bereiche = result.bereiche || [];
  const isEditing = !!editData;
  const lookup = result.contact_lookup || {};
  const lookupStatus = lookup.status || 'unknown';

  // Modus: 'update' (Bestandskontakt) oder 'create' (Neuanlage)
  // Default: bei not_found UND multiple (= keine eindeutige Identifikation) → create
  // 'found' (eindeutig) → update.
  const noClearMatch = lookupStatus === 'not_found' || lookupStatus === 'multiple';
  const defaultMode = noClearMatch ? 'create' : 'update';
  const [mode, setMode] = useState(defaultMode);
  const [gruppe, setGruppe] = useState('Mieter');

  const startEdit = () => setEditData({ ...result.extracted_data });
  const updateField = (key, value) => setEditData(prev => ({ ...prev, [key]: value }));

  const fieldStyle = (key) => ({
    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
    fontFamily: 'var(--font-body)',
    border: unsicher.includes(key) ? '2px solid var(--brand-warning)' : '1px solid var(--brand-warm-gray)',
    background: unsicher.includes(key) ? 'var(--brand-warning-bg)' : '#fff',
  });

  const labelStyle = {
    fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: 3,
  };

  const renderField = (key, label, value) => (
    <div key={key} style={{ marginBottom: 8 }}>
      <label style={labelStyle}>
        {label}
        {unsicher.includes(key) && <span style={{ color: 'var(--brand-warning)', marginLeft: 4, fontWeight: 700 }}>⚠ unsicher</span>}
      </label>
      {isEditing ? (
        <input value={data[key] || ''} onChange={e => updateField(key, e.target.value)} style={fieldStyle(key)} />
      ) : (
        <div style={{
          padding: '6px 10px', fontSize: 12, borderRadius: 4,
          background: unsicher.includes(key) ? 'var(--brand-warning-bg)' : 'var(--brand-cream)',
          border: unsicher.includes(key) ? '2px solid var(--brand-warning)' : '1px solid var(--brand-warm-gray)',
          color: value ? 'var(--brand-text)' : 'var(--brand-text-light)',
        }}>
          {value || '—'}
        </div>
      )}
      {hinweise[key] && (
        <div style={{ fontSize: 10, color: 'var(--brand-warning)', marginTop: 2, fontStyle: 'italic' }}>
          {hinweise[key]}
        </div>
      )}
    </div>
  );

  const renderCheckField = (key, label) => {
    const val = data[key];
    const isTrue = val === true || val === 'true';
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {isEditing ? (
          <input type="checkbox" checked={isTrue} onChange={e => updateField(key, e.target.checked)}
            style={{ flexShrink: 0 }} />
        ) : (
          <span style={{
            width: 18, height: 18, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, flexShrink: 0,
            background: isTrue ? 'var(--brand-success-bg)' : 'var(--brand-danger-bg)',
            color: isTrue ? 'var(--brand-success)' : 'var(--brand-danger)',
            border: `1px solid ${isTrue ? 'var(--brand-success)' : 'var(--brand-danger)'}`,
          }}>{isTrue ? '✓' : '✗'}</span>
        )}
        <span style={{ fontSize: 12, color: 'var(--brand-text)' }}>{label}</span>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20 }}>
      {/* Header mit Bereiche-Badges */}
      <div style={{
        padding: '12px 16px', background: 'var(--brand-cream)', borderRadius: '8px 8px 0 0',
        border: '1px solid var(--brand-warm-gray)', borderBottom: 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>
            Extrahierte Daten prüfen
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            {bereiche.map(b => (
              <span key={b} style={{
                padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                textTransform: 'uppercase', letterSpacing: '0.3px',
              }}>{b}</span>
            ))}
          </div>
        </div>
        {unsicher.length > 0 && (
          <div style={{
            padding: '6px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: 'var(--brand-warning-bg)', color: 'var(--brand-warning)',
            border: '1px solid var(--brand-warning)',
          }}>
            {unsicher.length} unsichere{unsicher.length === 1 ? 's' : ''} Feld{unsicher.length === 1 ? '' : 'er'}
          </div>
        )}
      </div>

      {/* Lookup-Status & Modus-Auswahl */}
      {(lookupStatus === 'found' || lookupStatus === 'not_found' || lookupStatus === 'multiple') && (
        <div style={{
          padding: '12px 16px',
          background: noClearMatch ? 'var(--brand-warning-bg)' : 'var(--brand-cream)',
          border: '1px solid ' + (noClearMatch ? 'var(--brand-warning)' : 'var(--brand-warm-gray)'),
          borderTop: 'none',
          fontSize: 12,
        }}>
          {lookupStatus === 'found' && (
            <div style={{ color: 'var(--brand-text)' }}>
              <strong style={{ color: 'var(--brand-success)' }}>Kontakt gefunden:</strong>{' '}
              {(lookup.current_data && (
                (lookup.current_data.vorname || '') + ' ' + (lookup.current_data.nachname || '')
              ).trim()) || 'ID ' + lookup.contact_id} (ID {lookup.contact_id})
            </div>
          )}
          {lookupStatus === 'not_found' && (
            <div>
              <div style={{ color: 'var(--brand-warning)', fontWeight: 600, marginBottom: 6 }}>
                ⚠ Kontakt nicht in Immoware24 gefunden
              </div>
              <div style={{ color: 'var(--brand-text-muted)', fontSize: 11, marginBottom: 8, lineHeight: 1.4 }}>
                Suchbegriff: <code>{lookup.query || (data.nachname || '?')}</code>. Du kannst den Kontakt jetzt direkt mit den extrahierten Daten neu anlegen — bitte Gruppe und Daten vorher pruefen.
              </div>
            </div>
          )}
          {lookupStatus === 'multiple' && (
            <div>
              <div style={{ color: 'var(--brand-warning)', fontWeight: 600, marginBottom: 6 }}>
                ⚠ Kein eindeutiger Treffer in Immoware24
              </div>
              <div style={{ color: 'var(--brand-text-muted)', fontSize: 11, marginBottom: 8, lineHeight: 1.4 }}>
                Suchbegriff <code>{lookup.query || (data.nachname || '?')}</code> hat
                {' '}{(lookup.candidates || []).length} Kandidaten geliefert, aber keinen eindeutigen Match auf
                {' '}<strong>{(data.vorname || '') + ' ' + (data.nachname || '')}</strong>. Wenn keiner davon
                der gesuchte Kontakt ist, neu anlegen.
              </div>
            </div>
          )}

          {/* Modus-Toggle */}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: lookupStatus === 'not_found' ? 'not-allowed' : 'pointer', opacity: lookupStatus === 'not_found' ? 0.5 : 1 }}>
              <input
                type="radio"
                name="sepa-mode"
                checked={mode === 'update'}
                disabled={lookupStatus === 'not_found'}
                onChange={() => setMode('update')}
              />
              <span style={{ fontSize: 12 }}>Bestandskontakt aktualisieren</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name="sepa-mode"
                checked={mode === 'create'}
                onChange={() => setMode('create')}
              />
              <span style={{ fontSize: 12 }}>Neuen Kontakt anlegen</span>
            </label>
          </div>

          {/* Gruppen-Auswahl nur bei create */}
          {mode === 'create' && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Gruppe:
              </span>
              <select
                value={gruppe}
                onChange={e => setGruppe(e.target.value)}
                style={{
                  padding: '4px 8px', fontSize: 12, borderRadius: 4,
                  border: '1px solid var(--brand-warm-gray)', background: '#fff',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <option value="Mieter">Mieter</option>
                <option value="Eigentümer">Eigentuemer</option>
                <option value="Sonstige">Sonstige</option>
              </select>
              <span style={{ fontSize: 10, color: 'var(--brand-text-light)', fontStyle: 'italic' }}>
                — Verantwortung bleibt beim Sachbearbeiter.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Datenfelder */}
      <div style={{
        padding: 16, background: '#fff', border: '1px solid var(--brand-warm-gray)',
        borderRadius: '0 0 8px 8px',
      }}>
        {/* Persönliche Daten */}
        {(data.nachname || data.vorname) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
              paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
            }}>Person & Objekt</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {renderField('vorname', 'Vorname', data.vorname)}
              {renderField('nachname', 'Nachname', data.nachname)}
              {renderField('anschrift', 'Anschrift', data.anschrift)}
              {renderField('plz', 'PLZ', data.plz)}
              {renderField('ort', 'Ort', data.ort)}
              {renderField('objekt', 'Objekt', data.objekt)}
              {renderField('einheit', 'Einheit', data.einheit)}
            </div>
          </div>
        )}

        {/* Kontaktdaten */}
        {bereiche.includes('Kontaktdaten') && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
              paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
            }}>Kontaktdaten</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {renderField('email', 'E-Mail', data.email)}
              {renderField('telefon', 'Telefon', data.telefon)}
              {data.telefon_typ && renderField('telefon_typ', 'Typ', data.telefon_typ)}
            </div>
          </div>
        )}

        {/* Zustellweg — Sachbearbeiter wählt selbst */}
        {bereiche.includes('Zustellweg') && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
              paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
            }}>Bevorzugten Zustellungsweg anpassen</div>
            <div style={{
              fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 8,
              fontStyle: 'italic', lineHeight: 1.4,
            }}>
              Nichts auswählen = bestehender Zustellweg in IW24 bleibt unverändert.
              Nur anhaken, wenn die Mail/Info eine Änderung explizit verlangt.
            </div>
            {(() => {
              // Zustellweg als kommagetrennte Codes — IMMER editierbar (nicht nur im Edit-Modus)
              // Default: leer (= keine Änderung). Kein implizites 'e-post' mehr.
              const currentZw = (data.zustellweg || '').split(',').map(s => s.trim()).filter(Boolean);
              const options = [
                { code: 'e-post', label: 'Post (postalisch)' },
                { code: 'e-post-brief', label: 'E-Post-Brief' },
                { code: 'e-mail', label: 'E-Mail' },
                { code: 'portal', label: 'Portalfreigabe' },
                { code: 'e-mail-share', label: 'E-Mail-Share' },
              ];
              // Zustellweg-Änderung: direkt in extracted_data UND editData schreiben
              const setZustellweg = (newCodes) => {
                const val = newCodes.join(',');
                // Immer editData initialisieren wenn noch nicht vorhanden
                if (!editData) {
                  setEditData({ ...result.extracted_data, zustellweg: val });
                } else {
                  updateField('zustellweg', val);
                }
              };
              return options.map(opt => {
                const checked = currentZw.indexOf(opt.code) !== -1;
                return (
                  <div key={opt.code} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input type="checkbox" checked={checked} onChange={e => {
                      let codes = currentZw.filter(c => c && c !== opt.code);
                      if (e.target.checked) codes.push(opt.code);
                      // Wenn e-post gewählt, alle anderen abwählen
                      if (opt.code === 'e-post' && e.target.checked) {
                        codes = ['e-post'];
                      }
                      // Wenn etwas anderes als e-post gewählt, e-post entfernen
                      if (opt.code !== 'e-post' && e.target.checked) {
                        codes = codes.filter(c => c !== 'e-post');
                      }
                      // Leere Auswahl erlaubt → bedeutet "nicht ändern"
                      setZustellweg(codes);
                    }} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--brand-text)' }}>{opt.label}</span>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Datenschutz */}
        {bereiche.includes('Datenschutz') && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
              paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
            }}>Datenschutz (Anlage 1)</div>
            {renderCheckField('datenschutz_einwilligung', 'Einwilligung erteilt')}
            {renderField('datenschutz_datum', 'Datum', data.datenschutz_datum)}
          </div>
        )}

        {/* SEPA */}
        {bereiche.includes('SEPA-Mandat') && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
              textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
              paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
            }}>SEPA-Mandat</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {renderField('kontoinhaber', 'Kontoinhaber', data.kontoinhaber)}
              {renderField('iban', 'IBAN', data.iban)}
              {renderField('bic', 'BIC', data.bic)}
              {renderField('sepa_datum', 'Unterschriftsdatum', data.sepa_datum)}
              {renderField('sepa_anfangsdatum', 'Anfangsdatum Mandat', data.sepa_anfangsdatum || data.sepa_datum)}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--brand-warm-gray)' }}>
          {!isEditing ? (
            <>
              <button onClick={startEdit}
                style={{
                  padding: '10px 20px', fontSize: 12, fontWeight: 600,
                  background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                  color: 'var(--brand-navy)', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                }}
                onMouseEnter={e => e.target.style.borderColor = 'var(--brand-gold)'}
                onMouseLeave={e => e.target.style.borderColor = 'var(--brand-warm-gray)'}
              >Daten bearbeiten</button>
              <button onClick={() => onConfirm(data, mode, gruppe)}
                style={{
                  padding: '10px 20px', fontSize: 12, fontWeight: 600,
                  background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                }}
              >{mode === 'create' ? `Kontakt neu anlegen (${gruppe})` : 'Daten bestätigen & einpflegen'}</button>
            </>
          ) : (
            <>
              <button onClick={() => setEditData(null)}
                style={{
                  padding: '10px 20px', fontSize: 12, fontWeight: 600,
                  background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                  color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                }}
              >Abbrechen</button>
              <button onClick={() => onConfirm(editData, mode, gruppe)}
                style={{
                  padding: '10px 20px', fontSize: 12, fontWeight: 600,
                  background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                  border: 'none', borderRadius: 4, cursor: 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                }}
              >{mode === 'create' ? `Änderungen übernehmen & Kontakt anlegen (${gruppe})` : 'Änderungen bestätigen & einpflegen'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Mietvertragsprüfung: Drag & Drop Upload ──
const PFLICHTUNTERLAGEN = [
  { name: 'Vermietungsaufgabe (Excel/PDF)', required: true },
  { name: 'SCHUFA-Auskunft', required: true },
  { name: 'Personalausweis / Reisepass', required: true },
  { name: 'Gehaltsnachweise (letzte 3 Monate)', required: true },
  { name: 'Selbstauskunft', required: true },
  { name: 'Arbeitsvertrag / Einkommensnachweis', required: false },
  { name: 'Mietschuldenfreiheitsbescheinigung', required: false, bonus: true },
  { name: 'Bürgschaft (falls vorhanden)', required: false },
];

// Einfacher Markdown-zu-HTML Renderer für Prüfberichte
function MarkdownReport({ text }) {
  // Lazy-import DOMPurify to sanitize AI-generated HTML (XSS prevention)
  const DOMPurify = require('dompurify');
  // Leerzeilen bereinigen bevor Markdown geparst wird
  const cleaned = text
    .replace(/\n{3,}/g, '\n\n')        // Max 1 Leerzeile
    .replace(/[ \t]+$/gm, '')          // Trailing whitespace
    .replace(/^\s*\n/gm, '\n')         // Leere Zeilen normalisieren
    .trim();

  const rawHtml = cleaned
    // Tabellen
    .replace(/\|(.+)\|\n\|[-| :]+\|\n/g, (match, header) => {
      const cols = header.split('|').map(s => s.trim()).filter(Boolean);
      return `<table><thead><tr>${cols.map(c => `<th>${c}</th>`).join('')}</tr></thead><tbody>`;
    })
    .replace(/\|(.+)\|/g, (match, row) => {
      const cols = row.split('|').map(s => s.trim()).filter(Boolean);
      if (cols.some(c => c.match(/^[-:]+$/))) return '';
      return `<tr>${cols.map(c => `<td>${c}</td>`).join('')}</tr>`;
    })
    .replace(/<\/tr>\n(?!<tr|<\/tbody)/g, '</tr></tbody></table>\n')
    // Headers (mit Abstand)
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 6px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:16px 0 8px">$1</h3>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Listen
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul style="margin:4px 0">$&</ul>')
    // Absaetze: Doppelte Leerzeilen → Absatzwechsel, einzelne → Zeilenumbruch
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>');

  // Sanitize: strip script tags, event handlers, etc.
  const html = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ['table', 'thead', 'tbody', 'tr', 'th', 'td', 'h3', 'h4', 'strong', 'em', 'li', 'ul', 'ol', 'br', 'p', 'div', 'span', 'hr'],
    ALLOWED_ATTR: ['style', 'class'],
  });

  return (
    <div
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--brand-text)' }}
      className="pruefbericht"
    />
  );
}

// ── Schwarzes Brett: Eingabe mit Drag & Drop + Objekte + Vorlagen ──
function SchwarzesBrettInput({ token, selectedObjekte, setSelectedObjekte, onObjekteLoaded, status, setStatus, setResult, setError, startTracking, setActiveJobId, setExecutionId, isDisabled }) {
  const [dragOver, setDragOver] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [vorlage, setVorlage] = useState('');
  const disabled = isDisabled;

  const vorlagen = [
    { value: '', label: '— Keine Vorlage (freier Text / E-Mail) —' },
    { value: 'notfall', label: 'Notfall / Havarie (Rohrbruch, Stromausfall, Gas...)' },
    { value: 'schaden', label: 'Schaden / Instandsetzung (Aufzug, Fassade, Dach...)' },
    { value: 'dienstleisterwechsel', label: 'Wechsel Dienstleister (Hausmeister, Reinigung...)' },
    { value: 'baumassnahme', label: 'Baumaßnahmen / Modernisierung (Balkon, Fenster...)' },
    { value: 'sicherheit', label: 'Sicherheit / Ordnung (Brandschutz, Rauchmelder...)' },
    { value: 'versorgung', label: 'Versorgung / Zähler (Wasser, Heizung, Strom...)' },
    { value: 'versammlung', label: 'Versammlung / Termin (ETV, Sprechstunde...)' },
    { value: 'allgemein', label: 'Allgemeine Information (Müll, Hausordnung...)' },
  ];

  const vorlagenTexte = {
    notfall: 'NOTFALL: [Art des Notfalls]\n\nWas ist passiert: [Beschreibung]\nBetroffener Bereich: [z.B. Keller, Treppenhaus]\nNotdienst vor Ort: [Firma]\n\nBitte beachten: [Anweisungen für Bewohner]',
    schaden: 'Schaden/Reparatur\n\nSchaden: [Beschreibung]\nZeitraum der Arbeiten: [Datum] bis [Datum]\nFirma: [Name]\n\nAuswirkungen für Bewohner: [z.B. Lärm, kein Aufzug]',
    dienstleisterwechsel: 'Neuer Dienstleister für [Leistung]\n\nAb dem [Datum] übernimmt: [Firma]\nKontaktperson: [Name]\n\nWas ändert sich: [Details]',
    baumassnahme: 'Baumaßnahme\n\nMaßnahme: [Beschreibung]\nZeitplan: [Datum] bis [Datum]\nFirma: [Name]\nBauleitung: [Name]\n\nBitte beachten: [Balkone räumen, Zugang gewähren etc.]',
    sicherheit: 'Sicherheitshinweis\n\nAnlass: [z.B. Brandschutzbegehung, Rauchmelderprüfung]\nTermin: [Datum], [Uhrzeit]\nDurchführung: [Firma]\n\nBitte stellen Sie sicher: [Zugang, Fluchtwege frei etc.]',
    versorgung: 'Geplante [Wasser/Strom/Heizungs]-Abstellung\n\nTermin: [Datum], [Uhrzeit]\nDauer: ca. [X] Stunden\nGrund: [Wartung/Austausch/Reparatur]\nFirma: [Name]',
    versammlung: 'Einladung: [Eigentümerversammlung/Mietersprechstunde]\n\nDatum: [TT.MM.JJJJ]\nUhrzeit: [HH:MM] Uhr\nOrt: [Raum/Adresse]\n\nThemen:\n1. [TOP 1]\n2. [TOP 2]\n3. Verschiedenes',
    allgemein: 'Information für die Bewohner\n\nBetreff: [Thema]\n\n[Details der Mitteilung]\n\nAb wann: [Datum]',
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const newFiles = Array.from(e.dataTransfer.files);
    setFileList(prev => [...prev, ...newFiles]);
  };

  const handleFileInput = (e) => {
    const newFiles = Array.from(e.target.files);
    setFileList(prev => [...prev, ...newFiles]);
  };

  const removeFile = (idx) => {
    setFileList(prev => prev.filter((_, i) => i !== idx));
  };

  const handleVorlageChange = (v) => {
    setVorlage(v);
    if (v && vorlagenTexte[v]) {
      setTextInput(vorlagenTexte[v]);
    }
  };

  const handleSubmit = async () => {
    if (selectedObjekte.length === 0) { setError('Bitte mindestens ein Objekt auswählen.'); return; }
    if (fileList.length === 0 && !textInput.trim()) { setError('Bitte eine Datei hochladen oder Text eingeben.'); return; }

    setStatus('running');
    setError(null);

    const fd = new FormData();
    const data = {
      _phase: 'analyse',
      email_text: textInput,
      objekt_ids: selectedObjekte.join(', '),
      vorlage_kategorie: vorlage || '',
    };
    fd.append('data', JSON.stringify(data));
    fileList.forEach(f => fd.append('files', f));

    try {
      const res = await fetch(`${API_URL}/skills/schwarzes-brett/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const result = await res.json();
      if (res.ok) {
        if (result.execution_id) setExecutionId(result.execution_id);
        if (result.result?.job_id) {
          setActiveJobId(result.result.job_id);
          startTracking(result.result.job_id, 'schwarzes-brett');
        } else {
          setStatus('done');
          setResult(result.result);
        }
      } else {
        setError(result.detail?.errors?.join(', ') || result.detail || 'Fehler');
        setStatus('idle');
      }
    } catch (e) {
      setError(e.message);
      setStatus('idle');
    }
  };

  const formatSize = (bytes) => bytes < 1024 ? bytes + ' B' : bytes < 1048576 ? (bytes / 1024).toFixed(1) + ' KB' : (bytes / 1048576).toFixed(1) + ' MB';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Objekt-Auswahl */}
      <ObjektSelector
        token={token}
        selected={selectedObjekte}
        onSelect={setSelectedObjekte}
        onObjekteLoaded={onObjekteLoaded}
        disabled={disabled}
      />

      {/* Vorlagen-Dropdown */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
          Vorlage (optional)
        </label>
        <select value={vorlage} onChange={e => handleVorlageChange(e.target.value)} disabled={disabled}
          style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', background: '#fff' }}>
          {vorlagen.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
        </select>
      </div>

      {/* Drag & Drop Zone */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
          E-Mail oder Dokument
        </label>
        <div
          onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && document.getElementById('sb-file-input').click()}
          style={{
            border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
            borderRadius: 8, padding: '1.5rem', textAlign: 'center',
            background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <input id="sb-file-input" type="file" accept=".eml,.msg,.txt,.html,.pdf" multiple
            onChange={handleFileInput} style={{ display: 'none' }} />
          <div style={{ fontSize: 28, marginBottom: 6 }}>
            {dragOver ? '\uD83D\uDCE5' : '\uD83D\uDCE7'}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>
            {dragOver ? 'Datei hier ablegen' : 'E-Mail hierher ziehen'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
            oder klicken — EML, MSG, TXT, HTML, PDF
          </div>
        </div>

        {/* Dateiliste */}
        {fileList.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {fileList.map((f, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 10px', background: '#fff', borderRadius: 4,
                border: '1px solid var(--brand-warm-gray)', marginBottom: 3, fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: 'var(--brand-cream)', color: 'var(--brand-navy)', textTransform: 'uppercase' }}>
                    {f.name.split('.').pop()}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <span style={{ color: 'var(--brand-text-light)', flexShrink: 0 }}>{formatSize(f.size)}</span>
                </div>
                {!disabled && (
                  <button onClick={e => { e.stopPropagation(); removeFile(i); }}
                    style={{ background: 'transparent', border: 'none', color: 'var(--brand-text-light)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
                    onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                    onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                  >&times;</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Text-Eingabe */}
      <div>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
          {fileList.length > 0 ? 'Zusätzliche Hinweise (optional)' : 'Oder: Text / E-Mail-Inhalt einfügen'}
        </label>
        <textarea value={textInput} onChange={e => setTextInput(e.target.value)} disabled={disabled}
          rows={vorlage ? 10 : 5}
          placeholder={vorlage ? 'Vorlage anpassen — Platzhalter in [Klammern] ersetzen...' : 'E-Mail-Text hier einfügen oder Sachverhalt beschreiben...'}
          style={{
            width: '100%', padding: '10px 12px', fontSize: 13, lineHeight: 1.6,
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            fontFamily: vorlage ? 'monospace' : 'var(--font-body)',
            background: vorlage ? '#fafafa' : '#fff', boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Submit */}
      <button onClick={handleSubmit}
        disabled={disabled || (selectedObjekte.length === 0) || (fileList.length === 0 && !textInput.trim())}
        style={{
          padding: '10px 24px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 6,
          background: disabled || selectedObjekte.length === 0 ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
          color: '#fff', cursor: disabled || selectedObjekte.length === 0 ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--font-body)', alignSelf: 'flex-end',
        }}
      >
        {status === 'running' ? 'Wird analysiert...' : `Analysieren & Vorschlag erstellen`}
      </button>
    </div>
  );
}

// ── Schwarzes Brett: Vorschlag-Bestaetigung mit Edit ──
const SB_HTML_VORLAGEN = {
  notfall: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>wir informieren Sie über einen <strong>dringenden Vorfall</strong>:</p><p><strong>Was ist passiert:</strong> [Beschreibung]</p><p><strong>Betroffener Bereich:</strong> [Bereich]</p><p><strong>Sofortmaßnahmen:</strong></p><ul><li>[Maßnahme 1]</li><li>[Maßnahme 2]</li></ul><p><strong>Notdienst:</strong> [Firma]</p><p style="color:#8b1a1a;"><strong>Bitte beachten Sie:</strong> [Hinweis]</p><p>Bei akuter Gefahr: <strong>Feuerwehr 112</strong> oder <strong>Polizei 110</strong>.</p><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  schaden: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>wir informieren Sie über eine <strong>anstehende Instandsetzung</strong>:</p><p><strong>Schaden:</strong> [Beschreibung]</p><p><strong>Zeitraum:</strong> [Datum von] bis [Datum bis]</p><p><strong>Firma:</strong> [Name]</p><p><strong>Was bedeutet das für Sie?</strong></p><ul><li>[Auswirkung]</li></ul><p>Wir bitten um Ihr Verständnis.</p><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  dienstleisterwechsel: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>ab dem <strong>[Datum]</strong> übernimmt ein neuer Dienstleister für <strong>[Leistung]</strong>:</p><p><strong>Neue Firma:</strong> [Name]<br/><strong>Kontakt:</strong> [Ansprechpartner]</p><p><strong>Was ändert sich?</strong><br/>[Details]</p><p>Der Wechsel erfolgt nahtlos.</p><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  baumassnahme: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>wir informieren Sie über <strong>geplante Bauarbeiten</strong>:</p><p><strong>Maßnahme:</strong> [Beschreibung]</p><p><strong>Zeitraum:</strong> [Datum von] bis [Datum bis]</p><p><strong>Firma:</strong> [Name]</p><p><strong>Wichtige Hinweise:</strong></p><ul><li>[Hinweis 1]</li><li>[Hinweis 2]</li></ul><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  sicherheit: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>zu Ihrer <strong>Sicherheit</strong> informieren wir Sie:</p><p><strong>Anlass:</strong> [Beschreibung]</p><p><strong>Termin:</strong> [Datum], [Uhrzeit]</p><p><strong>Was müssen Sie tun?</strong></p><ol><li>[Aufgabe 1]</li><li>[Aufgabe 2]</li></ol><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  versorgung: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>am <strong>[Datum]</strong> zwischen <strong>[Uhrzeit]</strong> wird die <strong>[Versorgungsart]</strong> vorübergehend unterbrochen.</p><p><strong>Grund:</strong> [Beschreibung]</p><p><strong>Firma:</strong> [Name]</p><p><strong>Bitte beachten:</strong></p><ul><li>[Hinweis]</li></ul><p>Die Versorgung wird schnellstmöglich wiederhergestellt.</p><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  versammlung: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>wir laden Sie herzlich ein:</p><p><strong>[Veranstaltung]</strong></p><p><strong>Datum:</strong> [TT.MM.JJJJ]<br/><strong>Uhrzeit:</strong> [HH:MM] Uhr<br/><strong>Ort:</strong> [Raum/Adresse]</p><p><strong>Themen:</strong></p><ol><li>[TOP 1]</li><li>[TOP 2]</li><li>Verschiedenes</li></ol><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
  allgemein: '<p><strong>Sehr geehrte Bewohnerinnen und Bewohner,</strong></p><p>wir möchten Sie informieren:</p><p><strong>[Betreff]</strong></p><p>[Details der Mitteilung]</p><p><strong>Ab wann:</strong> [Datum]</p><p>Mit freundlichen Grüßen<br/><strong>One Dot Immo GmbH</strong></p>',
};

// ── Schwarzes Brett: Vorlagen-Manager (Admin/Manager) ──
function SchwarzesBrettVorlagen({ token }) {
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editKategorie, setEditKategorie] = useState(null);
  const [editHtml, setEditHtml] = useState('');
  const [editBetreffs, setEditBetreffs] = useState([]);
  const [editDefaults, setEditDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [newBetreff, setNewBetreff] = useState('');
  const [newDefaultKey, setNewDefaultKey] = useState('');
  const [newDefaultVal, setNewDefaultVal] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/skills/schwarzes-brett/vorlagen`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => { setVorlagen(data); setLoading(false); })
      .catch(err => { setError('Vorlagen konnten nicht geladen werden.'); setLoading(false); });
  }, [token]);

  const openEditor = (v) => {
    setEditKategorie(v.kategorie);
    setEditHtml(v.template_html);
    setEditBetreffs([...(v.beispiel_betreffs || [])]);
    setEditDefaults({ ...(v.defaults || {}) });
    setSaveMsg(null);
    setShowPreview(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API_URL}/skills/schwarzes-brett/vorlagen/${editKategorie}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_html: editHtml,
          beispiel_betreffs: editBetreffs,
          defaults: editDefaults,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Speichern');
      setSaveMsg('Vorlage gespeichert!');
      // Update local state
      setVorlagen(prev => prev.map(v => v.kategorie === editKategorie
        ? { ...v, template_html: editHtml, beispiel_betreffs: editBetreffs, defaults: editDefaults, updated_at: data.updated_at }
        : v
      ));
    } catch (e) {
      setSaveMsg(`Fehler: ${e.message}`);
    }
    setSaving(false);
  };

  const removeBetreff = (idx) => setEditBetreffs(prev => prev.filter((_, i) => i !== idx));
  const addBetreff = () => {
    if (newBetreff.trim()) {
      setEditBetreffs(prev => [...prev, newBetreff.trim()]);
      setNewBetreff('');
    }
  };
  const removeDefault = (key) => setEditDefaults(prev => { const n = { ...prev }; delete n[key]; return n; });
  const addDefault = () => {
    if (newDefaultKey.trim()) {
      setEditDefaults(prev => ({ ...prev, [newDefaultKey.trim()]: newDefaultVal }));
      setNewDefaultKey('');
      setNewDefaultVal('');
    }
  };

  const prioColors = { hoch: '#f44336', mittel: '#ff9800', normal: '#4CAF50', niedrig: '#9e9e9e' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--brand-text-muted)' }}>Vorlagen werden geladen...</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--brand-danger)' }}>{error}</div>;

  // Editor-Ansicht
  if (editKategorie) {
    const vorlage = vorlagen.find(v => v.kategorie === editKategorie);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Back + Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setEditKategorie(null)} style={{
            padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            background: '#fff', color: 'var(--brand-text)', cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>← Zurück</button>
          <div style={{ fontSize: 18, fontFamily: 'var(--font-headline)', color: 'var(--brand-navy)' }}>
            {vorlage?.icon} {vorlage?.display_name}
          </div>
          <span style={{
            padding: '3px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: prioColors[vorlage?.prioritaet] || '#9e9e9e', color: '#fff',
          }}>{vorlage?.prioritaet}</span>
        </div>

        {/* Pflicht-Variablen (readonly) */}
        <div style={{ padding: '10px 14px', background: 'var(--brand-cream)', borderRadius: 6, border: '1px solid var(--brand-warm-gray)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 6 }}>Pflicht-Variablen (nicht editierbar)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(vorlage?.pflicht_vars || []).map(v => (
              <span key={v} style={{ padding: '3px 8px', fontSize: 11, background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, fontFamily: 'monospace' }}>{'{{' + v + '}}'}</span>
            ))}
          </div>
        </div>

        {/* Optionale Variablen (readonly) */}
        <div style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: 6, border: '1px solid var(--brand-warm-gray)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 6 }}>Optionale Variablen</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(vorlage?.optional_vars || []).map(v => (
              <span key={v} style={{ padding: '3px 8px', fontSize: 11, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, fontFamily: 'monospace', color: 'var(--brand-text-muted)' }}>{'{{' + v + '}}'}</span>
            ))}
          </div>
        </div>

        {/* Beispiel-Betreffs */}
        <div>
          <label style={labelStyle}>Beispiel-Betreffs</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {editBetreffs.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={b} onChange={e => setEditBetreffs(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeBetreff(i)} style={{ padding: '6px 10px', fontSize: 14, border: 'none', background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)', borderRadius: 4, cursor: 'pointer', lineHeight: 1 }} title="Entfernen">x</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newBetreff} onChange={e => setNewBetreff(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBetreff()}
                placeholder="Neuen Beispiel-Betreff hinzufügen..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addBetreff} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>+ Hinzufuegen</button>
            </div>
          </div>
        </div>

        {/* HTML-Template */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>HTML-Template</label>
            <button onClick={() => setShowPreview(!showPreview)} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, background: showPreview ? 'var(--brand-navy)' : '#fff', color: showPreview ? '#fff' : 'var(--brand-text)', cursor: 'pointer' }}>
              {showPreview ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>
          {showPreview ? (
            <div style={{ ...inputStyle, minHeight: 300, padding: 16, fontSize: 13, lineHeight: 1.6, overflow: 'auto', background: '#fafafa' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editHtml, { ALLOWED_TAGS: ['h1','h2','h3','h4','p','br','strong','em','b','i','u','ul','ol','li','table','thead','tbody','tr','th','td','div','span','hr','a','img'], ALLOWED_ATTR: ['style','class','href','src','alt'] }) }} />
          ) : (
            <textarea value={editHtml} onChange={e => setEditHtml(e.target.value)} style={{ ...inputStyle, minHeight: 300, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }} />
          )}
        </div>

        {/* Defaults (Key-Value Editor) */}
        <div>
          <label style={labelStyle}>Standard-Werte (Defaults)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(editDefaults).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={k} disabled style={{ ...inputStyle, flex: '0 0 180px', background: '#f5f5f5', fontFamily: 'monospace', fontSize: 12 }} />
                <input value={v} onChange={e => setEditDefaults(prev => ({ ...prev, [k]: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeDefault(k)} style={{ padding: '6px 10px', fontSize: 14, border: 'none', background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)', borderRadius: 4, cursor: 'pointer', lineHeight: 1 }} title="Entfernen">x</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input value={newDefaultKey} onChange={e => setNewDefaultKey(e.target.value)} placeholder="Schluessel" style={{ ...inputStyle, flex: '0 0 180px', fontFamily: 'monospace', fontSize: 12 }} />
              <input value={newDefaultVal} onChange={e => setNewDefaultVal(e.target.value)} placeholder="Wert" onKeyDown={e => e.key === 'Enter' && addDefault()} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addDefault} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>+</button>
            </div>
          </div>
        </div>

        {/* Speichern */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 24px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 4,
            background: saving ? 'var(--brand-warm-gray)' : 'var(--brand-navy)', color: '#fff',
            cursor: saving ? 'default' : 'pointer', fontFamily: 'var(--font-body)',
          }}>{saving ? 'Wird gespeichert...' : 'Vorlage speichern'}</button>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Fehler') ? 'var(--brand-danger)' : 'var(--brand-success)' }}>{saveMsg}</span>}
        </div>
      </div>
    );
  }

  // Karten-Uebersicht
  return (
    <div>
      <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
        Hier können Sie die 8 Schwarzes-Brett-Vorlagen bearbeiten. Änderungen werden sofort für neue Postings wirksam.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {vorlagen.map(v => (
          <div key={v.kategorie} onClick={() => openEditor(v)} style={{
            background: '#fff', borderRadius: 8, padding: '16px 20px', cursor: 'pointer',
            border: '1px solid var(--brand-warm-gray)', transition: 'all 0.15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-gold)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--brand-warm-gray)'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 24 }}>{v.icon}</span>
              <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: prioColors[v.prioritaet] || '#9e9e9e', color: '#fff' }}>{v.prioritaet}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>{v.display_name}</div>
            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
              {(v.pflicht_vars || []).length} Pflicht-Vars | {(v.beispiel_betreffs || []).length} Beispiel-Betreffs
            </div>
            {v.updated_at && (
              <div style={{ fontSize: 10, color: 'var(--brand-text-muted)', marginTop: 6 }}>
                Zuletzt geändert: {new Date(v.updated_at).toLocaleDateString('de-DE')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SchwarzesBrettConfirm({ vorschlag, status, setStatus, setError, setResult, token, startTracking, setActiveJobId }) {
  const [betreff, setBetreff] = useState(vorschlag.betreff || '');
  const [inhalt, setInhalt] = useState(vorschlag.inhalt_html || '');
  const [kategorie, setKategorie] = useState(vorschlag.kategorie || 'allgemein');
  const [nachrichtentyp, setNachrichtentyp] = useState(vorschlag.nachrichtentyp || 'info');
  const [objektIds, setObjektIds] = useState((vorschlag.objekt_ids || []).join(', '));
  const [portalFreigabe, setPortalFreigabe] = useState(vorschlag.portal_freigabe !== false);
  const [whatsappSenden, setWhatsappSenden] = useState(false);
  const [gueltigBis, setGueltigBis] = useState('');
  const [posting, setPosting] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const kategorieLabels = {
    notfall: 'Notfall / Havarie', schaden: 'Schaden / Instandsetzung',
    dienstleisterwechsel: 'Wechsel Dienstleister', baumassnahme: 'Baumaßnahmen',
    sicherheit: 'Sicherheit / Ordnung', versorgung: 'Versorgung / Zähler',
    versammlung: 'Versammlung / Termin', allgemein: 'Allgemeine Information',
  };
  const typLabels = { danger: 'Gefahrenhinweis', warning: 'Warnung', info: 'Info', neutral: 'Neutral' };
  const typColors = { danger: '#f44336', warning: '#ff9800', info: '#2196f3', neutral: '#9e9e9e' };

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/skills/schwarzes-brett/post`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          betreff, inhalt_html: inhalt, kategorie, nachrichtentyp,
          objekt_ids: objektIds, portal_freigabe: portalFreigabe, gueltig_bis: gueltigBis,
          whatsapp_senden: whatsappSenden,
          whatsapp_empfaenger: whatsappSenden ? (vorschlag.whatsapp?.empfaenger_mit_telefon || []) : [],
          erkannte_fakten: vorschlag.erkannte_fakten || {},
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler beim Posten');
        setPosting(false);
        return;
      }
      if (data.result?.job_id) {
        setActiveJobId(data.result.job_id);
        startTracking(data.result.job_id, 'schwarzes-brett');
      }
    } catch (e) {
      setError(e.message);
      setPosting(false);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

  return (
    <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden' }}>
      {/* Header mit Zusammenfassung */}
      <div style={{ padding: '14px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>Schwarzes Brett — Vorschlag</span>
        <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: typColors[nachrichtentyp] || '#9e9e9e', color: '#fff' }}>{typLabels[nachrichtentyp]}</span>
      </div>

      {/* KI-Zusammenfassung */}
      {vorschlag.zusammenfassung && (
        <div style={{ padding: '12px 20px', background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)', fontSize: 13, color: 'var(--brand-text)' }}>
          <strong>KI-Analyse:</strong> {vorschlag.zusammenfassung}
        </div>
      )}

      {/* Erkannte Fakten */}
      {vorschlag.erkannte_fakten && Object.values(vorschlag.erkannte_fakten).some(v => v) && (
        <div style={{ padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid var(--brand-warm-gray)', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {Object.entries(vorschlag.erkannte_fakten).filter(([, v]) => v).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, padding: '3px 8px', background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 3 }}>
              <strong>{k.replace(/_/g, ' ')}:</strong> {v}
            </span>
          ))}
        </div>
      )}

      {/* Editierbare Felder */}
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Kategorie + Typ + Vorlage laden */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div>
            <label style={labelStyle}>Kategorie / Vorlage</label>
            <select value={kategorie} onChange={e => setKategorie(e.target.value)} style={inputStyle}>
              {Object.entries(kategorieLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Nachrichtentyp</label>
            <select value={nachrichtentyp} onChange={e => setNachrichtentyp(e.target.value)} style={inputStyle}>
              {Object.entries(typLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <button onClick={() => { if (SB_HTML_VORLAGEN[kategorie] && window.confirm('Inhalt mit Vorlage ersetzen?')) setInhalt(SB_HTML_VORLAGEN[kategorie]); }}
            style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy)', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}
            title="Ersetzt den Inhalt mit der HTML-Vorlage der gewählten Kategorie"
          >Vorlage laden</button>
        </div>

        {/* Betreff */}
        <div>
          <label style={labelStyle}>Betreff</label>
          <input value={betreff} onChange={e => setBetreff(e.target.value)} style={inputStyle} placeholder="Betreff des Aushangs" />
        </div>

        {/* Inhalt */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={labelStyle}>Inhalt (HTML)</label>
            <button onClick={() => setShowPreview(!showPreview)} style={{ fontSize: 11, padding: '2px 8px', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, background: showPreview ? 'var(--brand-navy)' : '#fff', color: showPreview ? '#fff' : 'var(--brand-text)', cursor: 'pointer' }}>
              {showPreview ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>
          {showPreview ? (
            <div style={{ ...inputStyle, minHeight: 200, padding: 16, fontSize: 13, lineHeight: 1.6, overflow: 'auto', background: '#fafafa' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(inhalt, { ALLOWED_TAGS: ['h1','h2','h3','h4','p','br','strong','em','b','i','u','ul','ol','li','table','thead','tbody','tr','th','td','div','span','hr','a','img'], ALLOWED_ATTR: ['style','class','href','src','alt'] }) }} />
          ) : (
            <textarea value={inhalt} onChange={e => setInhalt(e.target.value)} style={{ ...inputStyle, minHeight: 200, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }} />
          )}
        </div>

        {/* Objekte + Gültig-bis */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Objekt-IDs (kommagetrennt)</label>
            <input value={objektIds} onChange={e => setObjektIds(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Gültig bis (optional)</label>
            <input type="date" value={gueltigBis} onChange={e => setGueltigBis(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Portal-Freigabe + WhatsApp */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={portalFreigabe} onChange={e => setPortalFreigabe(e.target.checked)} />
            Im Portal für Mieter/Eigentümer freigeben
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', flexWrap: 'wrap' }}>
            <input type="checkbox" checked={whatsappSenden} onChange={e => setWhatsappSenden(e.target.checked)}
              disabled={!(vorschlag.whatsapp?.empfaenger_mit_telefon?.length > 0)} />
            Auch per WhatsApp senden
            {vorschlag.whatsapp?.empfaenger_mit_telefon?.length > 0 ? (
              <span style={{ fontSize: 10, padding: '2px 6px', background: '#25D366', color: '#fff', borderRadius: 3, fontWeight: 600 }}>
                {vorschlag.whatsapp.empfaenger_mit_telefon.length} Empfänger
              </span>
            ) : (
              <span style={{ fontSize: 10, padding: '2px 6px', background: 'var(--brand-warm-gray)', color: 'var(--brand-text-muted)', borderRadius: 3 }}>
                keine Mobilnummern gefunden
              </span>
            )}
          </label>
        </div>
      </div>

      {/* Actions */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--brand-warm-gray)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: '#fafafa' }}>
        <button onClick={() => setResult(null)} style={{ padding: '8px 20px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-text)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          Abbrechen
        </button>
        <button onClick={handlePost} disabled={posting || !betreff.trim() || !inhalt.trim()} style={{
          padding: '8px 24px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 4,
          background: posting ? 'var(--brand-warm-gray)' : 'var(--brand-navy)', color: '#fff',
          cursor: posting ? 'default' : 'pointer', fontFamily: 'var(--font-body)',
        }}>
          {posting ? 'Wird gepostet...' : `An ${(objektIds.match(/\d+/g) || []).length} Objekt(e) posten`}
        </button>
      </div>
    </div>
  );
}

// ── Begehungs-Tickets: Editierbare Bestaetigungsansicht ──
function BegehungConfirm({ analyse, ticketsJson, uploadPaths, status, setStatus, setError, setResult, setFiles, token, startTracking, job, setHistoryVersion }) {
  const [currentAnalyse, setCurrentAnalyse] = useState(analyse);
  const [maengel, setMaengel] = useState(analyse.maengel || []);
  const [excluded, setExcluded] = useState(new Set());
  const [editIdx, setEditIdx] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newMangel, setNewMangel] = useState({ titel: '', beschreibung: '', standort_im_objekt: '', massnahme: '', prioritaet: 2, zugewiesen_an: 'Jonas Langenbacher', kosten: '', finanzierung: '' });
  const [feedbackText, setFeedbackText] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const [refineCount, setRefineCount] = useState(0);

  const prioColors = {
    1: { badge: '#f44336', text: 'white', label: 'PRIO 1' },
    2: { badge: '#FFC107', text: 'black', label: 'PRIO 2' },
    3: { badge: '#4CAF50', text: 'white', label: 'PRIO 3' },
  };

  const updateMangel = (idx, field, value) => {
    setMaengel(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const toggleExclude = (idx) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleAddMangel = () => {
    if (!newMangel.titel.trim()) return;
    setMaengel(prev => [...prev, { ...newMangel }]);
    setNewMangel({ titel: '', beschreibung: '', standort_im_objekt: '', massnahme: '', prioritaet: 2, zugewiesen_an: 'Jonas Langenbacher', kosten: '', finanzierung: '' });
    setAddingNew(false);
  };

  const handleRefine = async () => {
    if (!feedbackText.trim()) return;
    setRefining(true);
    setRefineError(null);
    try {
      // Aktuelle manuelle Aenderungen in die Analyse einbauen
      const activeMaengel = maengel.filter((_, i) => !excluded.has(i));
      const currentState = { ...currentAnalyse, maengel: activeMaengel };

      const res = await fetch(`${API_URL}/skills/begehung-tickets/refine`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analyse: currentState,
          feedback: feedbackText,
          upload_paths: uploadPaths || [],
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRefineError(data.detail || 'Fehler bei der Rueckfrage');
        setRefining(false);
        return;
      }
      if (data.result?.job_id) {
        // Poll fuer Ergebnis
        const jobId = data.result.job_id;
        const poll = setInterval(async () => {
          try {
            const pRes = await fetch(`${API_URL}/skills/begehung-tickets/progress/${jobId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const pData = await pRes.json();
            if (pData.status === 'done' && pData.result) {
              clearInterval(poll);
              const newAn = pData.result.analyse;
              setCurrentAnalyse(newAn);
              setMaengel(newAn.maengel || []);
              setExcluded(new Set());
              setEditIdx(null);
              setFeedbackText('');
              setRefining(false);
              setRefineCount(c => c + 1);
            } else if (pData.status === 'error') {
              clearInterval(poll);
              setRefineError(pData.message || 'Fehler bei der Anpassung');
              setRefining(false);
            }
          } catch { /* polling weiter */ }
        }, 1500);
        // Timeout nach 60s
        setTimeout(() => { clearInterval(poll); if (refining) { setRefineError('Timeout'); setRefining(false); } }, 60000);
      }
    } catch (err) {
      setRefineError(err.message);
      setRefining(false);
    }
  };

  const activeCount = maengel.filter((_, i) => !excluded.has(i)).length;

  const handleCreateTickets = async () => {
    setStatus('running');
    setError(null);
    const activeMaengel = maengel.filter((_, i) => !excluded.has(i));
    const updatedAnalyse = { ...currentAnalyse, maengel: activeMaengel };
    try {
      const res = await fetch(`${API_URL}/skills/begehung-tickets/create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tickets_json: ticketsJson,
          updated_analyse: updatedAnalyse,
          upload_paths: uploadPaths || [],
        }),
      });
      const data = await res.json();
      if (res.ok && data.result?.job_id) {
        startTracking(data.result.job_id, 'begehung-tickets');
      } else if (res.ok) {
        setStatus('done');
        setResult(data.result);
        setHistoryVersion(v => v + 1);
      } else {
        setStatus('error');
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler bei der Erstellung');
      }
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const inputStyle = {
    width: '100%', padding: '5px 8px', fontSize: 12, borderRadius: 4,
    border: '1px solid var(--brand-warm-gray)', fontFamily: 'var(--font-body)',
    background: '#fff',
  };

  return (
    <div style={{
      marginTop: 16, background: '#fff', borderRadius: 8,
      border: '1px solid var(--brand-gold)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
        fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>Ticket-Vorschlag pruefen ({activeCount} aktiv){refineCount > 0 ? ` — ${refineCount}x angepasst` : ''}</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>
          {currentAnalyse.objekt} — {currentAnalyse.datum}
        </span>
      </div>

      <div style={{ padding: '8px 20px' }}>
        {maengel.map((m, i) => {
          const isExcluded = excluded.has(i);
          const isEditing = editIdx === i;
          const c = prioColors[m.prioritaet] || prioColors[3];
          return (
            <div key={i} style={{
              padding: '10px 0',
              borderBottom: i < maengel.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none',
              opacity: isExcluded ? 0.4 : 1,
              transition: 'opacity 0.2s',
            }}>
              {isEditing && !isExcluded ? (
                /* ── Editier-Modus ── */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--brand-cream)', padding: 12, borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Titel</label>
                      <input value={m.titel} onChange={e => updateMangel(i, 'titel', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Prio</label>
                      <select value={m.prioritaet} onChange={e => updateMangel(i, 'prioritaet', Number(e.target.value))}
                        style={{ ...inputStyle, padding: '5px 4px' }}>
                        <option value={1}>PRIO 1 – Sofort</option>
                        <option value={2}>PRIO 2 – ETV</option>
                        <option value={3}>PRIO 3 – Langfristig</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Beschreibung</label>
                    <textarea value={m.beschreibung} onChange={e => updateMangel(i, 'beschreibung', e.target.value)}
                      rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Standort</label>
                      <input value={m.standort_im_objekt || ''} onChange={e => updateMangel(i, 'standort_im_objekt', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Zugewiesen an</label>
                      <input value={m.zugewiesen_an || ''} onChange={e => updateMangel(i, 'zugewiesen_an', e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Massnahme</label>
                      <input value={m.massnahme || ''} onChange={e => updateMangel(i, 'massnahme', e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                  <button onClick={() => setEditIdx(null)} style={{
                    alignSelf: 'flex-end', padding: '4px 14px', fontSize: 11, fontWeight: 600,
                    background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                    border: 'none', borderRadius: 3, cursor: 'pointer', fontFamily: 'var(--font-body)',
                  }}>Fertig</button>
                </div>
              ) : (
                /* ── Anzeige-Modus ── */
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 700,
                    background: c.badge, color: c.text, flexShrink: 0,
                    textDecoration: isExcluded ? 'line-through' : 'none',
                  }}>{c.label}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)',
                      textDecoration: isExcluded ? 'line-through' : 'none',
                    }}>{m.titel}</div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text)', marginTop: 2 }}>{m.beschreibung}</div>
                    <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
                      {m.standort_im_objekt && <span>Standort: {m.standort_im_objekt}</span>}
                      {m.zugewiesen_an && <span style={{ marginLeft: 12 }}>Zugewiesen: {m.zugewiesen_an}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {!isExcluded && (
                      <button onClick={() => setEditIdx(i)} title="Bearbeiten" style={{
                        padding: '3px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                        background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                        color: 'var(--brand-gold)', fontFamily: 'var(--font-body)', fontWeight: 600,
                      }}>Bearbeiten</button>
                    )}
                    <button onClick={() => toggleExclude(i)} title={isExcluded ? 'Wiederherstellen' : 'Entfernen'} style={{
                      padding: '3px 8px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: isExcluded ? 'var(--brand-success-bg)' : 'transparent',
                      border: `1px solid ${isExcluded ? 'var(--brand-success)' : 'var(--brand-warm-gray)'}`,
                      color: isExcluded ? 'var(--brand-success)' : 'var(--brand-text-light)',
                      fontFamily: 'var(--font-body)',
                    }}
                      onMouseEnter={e => { if (!isExcluded) { e.target.style.color = 'var(--brand-danger)'; e.target.style.borderColor = 'var(--brand-danger)'; } }}
                      onMouseLeave={e => { if (!isExcluded) { e.target.style.color = 'var(--brand-text-light)'; e.target.style.borderColor = 'var(--brand-warm-gray)'; } }}
                    >{isExcluded ? 'Wiederherstellen' : 'Entfernen'}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* ── Mangel hinzufuegen ── */}
        {addingNew ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: '#f0faf0', padding: 12, borderRadius: 6, marginTop: 8, border: '1px dashed var(--brand-success)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-success)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Neuen Mangel hinzufuegen</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Titel</label>
                <input value={newMangel.titel} onChange={e => setNewMangel(p => ({ ...p, titel: e.target.value }))} style={inputStyle} placeholder="z.B. Riss in der Fassade" />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Prio</label>
                <select value={newMangel.prioritaet} onChange={e => setNewMangel(p => ({ ...p, prioritaet: Number(e.target.value) }))}
                  style={{ ...inputStyle, padding: '5px 4px' }}>
                  <option value={1}>PRIO 1 – Sofort</option>
                  <option value={2}>PRIO 2 – ETV</option>
                  <option value={3}>PRIO 3 – Langfristig</option>
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Beschreibung</label>
              <textarea value={newMangel.beschreibung} onChange={e => setNewMangel(p => ({ ...p, beschreibung: e.target.value }))}
                rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Detaillierte Beschreibung des Mangels" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Standort</label>
                <input value={newMangel.standort_im_objekt} onChange={e => setNewMangel(p => ({ ...p, standort_im_objekt: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Zugewiesen an</label>
                <input value={newMangel.zugewiesen_an} onChange={e => setNewMangel(p => ({ ...p, zugewiesen_an: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Massnahme</label>
                <input value={newMangel.massnahme} onChange={e => setNewMangel(p => ({ ...p, massnahme: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => setAddingNew(false)} style={{
                padding: '4px 14px', fontSize: 11, background: 'transparent',
                border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
                borderRadius: 3, cursor: 'pointer', fontFamily: 'var(--font-body)',
              }}>Abbrechen</button>
              <button onClick={handleAddMangel} disabled={!newMangel.titel.trim()} style={{
                padding: '4px 14px', fontSize: 11, fontWeight: 600,
                background: newMangel.titel.trim() ? 'var(--brand-success)' : 'var(--brand-text-muted)',
                color: '#fff', border: 'none', borderRadius: 3, cursor: newMangel.titel.trim() ? 'pointer' : 'not-allowed',
                fontFamily: 'var(--font-body)',
              }}>Hinzufuegen</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingNew(true)} style={{
            marginTop: 8, padding: '6px 12px', fontSize: 11, fontWeight: 600,
            background: 'transparent', border: '1px dashed var(--brand-success)',
            color: 'var(--brand-success)', borderRadius: 4, cursor: 'pointer',
            fontFamily: 'var(--font-body)', width: '100%',
          }}>+ Mangel hinzufuegen</button>
        )}

        {currentAnalyse.dokumentation_punkte?.length > 0 && (
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--brand-cream)', borderRadius: 4, fontSize: 12, color: 'var(--brand-text-muted)' }}>
            <strong>Nur Dokumentation:</strong> {currentAnalyse.dokumentation_punkte.join(', ')}
          </div>
        )}
      </div>

      {/* ── Rueckfrage an Claude ── */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--brand-warm-gray)', background: 'var(--brand-cream)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
          Rueckfrage / Anpassungswunsch
        </div>
        <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 8 }}>
          Beschreibe hier was Claude anpassen soll — z.B. &quot;Fass Punkt 2 und 3 zusammen&quot;, &quot;Der Schaden am Dach fehlt noch&quot;, &quot;Alles auf Tommy Le zuweisen&quot;
        </div>
        <textarea
          value={feedbackText}
          onChange={e => setFeedbackText(e.target.value)}
          disabled={refining}
          rows={2}
          placeholder="z.B. Punkt 1 und 3 zusammenfassen, Prio von Punkt 2 auf 1 aendern..."
          style={{
            width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 4,
            border: '1px solid var(--brand-warm-gray)', fontFamily: 'var(--font-body)',
            background: refining ? '#f5f5f5' : '#fff', resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
        {refineError && (
          <div style={{ marginTop: 4, fontSize: 11, color: 'var(--brand-danger)' }}>{refineError}</div>
        )}
        <button onClick={handleRefine} disabled={refining || !feedbackText.trim()}
          style={{
            marginTop: 8, padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: (refining || !feedbackText.trim()) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
            color: (refining || !feedbackText.trim()) ? '#fff' : 'var(--brand-navy)',
            border: 'none', borderRadius: 4,
            cursor: (refining || !feedbackText.trim()) ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          {refining ? 'Claude passt an...' : 'Claude anpassen lassen'}
        </button>
        {refining && (
          <div style={{ marginTop: 8 }}>
            <div style={{ width: '100%', height: 4, background: '#e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: '60%', height: '100%', borderRadius: 2,
                background: 'var(--brand-gold)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* Aktions-Buttons */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--brand-warm-gray)', display: 'flex', gap: 8 }}>
        <button onClick={handleCreateTickets} disabled={status === 'running' || activeCount === 0 || refining}
          style={{
            flex: 1, padding: '14px 24px', fontSize: 13, fontWeight: 600,
            background: (status === 'running' || activeCount === 0 || refining) ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
            color: (status === 'running' || activeCount === 0 || refining) ? '#fff' : 'var(--brand-gold)',
            border: 'none', borderRadius: 4,
            cursor: (status === 'running' || activeCount === 0 || refining) ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
          }}
        >
          {status === 'running' ? 'Tickets werden erstellt...' : `${activeCount} Tickets jetzt in Immoware erstellen`}
        </button>
        <button onClick={() => { setResult(null); setStatus('idle'); setFiles({}); }}
          style={{
            padding: '14px 16px', fontSize: 13, background: 'transparent',
            border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
            borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}
        >Abbrechen</button>
      </div>

      {/* Fortschrittsbalken Phase 2 */}
      {job && job.skillType === 'begehung-tickets' && job.status === 'running' && (
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</span>
          </div>
          <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${job.progress}%`, height: '100%',
              background: 'var(--brand-gold)', borderRadius: 3,
              transition: 'width 0.8s ease',
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Begehungs-Tickets: Drag & Drop Upload ──
function BegehungUpload({ files, onFilesChange, disabled }) {
  const [dragOver, setDragOver] = useState(false);
  const fileList = files['protokoll'] ? Array.from(files['protokoll']) : [];

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      const dt = new DataTransfer();
      dropped.forEach(f => dt.items.add(f));
      onFilesChange({ protokoll: dt.files });
    }
  };

  const handleFileInput = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) {
      const dt = new DataTransfer();
      selected.forEach(f => dt.items.add(f));
      onFilesChange({ protokoll: dt.files });
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    const updated = fileList.filter((_, i) => i !== index);
    const dt = new DataTransfer();
    updated.forEach(f => dt.items.add(f));
    onFilesChange({ protokoll: dt.files });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      {/* Hinweis-Box */}
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
        border: '1px solid var(--brand-warm-gray)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
        }}>So funktioniert es</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--brand-text)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>1</span>
            <span>Begehungsprotokoll hochladen (PDF oder Foto)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>2</span>
            <span>Ticket-Vorschlag pruefen und bestaetigen</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
            }}>3</span>
            <span>Tickets werden automatisch in Immoware24 erstellt</span>
          </div>
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('begehung-file-input').click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
          borderRadius: 8, padding: '2rem', textAlign: 'center',
          background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <input
          id="begehung-file-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: 32, marginBottom: 8 }}>
          {dragOver ? '\uD83D\uDCE5' : '\uD83C\uDFD7\uFE0F'}
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>
          {dragOver ? 'Protokoll hier ablegen' : 'Begehungsprotokoll hierher ziehen'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
          oder klicken zum Auswaehlen — PDF, JPG, PNG
        </div>
      </div>

      {/* Dateiliste */}
      {fileList.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>
            {fileList.length} Datei{fileList.length !== 1 ? 'en' : ''} ausgewaehlt
          </div>
          {fileList.map((f, i) => {
            const ext = f.name.split('.').pop().toLowerCase();
            const isPdf = ext === 'pdf';
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', background: '#fff', borderRadius: 4,
                border: '1px solid var(--brand-warm-gray)', marginBottom: 4,
                fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: isPdf ? 'var(--brand-danger-bg)' : 'var(--brand-success-bg)',
                    color: isPdf ? 'var(--brand-danger)' : 'var(--brand-success)',
                    textTransform: 'uppercase', flexShrink: 0,
                  }}>{ext}</span>
                  <span style={{ color: 'var(--brand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <span style={{ color: 'var(--brand-text-light)', flexShrink: 0 }}>{formatSize(f.size)}</span>
                </div>
                {!disabled && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--brand-text-light)',
                      cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1, marginLeft: 8,
                    }}
                    onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                    onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                  >&times;</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MietvertragUpload({ files, onFilesChange, disabled }) {
  const [dragOver, setDragOver] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const fileList = files['files'] ? Array.from(files['files']) : [];
  // Track welche Dateien aus ZIPs kamen (Set von Dateinamen)
  const [fromZip, setFromZip] = useState(new Set());

  const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.xlsx', '.xls'];

  const MIME_TYPES = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg', '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xls': 'application/vnd.ms-excel',
  };

  const extractZip = async (zipFile) => {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(zipFile);
    const extracted = [];
    const zipName = zipFile.name.replace(/\.zip$/i, '');

    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir) continue;
      const name = path.split('/').pop();
      if (!name || name.startsWith('.') || name.startsWith('__')) continue;
      const ext = '.' + name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) continue;

      const arrayBuffer = await entry.async('arraybuffer');
      const mimeType = MIME_TYPES[ext] || 'application/octet-stream';
      const file = new File([arrayBuffer], name, { type: mimeType });
      extracted.push(file);
    }
    return { extracted, zipName };
  };

  const processFiles = async (incoming) => {
    const normal = [];
    const zipFiles = [];
    for (const f of incoming) {
      if (f.name.toLowerCase().endsWith('.zip')) {
        zipFiles.push(f);
      } else {
        normal.push(f);
      }
    }

    let allNew = [...normal];
    const newFromZip = new Set(fromZip);

    if (zipFiles.length > 0) {
      setExtracting(true);
      try {
        for (const zf of zipFiles) {
          const { extracted } = await extractZip(zf);
          for (const ef of extracted) {
            allNew.push(ef);
            newFromZip.add(ef.name);
          }
        }
      } catch (err) {
        console.error('ZIP-Entpackung fehlgeschlagen:', err);
      }
      setExtracting(false);
    }

    if (allNew.length > 0) {
      setFromZip(newFromZip);
      // files prop direkt lesen statt closure-fileList (Async-sicher)
      const currentFiles = files['files'] ? Array.from(files['files']) : [];
      const merged = [...currentFiles, ...allNew];
      const dt = new DataTransfer();
      merged.forEach(f => dt.items.add(f));
      onFilesChange({ files: dt.files });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) processFiles(dropped);
  };

  const handleFileInput = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) processFiles(selected);
    e.target.value = '';
  };

  const removeFile = (index) => {
    const removed = fileList[index];
    const updated = fileList.filter((_, i) => i !== index);
    const dt = new DataTransfer();
    updated.forEach(f => dt.items.add(f));
    onFilesChange({ files: dt.files });
    if (removed && fromZip.has(removed.name)) {
      setFromZip(prev => { const s = new Set(prev); s.delete(removed.name); return s; });
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const extColor = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return { bg: 'var(--brand-danger-bg)', color: 'var(--brand-danger)' };
    if (['jpg', 'jpeg', 'png'].includes(ext)) return { bg: 'var(--brand-success-bg)', color: 'var(--brand-success)' };
    return { bg: 'var(--brand-info-bg)', color: 'var(--brand-info)' };
  };

  return (
    <div>
      {/* Pflichtunterlagen-Checkliste */}
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
        border: '1px solid var(--brand-warm-gray)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10,
        }}>Pflichtunterlagen Bewerbermappe</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
          {PFLICHTUNTERLAGEN.map(doc => (
            <div key={doc.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <span style={{
                width: 16, height: 16, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, flexShrink: 0,
                background: doc.required ? 'var(--brand-danger-bg)' : doc.bonus ? 'var(--brand-success-bg)' : 'var(--brand-cream)',
                color: doc.required ? 'var(--brand-danger)' : doc.bonus ? 'var(--brand-success)' : 'var(--brand-text-light)',
                border: `1px solid ${doc.required ? 'var(--brand-danger)' : doc.bonus ? 'var(--brand-success)' : 'var(--brand-warm-gray)'}`,
              }}>{doc.required ? '!' : doc.bonus ? '+' : '?'}</span>
              <span style={{ color: 'var(--brand-text)' }}>{doc.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && !extracting && document.getElementById('mietvertrag-file-input').click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
          borderRadius: 8, padding: '2rem', textAlign: 'center',
          background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
          cursor: disabled || extracting ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <input
          id="mietvertrag-file-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.zip"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        {extracting ? (<>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-gold)', marginBottom: 4 }}>
            ZIP wird entpackt...
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Dateien werden extrahiert</div>
        </>) : (<>
          <div style={{ fontSize: 32, marginBottom: 8 }}>
            {dragOver ? '\uD83D\uDCE5' : '\uD83D\uDCC4'}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>
            {dragOver ? 'Dateien hier ablegen' : 'Dateien hierher ziehen'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
            oder klicken zum Auswaehlen — PDF, JPG, PNG, Excel, ZIP
          </div>
        </>)}
      </div>

      {/* Dateiliste */}
      {fileList.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>
            {fileList.length} Datei{fileList.length !== 1 ? 'en' : ''} ausgewaehlt
          </div>
          {fileList.map((f, i) => {
            const ec = extColor(f.name);
            const isFromZip = fromZip.has(f.name);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', background: '#fff', borderRadius: 4,
                border: '1px solid var(--brand-warm-gray)', marginBottom: 4,
                fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: ec.bg, color: ec.color,
                    textTransform: 'uppercase', flexShrink: 0,
                  }}>
                    {f.name.split('.').pop()}
                  </span>
                  <span style={{ color: 'var(--brand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  {isFromZip && (
                    <span style={{
                      fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                      background: 'var(--brand-gold-10, rgba(196,151,90,0.1))', color: 'var(--brand-gold)',
                      flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.3px',
                    }}>ZIP</span>
                  )}
                  <span style={{ color: 'var(--brand-text-light)', flexShrink: 0 }}>{formatSize(f.size)}</span>
                </div>
                {!disabled && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--brand-text-light)',
                      cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1, marginLeft: 8,
                    }}
                    onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                    onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                  >&times;</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Mieteranlage Review (Daten prüfen nach Analyse) ──
function MieteranlageReview({ result, executionId, token, onConfirm, selectedDocTypes }) {
  const [editData, setEditData] = useState(() => ({ ...(result.extracted_data || {}) }));
  const unsicher = result.unsicher || [];
  const hinweise = result.hinweise || {};
  const bereiche = result.bereiche || [];
  const docTypes = selectedDocTypes || ['mietvertrag'];

  const updateField = (key, value) => setEditData(prev => ({ ...prev, [key]: value }));

  // Sektion sichtbar wenn DocType ausgewaehlt ODER Bereich von KI erkannt
  // Sektion nur zeigen wenn User den Dokumenttyp ausgewaehlt hat — User-Auswahl hat Vorrang
  const showSection = (docType) => docTypes.includes(docType);

  const fieldStyle = (key) => ({
    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
    fontFamily: 'var(--font-body)',
    border: unsicher.includes(key) ? '2px solid var(--brand-warning)' : '1px solid var(--brand-warm-gray)',
    background: unsicher.includes(key) ? 'var(--brand-warning-bg)' : '#fff',
  });

  const labelStyle = {
    fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: 3,
  };

  const sectionTitle = (title) => (
    <div style={{
      fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)',
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
      paddingBottom: 4, borderBottom: '1px solid var(--brand-warm-gray)',
    }}>{title}</div>
  );

  const renderField = (key, label) => (
    <div key={key} style={{ marginBottom: 8 }}>
      <label style={labelStyle}>
        {label}
        {unsicher.includes(key) && <span style={{ color: 'var(--brand-warning)', marginLeft: 4, fontWeight: 700 }}>⚠ unsicher</span>}
      </label>
      <input value={editData[key] || ''} onChange={e => updateField(key, e.target.value)} style={fieldStyle(key)} />
      {hinweise[key] && (
        <div style={{ fontSize: 10, color: 'var(--brand-warning)', marginTop: 2, fontStyle: 'italic' }}>
          {hinweise[key]}
        </div>
      )}
    </div>
  );

  const renderCheckField = (key, label) => {
    const val = editData[key];
    const isTrue = val === true || val === 'true';
    return (
      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <input type="checkbox" checked={isTrue} onChange={e => updateField(key, e.target.checked)}
          style={{ flexShrink: 0, accentColor: 'var(--brand-gold)' }} />
        <span style={{ fontSize: 12, color: 'var(--brand-text)' }}>{label}</span>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 20 }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', background: 'var(--brand-cream)', borderRadius: '8px 8px 0 0',
        border: '1px solid var(--brand-warm-gray)', borderBottom: 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>
            Daten pruefen & bearbeiten
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {bereiche.map(b => (
              <span key={b} style={{
                padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                textTransform: 'uppercase', letterSpacing: '0.3px',
              }}>{b}</span>
            ))}
          </div>
        </div>
        {unsicher.length > 0 && (
          <div style={{
            padding: '6px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: 'var(--brand-warning-bg)', color: 'var(--brand-warning)',
            border: '1px solid var(--brand-warning)',
          }}>
            {unsicher.length} unsichere{unsicher.length === 1 ? 's' : ''} Feld{unsicher.length === 1 ? '' : 'er'}
          </div>
        )}
      </div>

      {/* Datenfelder — immer editierbar */}
      <div style={{
        padding: 16, background: '#fff', border: '1px solid var(--brand-warm-gray)',
        borderRadius: '0 0 8px 8px',
      }}>
        {/* Mieter 1 — alle persönlichen Daten + Kontakt + Objekt */}
        <div style={{ marginBottom: 16 }}>
          {sectionTitle('Mieter 1')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Anrede</label>
              <select
                value={editData.anrede || ''}
                onChange={e => updateField('anrede', e.target.value)}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                  fontFamily: 'var(--font-body)',
                  border: '1px solid var(--brand-warm-gray)', background: '#fff',
                }}
              >
                <option value="">— bitte waehlen —</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
              </select>
            </div>
            {renderField('vorname', 'Vorname')}
            {renderField('nachname', 'Nachname')}
            {renderField('geburtsdatum', 'Geburtsdatum')}
            {renderField('email', 'E-Mail')}
            {renderField('telefon', 'Telefon')}
            {renderField('telefon_typ', 'Typ (mobil/festnetz)')}
            {renderField('anschrift', 'Bisherige Adresse')}
            {renderField('plz', 'PLZ')}
            {renderField('ort', 'Ort')}
          </div>
        </div>

        {/* Mieter 2 */}
        <div style={{ marginBottom: 16 }}>
          {sectionTitle('Mieter 2 (Gemeinschaftsmietvertrag)')}
          {(() => {
            const wm = editData.weitere_mieter || [];
            const m2 = wm[0] || {};
            const updateMieter2 = (key, value) => {
              const current = (editData.weitere_mieter || [])[0] || {};
              const updated = { ...current, [key]: value };
              updateField('weitere_mieter', [updated]);
            };
            const removeMieter2 = () => updateField('weitere_mieter', []);
            const hasMieter2 = !!(m2.vorname || m2.nachname);
            return (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Vorname</label>
                    <input value={m2.vorname || ''} onChange={e => updateMieter2('vorname', e.target.value)}
                      placeholder="leer lassen wenn kein 2. Mieter"
                      style={{ ...fieldStyle(''), fontStyle: !m2.vorname ? 'italic' : 'normal' }} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Nachname</label>
                    <input value={m2.nachname || ''} onChange={e => updateMieter2('nachname', e.target.value)}
                      placeholder="leer lassen wenn kein 2. Mieter"
                      style={{ ...fieldStyle(''), fontStyle: !m2.nachname ? 'italic' : 'normal' }} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Geburtsdatum</label>
                    <input value={m2.geburtsdatum || ''} onChange={e => updateMieter2('geburtsdatum', e.target.value)}
                      style={fieldStyle('')} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>E-Mail</label>
                    <input value={m2.email || ''} onChange={e => updateMieter2('email', e.target.value)}
                      style={fieldStyle('')} />
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label style={labelStyle}>Telefon</label>
                    <input value={m2.telefon || ''} onChange={e => updateMieter2('telefon', e.target.value)}
                      style={fieldStyle('')} />
                  </div>
                </div>
                {hasMieter2 && (
                  <button onClick={removeMieter2} style={{
                    marginTop: 4, padding: '4px 10px', fontSize: 11, background: 'transparent',
                    border: '1px solid var(--brand-warm-gray)', borderRadius: 3, cursor: 'pointer',
                    color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                  }}>2. Mieter entfernen</button>
                )}
              </div>
            );
          })()}
        </div>

        {/* Objekt & Einheit */}
        <div style={{ marginBottom: 16 }}>
          {sectionTitle('Objekt & Einheit')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {renderField('objekt', 'Objekt')}
            {renderField('einheit', 'Einheit')}
            {renderField('einheit_lage', 'Lage (z.B. 2. OG links)')}
          </div>
        </div>

        {/* Mietvertrag — immer sichtbar (mietvertrag ist immer gewaehlt) */}
        <div style={{ marginBottom: 16 }}>
          {sectionTitle('Mietvertrag')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {renderField('unterschriftsdatum', 'Unterschriftsdatum')}
            {renderField('mietbeginn', 'Mietbeginn')}
            {renderField('mietende', 'Mietende')}
            {renderField('kaltmiete', 'Nettokaltmiete')}
            {renderField('betriebskosten', 'Betriebskosten')}
            {renderField('heizkosten', 'Heizkosten')}
            {renderField('nebenkosten', 'Nebenkosten (gesamt)')}
            {renderField('gesamtmiete', 'Gesamtmiete')}
            {renderField('kaution', 'Kaution')}
            {renderField('wohnflaeche', 'Wohnflaeche (m2)')}
            {renderField('anzahl_personen', 'Anzahl Personen')}
          </div>
          <div style={{ marginTop: 8 }}>
            <label style={{
              ...labelStyle,
              color: !editData.mietanpassung_typ ? 'var(--brand-warning)' : 'var(--brand-text-muted)',
            }}>
              Mieterhoehungsart
              {!editData.mietanpassung_typ && <span style={{ fontWeight: 700, marginLeft: 4 }}>⚠ Pflichtfeld</span>}
            </label>
            <select
              value={editData.mietanpassung_typ || ''}
              onChange={e => updateField('mietanpassung_typ', e.target.value)}
              style={{
                width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4,
                fontFamily: 'var(--font-body)',
                border: !editData.mietanpassung_typ ? '2px solid var(--brand-warning)' : '1px solid var(--brand-warm-gray)',
                background: !editData.mietanpassung_typ ? 'var(--brand-warning-bg)' : '#fff',
              }}
            >
              <option value="">— bitte waehlen —</option>
              <option value="Indexmiete">Indexmiete</option>
              <option value="Staffelmiete">Staffelmiete</option>
              <option value="Vergleichsmiete">Vergleichsmiete (Kappungsgrenze)</option>
            </select>
          </div>
        </div>

        {/* Zustellweg */}
        {showSection('zustellweg') && (
          <div style={{ marginBottom: 16 }}>
            {sectionTitle('Bevorzugten Zustellungsweg anpassen')}
            <div style={{
              fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 8,
              fontStyle: 'italic', lineHeight: 1.4,
            }}>
              Nichts auswählen = bestehender Zustellweg in IW24 bleibt unverändert.
              Nur anhaken, wenn die Mail/Info eine Änderung explizit verlangt.
            </div>
            {(() => {
              // Default: leer (= keine Änderung). Kein implizites 'e-post' mehr.
              const currentZw = (editData.zustellweg || '').split(',').map(s => s.trim()).filter(Boolean);
              const options = [
                { code: 'e-post', label: 'Post (postalisch)' },
                { code: 'e-post-brief', label: 'E-Post-Brief' },
                { code: 'e-mail', label: 'E-Mail' },
                { code: 'portal', label: 'Portalfreigabe' },
                { code: 'e-mail-share', label: 'E-Mail-Share' },
              ];
              return options.map(opt => {
                const checked = currentZw.indexOf(opt.code) !== -1;
                return (
                  <div key={opt.code} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input type="checkbox" checked={checked} onChange={e => {
                      let codes = currentZw.filter(c => c && c !== opt.code);
                      if (e.target.checked) codes.push(opt.code);
                      if (opt.code === 'e-post' && e.target.checked) codes = ['e-post'];
                      if (opt.code !== 'e-post' && e.target.checked) codes = codes.filter(c => c !== 'e-post');
                      // Leere Auswahl erlaubt → bedeutet "nicht ändern"
                      updateField('zustellweg', codes.join(','));
                    }} style={{ flexShrink: 0, accentColor: 'var(--brand-gold)' }} />
                    <span style={{ fontSize: 13, color: 'var(--brand-text)' }}>{opt.label}</span>
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* Datenschutz */}
        {showSection('datenschutz') && (
          <div style={{ marginBottom: 16 }}>
            {sectionTitle('Datenschutz (Anlage 1)')}
            {renderCheckField('datenschutz_einwilligung', 'Einwilligung erteilt')}
            {renderField('datenschutz_datum', 'Datum')}
          </div>
        )}

        {/* SEPA */}
        {showSection('sepa') && (
          <div style={{ marginBottom: 16 }}>
            {sectionTitle('SEPA-Mandat')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              {renderField('kontoinhaber', 'Kontoinhaber')}
              {renderField('iban', 'IBAN')}
              {renderField('bic', 'BIC')}
              {renderField('sepa_datum', 'Unterschriftsdatum')}
              {renderField('sepa_anfangsdatum', 'Anfangsdatum Mandat')}
            </div>
          </div>
        )}

        {/* Bestaetigen-Button */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--brand-warm-gray)' }}>
          <button onClick={() => setEditData({ ...(result.extracted_data || {}) })}
            style={{
              padding: '10px 20px', fontSize: 12, fontWeight: 600,
              background: 'transparent', border: '1px solid var(--brand-warm-gray)',
              color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'var(--font-body)',
            }}
          >Zuruecksetzen</button>
          <button onClick={() => onConfirm(editData)}
            style={{
              padding: '10px 20px', fontSize: 12, fontWeight: 600,
              background: 'var(--brand-gold)', color: 'var(--brand-navy)',
              border: 'none', borderRadius: 4, cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >Daten bestaetigen & Mieter anlegen</button>
        </div>
      </div>
    </div>
  );
}

// ── Mieteranlage Upload (Drag & Drop + Dokumenttyp-Auswahl) ──
const MIETERANLAGE_DOC_TYPES = [
  { key: 'mietvertrag', label: 'Mietvertrag (unterschrieben)', locked: true },
  { key: 'datenblatt', label: 'Datenblatt / Kontaktdatenblatt' },
  { key: 'sepa', label: 'SEPA-Lastschriftmandat' },
  { key: 'datenschutz', label: 'Datenschutzerklaerung (Anlage 1)' },
  { key: 'zustellweg', label: 'Zustellweg (Anlage 2)' },
  { key: 'personalausweis', label: 'Personalausweis' },
  { key: 'sonstige', label: 'Sonstige Anlagen' },
];

function ERPdfDropzone({ id, label, description, fieldKey, files, onFilesChange, disabled }) {
  const [dragOver, setDragOver] = useState(false);
  const fileList = files[fieldKey] ? Array.from(files[fieldKey]) : [];

  const processFiles = (incoming) => {
    const pdfs = incoming.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) return;
    const current = files[fieldKey] ? Array.from(files[fieldKey]) : [];
    const dt = new DataTransfer();
    [...current, ...pdfs].forEach(f => dt.items.add(f));
    onFilesChange(prev => ({ ...prev, [fieldKey]: dt.files }));
  };

  const removeFile = (index) => {
    const updated = fileList.filter((_, i) => i !== index);
    const dt = new DataTransfer();
    updated.forEach(f => dt.items.add(f));
    onFilesChange(prev => ({ ...prev, [fieldKey]: dt.files }));
  };

  const fmtSize = (b) => b < 1024 * 1024 ? (b / 1024).toFixed(1) + ' KB' : (b / 1024 / 1024).toFixed(1) + ' MB';

  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      {description && <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 10 }}>{description}</div>}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); if (!disabled) processFiles(Array.from(e.dataTransfer.files)); }}
        onClick={() => !disabled && document.getElementById(id).click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
          borderRadius: 8, padding: '24px 20px', textAlign: 'center',
          background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
          cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
        }}
      >
        <div style={{ fontSize: 24, marginBottom: 6 }}>{dragOver ? '📥' : '📄'}</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)' }}>
          {dragOver ? 'Ablegen' : 'PDFs hierher ziehen oder klicken'}
        </div>
        <input id={id} type="file" accept=".pdf" multiple style={{ display: 'none' }}
          onChange={e => { processFiles(Array.from(e.target.files)); e.target.value = ''; }} disabled={disabled} />
      </div>
      {fileList.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {fileList.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 6,
            }}>
              <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)', textTransform: 'uppercase' }}>PDF</span>
              <span style={{ flex: 1, fontSize: 12, color: 'var(--brand-text)' }}>{f.name}</span>
              <span style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{fmtSize(f.size)}</span>
              {!disabled && (
                <button onClick={e => { e.stopPropagation(); removeFile(i); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--brand-danger)', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EigentuemerReportUpload({ files, onFilesChange, disabled }) {
  return (
    <ERPdfDropzone id="er-pdf-input" fieldKey="pdfs"
      label={<>Abrechnungs-PDFs (Immoware24) <span style={{ color: 'var(--brand-danger)' }}>*</span></>}
      description="Exportierte Reports aus Immoware24 — mehrere PDFs möglich"
      files={files} onFilesChange={onFilesChange} disabled={disabled} />
  );
}

function MieteranlageUpload({ files, onFilesChange, disabled, selectedDocTypes, onDocTypesChange }) {
  const [dragOver, setDragOver] = useState(false);
  const fileList = files['files'] ? Array.from(files['files']) : [];

  const toggleDocType = (key) => {
    if (key === 'mietvertrag') return; // immer aktiv
    const current = selectedDocTypes || ['mietvertrag'];
    if (current.includes(key)) {
      onDocTypesChange(current.filter(k => k !== key));
    } else {
      onDocTypesChange([...current, key]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) processFiles(dropped);
  };

  const processFiles = (incoming) => {
    const currentFiles = files['files'] ? Array.from(files['files']) : [];
    const merged = [...currentFiles, ...incoming];
    const dt = new DataTransfer();
    merged.forEach(f => dt.items.add(f));
    onFilesChange({ files: dt.files });
  };

  const handleFileInput = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) processFiles(selected);
    e.target.value = '';
  };

  const removeFile = (index) => {
    const updated = fileList.filter((_, i) => i !== index);
    const dt = new DataTransfer();
    updated.forEach(f => dt.items.add(f));
    onFilesChange({ files: dt.files });
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const extColor = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return { bg: 'var(--brand-danger-bg)', color: 'var(--brand-danger)' };
    if (['jpg', 'jpeg', 'png'].includes(ext)) return { bg: 'var(--brand-success-bg)', color: 'var(--brand-success)' };
    return { bg: 'var(--brand-info-bg)', color: 'var(--brand-info)' };
  };

  const docTypes = selectedDocTypes || ['mietvertrag'];

  return (
    <div>
      {/* Dokumenttyp-Auswahl */}
      <div style={{
        background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
        border: '1px solid var(--brand-warm-gray)',
      }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10,
        }}>Welche Unterlagen werden hochgeladen?</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
          {MIETERANLAGE_DOC_TYPES.map(doc => {
            const checked = doc.locked || docTypes.includes(doc.key);
            return (
              <label key={doc.key} style={{
                display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                cursor: doc.locked ? 'default' : 'pointer', userSelect: 'none',
                opacity: doc.locked ? 0.7 : 1,
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={doc.locked || disabled}
                  onChange={() => toggleDocType(doc.key)}
                  style={{ flexShrink: 0, accentColor: 'var(--brand-gold)' }}
                />
                <span style={{ color: checked ? 'var(--brand-navy)' : 'var(--brand-text-light)' }}>
                  {doc.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('mieteranlage-file-input').click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
          borderRadius: 8, padding: '2rem', textAlign: 'center',
          background: dragOver ? 'var(--brand-gold-10)' : 'var(--brand-cream)',
          cursor: disabled ? 'default' : 'pointer',
          transition: 'all 0.2s',
        }}
      >
        <input
          id="mieteranlage-file-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: 32, marginBottom: 8 }}>
          {dragOver ? '\uD83D\uDCE5' : '\uD83C\uDFE0'}
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>
          {dragOver ? 'Dateien hier ablegen' : 'Mietvertragsdokumente hierher ziehen'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
          oder klicken zum Auswaehlen — PDF, JPG, PNG
        </div>
      </div>

      {/* Dateiliste */}
      {fileList.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8 }}>
            {fileList.length} Datei{fileList.length !== 1 ? 'en' : ''} ausgewaehlt
          </div>
          {fileList.map((f, i) => {
            const ec = extColor(f.name);
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', background: '#fff', borderRadius: 4,
                border: '1px solid var(--brand-warm-gray)', marginBottom: 4,
                fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                    background: ec.bg, color: ec.color,
                    textTransform: 'uppercase', flexShrink: 0,
                  }}>
                    {f.name.split('.').pop()}
                  </span>
                  <span style={{ color: 'var(--brand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.name}
                  </span>
                  <span style={{ color: 'var(--brand-text-light)', flexShrink: 0 }}>{formatSize(f.size)}</span>
                </div>
                {!disabled && (
                  <button onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--brand-text-light)',
                      cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1, marginLeft: 8,
                    }}
                    onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                    onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                  >&times;</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Editierbares Datenblatt ──
function EditCheckbox({ dataKey, label, value, onChange, children }) {
  const checked = value === 1 || value === true || value === '1';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <input type="checkbox" checked={checked}
        onChange={e => onChange(dataKey, e.target.checked ? 1 : 0)}
        style={{ marginTop: 3, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <span style={{ fontSize: 12, color: 'var(--brand-navy)' }}>{label}</span>
        {checked && children}
      </div>
    </div>
  );
}

function EditField({ dataKey, label, placeholder, value, onChange }) {
  const inputBase = {
    width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 4, fontFamily: 'var(--font-body)',
  };
  const hasValue = value && String(value).trim();
  return (
    <div>
      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: 3 }}>
        {label} {!hasValue && <span style={{ color: 'var(--brand-warning)' }}>*fehlt</span>}
      </label>
      <input
        value={value || ''}
        onChange={e => onChange(dataKey, e.target.value)}
        placeholder={placeholder || ''}
        style={{
          ...inputBase,
          border: hasValue ? '1px solid var(--brand-warm-gray)' : '2px solid var(--brand-warning)',
          background: hasValue ? '#fff' : 'var(--brand-warning-bg)',
        }}
      />
    </div>
  );
}

function EditDataSheet({ data, onChange, onSubmit, onCancel, submitLabel }) {
  const update = (key, value) => onChange(prev => ({ ...prev, [key]: value }));
  const [showMieter2, setShowMieter2] = useState(!!(data.mieter2_name || data.anrede_2));
  const sectionStyle = { fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 0 4px', borderBottom: '1px solid var(--brand-warm-gray)', marginBottom: 8 };
  const btnSmall = { padding: '4px 12px', fontSize: 11, fontWeight: 500, borderRadius: 3, cursor: 'pointer', background: 'transparent', border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-gold)', fontFamily: 'var(--font-body)' };

  return (
    <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-gold)', overflow: 'hidden' }}>
      <div style={{ padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Daten prüfen & ergänzen
      </div>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>

        <div style={sectionStyle}>Objekt</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <EditField dataKey="objektanschrift" label="Objektadresse" placeholder="Straße Nr, PLZ Stadt" value={data.objektanschrift} onChange={update} />
          <EditField dataKey="einheitsnummer" label="Einheitsnummer" placeholder="z.B. Matt.37.WE05.EG" value={data.einheitsnummer} onChange={update} />
          <EditField dataKey="geschoss" label="Lage / Geschoss" placeholder="z.B. 3.OG links" value={data.geschoss} onChange={update} />
          <EditField dataKey="wohnflaeche" label="Wohnfläche (m²)" placeholder="z.B. 46,30" value={data.wohnflaeche} onChange={update} />
          <EditField dataKey="zimmer" label="Zimmer" placeholder="z.B. 2" value={data.zimmer} onChange={update} />
          <EditField dataKey="mietbeginn" label="Mietbeginn" placeholder="TT.MM.JJJJ" value={data.mietbeginn} onChange={update} />
          <EditField dataKey="unterschriftsdatum" label="Unterschriftsdatum" placeholder="TT.MM.JJJJ" value={data.unterschriftsdatum} onChange={update} />
        </div>

        <div style={sectionStyle}>Eigentümer / Vermieter</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <EditField dataKey="eigentuemer" label="Eigentümer Name" placeholder="Name oder Firma" value={data.eigentuemer} onChange={update} />
          <EditField dataKey="eigentuemer_anschrift" label="Eigentümer Anschrift" placeholder="Straße Nr, PLZ Ort" value={data.eigentuemer_anschrift} onChange={update} />
        </div>

        <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Mieter</span>
          {!showMieter2 && (
            <button type="button" onClick={() => setShowMieter2(true)} style={btnSmall}>+ 2. Mieter hinzufügen</button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
          <EditField dataKey="anrede_1" label="Anrede" placeholder="Herr / Frau" value={data.anrede_1} onChange={update} />
          <EditField dataKey="mieter_name" label="Mietername" value={data.mieter_name} onChange={update} />
        </div>
        {showMieter2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginTop: 4 }}>
            <EditField dataKey="anrede_2" label="Anrede 2. Mieter" placeholder="Herr / Frau" value={data.anrede_2} onChange={update} />
            <EditField dataKey="mieter2_name" label="2. Mieter" value={data.mieter2_name} onChange={update} />
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="button" onClick={() => { setShowMieter2(false); update('anrede_2', ''); update('mieter2_name', ''); }}
                style={{ ...btnSmall, color: 'var(--brand-danger)', borderColor: 'var(--brand-danger-bg)', marginBottom: 1 }}>Entfernen</button>
            </div>
          </div>
        )}

        <div style={sectionStyle}>Mietkonditionen</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <EditField dataKey="kaltmiete" label="Nettokaltmiete (€)" value={data.kaltmiete} onChange={update} />
          <EditField dataKey="vorauszahlung_betriebskosten" label="BK-Vorauszahlung (€)" placeholder="Betriebskosten" value={data.vorauszahlung_betriebskosten || data.nebenkosten} onChange={update} />
          <EditField dataKey="vorauszahlung_heizkosten" label="HK-Vorauszahlung (€)" placeholder="Heizkosten" value={data.vorauszahlung_heizkosten} onChange={update} />
          <EditField dataKey="mindestlaufzeit_jahre" label="Mindestlaufzeit (Jahre)" placeholder="2" value={data.mindestlaufzeit_jahre || '2'} onChange={update} />
        </div>

        <div style={sectionStyle}>§4 Mietpreisbremse — Ausnahmen</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <EditCheckbox dataKey="vormiete_check" label="Vormiete: Ein Jahr vor Ende des Vormietverhältnisses betrug die Nettomiete:" value={data.vormiete_check} onChange={update}>
            <div style={{ marginTop: 4 }}>
              <EditField dataKey="vormiete_betrag" label="Vormiete-Betrag (€)" placeholder="z.B. 650,00" value={data.vormiete_betrag} onChange={update} />
            </div>
          </EditCheckbox>
          <EditCheckbox dataKey="modernisierung_vorher_check" label="Es wurden vor Mietvertragsbeginn Modernisierungsmaßnahmen nach §555b BGB durchgeführt" value={data.modernisierung_vorher_check} onChange={update} />
          <EditCheckbox dataKey="vermietet_nach_okt_2014_check" label="Die Wohnung wurde nach dem 1. Oktober 2014 erstmals genutzt und vermietet" value={data.vermietet_nach_okt_2014_check} onChange={update} />
          <EditCheckbox dataKey="miete_nach_modernisierung_check" label="Es handelt sich um die erste Vermietung nach umfassender Modernisierung" value={data.miete_nach_modernisierung_check} onChange={update} />
        </div>

        <div style={sectionStyle}>Mietkonto</div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
          <EditField dataKey="mietkonto_iban" label="IBAN" placeholder="DE..." value={data.mietkonto_iban} onChange={update} />
          <EditField dataKey="mietkonto_bic" label="BIC" placeholder="HASPDEHHXXX" value={data.mietkonto_bic} onChange={update} />
          <EditField dataKey="mietkonto_inhaber" label="Kontoinhaber" value={data.mietkonto_inhaber} onChange={update} />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={onSubmit}
            style={{
              flex: 1, padding: '12px 24px', fontSize: 13, fontWeight: 600,
              background: 'var(--brand-navy)', color: 'var(--brand-gold)',
              border: 'none', borderRadius: 4, cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >{submitLabel || 'Dokumente erstellen'}</button>
          <button onClick={onCancel}
            style={{
              padding: '12px 20px', fontSize: 13, background: 'transparent',
              border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
              borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >Abbrechen</button>
        </div>
      </div>
    </div>
  );
}

// ── SkillForm Komponente ──


// ── Serienbrief-Skill Standalone-Komponente ──

// ─── Fleet CI Design Tokens (inline) ────────────────────────────────
const T = {
  navy:      'var(--brand-navy, #1a2744)',
  gold:      'var(--brand-gold, #c9a84c)',
  cream:     'var(--brand-cream, #faf8f4)',
  warmGray:  'var(--brand-warm-gray, #d4cfc7)',
  textMuted: 'var(--brand-text-muted, #8a8278)',
  success:   'var(--brand-success, #2e7d32)',
  danger:    'var(--brand-danger, #c62828)',
  fontHead:  'var(--font-headline, "Playfair Display", serif)',
  fontBody:  'var(--font-body, "Inter", sans-serif)',
};

// ─── Shared inline style helpers ─────────────────────────────────────
const styles = {
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: T.navy,
    marginBottom: 6,
    fontFamily: T.fontBody,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${T.warmGray}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: T.fontBody,
    color: T.navy,
    background: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%',
    minHeight: 220,
    padding: '10px 12px',
    border: `1px solid ${T.warmGray}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: T.fontBody,
    color: T.navy,
    background: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    lineHeight: 1.6,
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${T.warmGray}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: T.fontBody,
    color: T.navy,
    background: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='7'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%231a2744' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 32,
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '11px 24px',
    background: T.navy,
    color: T.gold,
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: T.fontBody,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 22px',
    background: 'transparent',
    color: T.navy,
    border: `1px solid ${T.warmGray}`,
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: T.fontBody,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
  },
  hint: {
    fontSize: 12,
    color: T.textMuted,
    marginTop: 4,
    fontFamily: T.fontBody,
  },
  card: {
    background: '#fff',
    borderRadius: 10,
    border: `1px solid ${T.warmGray}`,
    padding: '28px 32px',
    marginBottom: 20,
  },
};

// ─── Step Indicator ──────────────────────────────────────────────────
function StepIndicator({ current }) {
  const steps = ['Eingabe', 'Entwurf', 'Versand'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === current;
        const isDone = stepNum < current;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div style={{
                flex: 1,
                height: 2,
                background: isDone ? T.gold : T.warmGray,
                transition: 'background 0.3s',
              }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                fontFamily: T.fontBody,
                background: isActive || isDone ? T.gold : '#fff',
                color: isActive || isDone ? T.navy : T.textMuted,
                border: isActive || isDone ? `2px solid ${T.gold}` : `2px solid ${T.warmGray}`,
                transition: 'all 0.3s',
              }}>
                {isDone ? '✓' : stepNum}
              </div>
              <span style={{
                fontSize: 11,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? T.navy : T.textMuted,
                fontFamily: T.fontBody,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}>
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Objekt Autocomplete ─────────────────────────────────────────────
function ObjektAutocomplete({ value, onChange, token }) {
  const [query, setQuery] = useState('');
  const [objekte, setObjekte] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch('/api/admin/objekte/aktiv', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) {
          setObjekte(data.objekte || []);
          setLoading(false);
        }
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!query.trim()) { setFiltered(objekte); return; }
    const q = query.toLowerCase();
    setFiltered(
      objekte.filter(
        (o) =>
          o.adresse.toLowerCase().includes(q) ||
          o.objekt_nr.toString().includes(q) ||
          (o.verwaltungsart || '').toLowerCase().includes(q),
      ),
    );
  }, [query, objekte]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (obj) => {
    onChange(obj);
    setQuery(`${obj.objekt_nr} - ${obj.adresse}`);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <label style={styles.label}>Objekt</label>
      <div style={{ position: 'relative' }}>
        <input
          style={styles.input}
          placeholder={loading ? 'Lade Objekte...' : 'Objekt suchen (Nr. oder Adresse)...'}
          value={value ? `${value.objekt_nr} - ${value.adresse}` : query}
          onChange={(e) => {
            if (value) clear();
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => { if (!value) setOpen(true); }}
          readOnly={!!value}
        />
        {value && (
          <button
            onClick={clear}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', fontSize: 18, color: T.textMuted,
              cursor: 'pointer', lineHeight: 1, padding: 0,
            }}
            title="Auswahl aufheben"
          >
            ×
          </button>
        )}
      </div>
      {open && !value && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: `1px solid ${T.warmGray}`, borderRadius: 6,
          maxHeight: 240, overflowY: 'auto', margin: 0, padding: 0,
          listStyle: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
        }}>
          {filtered.length === 0 && (
            <li style={{ padding: '12px 14px', color: T.textMuted, fontSize: 13 }}>
              Kein Objekt gefunden
            </li>
          )}
          {filtered.map((o) => (
            <li
              key={o.objekt_nr}
              onClick={() => select(o)}
              style={{
                padding: '10px 14px', cursor: 'pointer', fontSize: 13,
                fontFamily: T.fontBody, borderBottom: `1px solid ${T.cream}`,
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.cream; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
            >
              <strong>{o.objekt_nr}</strong>
              <span style={{ margin: '0 6px', color: T.textMuted }}>&mdash;</span>
              {o.adresse}
              {o.verwaltungsart && (
                <span style={{
                  marginLeft: 10, fontSize: 10, fontWeight: 600,
                  textTransform: 'uppercase', color: T.textMuted,
                  background: T.cream, padding: '2px 6px', borderRadius: 3,
                }}>
                  {o.verwaltungsart}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Toggle Button Group ─────────────────────────────────────────────
function ToggleGroup({ label, options, value, onChange }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{
                padding: '9px 18px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: T.fontBody,
                cursor: 'pointer',
                border: active ? `2px solid ${T.navy}` : `1px solid ${T.warmGray}`,
                background: active ? T.navy : '#fff',
                color: active ? T.gold : T.navy,
                transition: 'all 0.15s',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Kanal Checkbox ──────────────────────────────────────────────────
function KanalCheckbox({ label, checked, disabled, onChange }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      fontSize: 14, fontFamily: T.fontBody, color: disabled ? T.textMuted : T.navy,
      cursor: disabled ? 'default' : 'pointer', userSelect: 'none',
    }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: T.navy, width: 16, height: 16, cursor: disabled ? 'default' : 'pointer' }}
      />
      {label}
      {disabled && <span style={{ fontSize: 11, color: T.textMuted }}>(immer aktiv)</span>}
    </label>
  );
}

// ─── Progress Bar ────────────────────────────────────────────────────
function ProgressBar({ progress, message }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        height: 8, borderRadius: 4, background: T.cream, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', borderRadius: 4,
          width: `${Math.min(100, Math.max(0, progress || 0))}%`,
          background: `linear-gradient(90deg, ${T.navy}, ${T.gold})`,
          transition: 'width 0.4s ease',
        }} />
      </div>
      {message && (
        <p style={{ fontSize: 13, color: T.textMuted, marginTop: 8, fontFamily: T.fontBody }}>
          {message}
        </p>
      )}
    </div>
  );
}

// =====================================================================
// ─── MAIN COMPONENT ─────────────────────────────────────────────────
// =====================================================================

function SerienbriefForm() {
  const { token } = useAuth();
  const { job, startTracking } = useJobTracker();

  // ── Wizard step ────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1 state ───────────────────────────────────────────────────
  const [objekt, setObjekt] = useState(null);
  const [empfaenger, setEmpfaenger] = useState('mieter');
  const [prompt, setPrompt] = useState('');
  const [kanaele, setKanaele] = useState({ portal: true, email: true, epost: false });

  // ── Step 2 state ───────────────────────────────────────────────────
  const [betreff, setBetreff] = useState('');
  const [brieftext, setBrieftext] = useState('');
  const [portalCategories, setPortalCategories] = useState(['/Posteingang']);
  const [portalCategory, setPortalCategory] = useState('/Posteingang');
  const [objektInfo, setObjektInfo] = useState(null);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  // ── Step 3 state ───────────────────────────────────────────────────
  const [sendJobId, setSendJobId] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  // ── Drafting state ─────────────────────────────────────────────────
  const [draftJobId, setDraftJobId] = useState(null);
  const [draftError, setDraftError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Track draft job completion ─────────────────────────────────────
  useEffect(() => {
    if (!job) return;
    // Draft phase completed
    if (draftJobId && job.status === 'done' && job.result) {
      const r = job.result;
      setBetreff(r.betreff || '');
      setBrieftext(r.brieftext || '');
      setPortalCategories(r.portal_categories || ['/Posteingang']);
      setPortalCategory(r.portal_categories?.[0] || '/Posteingang');
      setObjektInfo(r.objekt_info || null);
      setStep(2);
      setSubmitting(false);
    }
    if (draftJobId && job.status === 'error') {
      setDraftError(job.message || 'Fehler bei der Entwurfserstellung');
      setSubmitting(false);
    }
    // Send phase completed
    if (sendJobId && job.status === 'done') {
      setSendResult(job.result || { success: true });
    }
    if (sendJobId && job.status === 'error') {
      setSendResult({ error: true, message: job.message || 'Versandfehler' });
    }
  }, [job, draftJobId, sendJobId]);

  // ── Handlers ───────────────────────────────────────────────────────

  const handleDraft = useCallback(async (extraPrompt) => {
    setDraftError(null);
    setSubmitting(true);
    try {
      const body = {
        objekt_id: objekt.immoware_id || objekt.objekt_nr,
        empfaenger,
        prompt: extraPrompt ? `${prompt}\n\nFeedback: ${extraPrompt}` : prompt,
        kanaele: Object.entries(kanaele).filter(([, v]) => v).map(([k]) => k),
      };
      const res = await fetch('/api/skills/serienbrief/draft', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Server-Fehler (${res.status})`);
      const data = await res.json();
      // Draft is synchronous - result is already in the response
      const r = data.result || {};
      if (r.betreff || r.brieftext) {
        setBetreff(r.betreff || '');
        setBrieftext(r.brieftext || '');
        setPortalCategories(r.portal_categories || ['/Posteingang']);
        setPortalCategory(r.portal_categories?.[0] || '/Posteingang');
        setObjektInfo(r.objekt_info || null);
        if (r.error) setDraftError(r.error);
        setStep(2);
        setSubmitting(false);
        return;
      }
      if (r.error) {
        setDraftError(r.error);
        setSubmitting(false);
        return;
      }
      // Fallback: async job tracking
      setDraftJobId(data.job_id);
      startTracking(data.job_id, 'serienbrief');
    } catch (err) {
      setDraftError(err.message);
      setSubmitting(false);
    }
  }, [objekt, empfaenger, prompt, kanaele, token, startTracking]);

  const handleSend = useCallback(async () => {
    setSubmitting(true);
    try {
      const body = {
        objekt_immoware_id: objektInfo?.immoware_id || objekt?.immoware_id,
        betreff,
        brieftext,
        kanaele: Object.entries(kanaele).filter(([, v]) => v).map(([k]) => k),
        portal_category: portalCategory,
        empfaenger,
      };
      const res = await fetch('/api/skills/serienbrief/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Server-Fehler (${res.status})`);
      const data = await res.json();
      setSendJobId(data.job_id);
      startTracking(data.job_id, 'serienbrief');
      setStep(3);
    } catch (err) {
      setDraftError(err.message);
      setSubmitting(false);
    }
  }, [betreff, brieftext, kanaele, portalCategory, empfaenger, objektInfo, objekt, token, startTracking]);

  const handleRegenerate = () => {
    setShowFeedback(false);
    handleDraft(feedbackInput);
    setFeedbackInput('');
  };

  const canSubmitStep1 = objekt && prompt.trim().length > 0;

  // ── RENDER ─────────────────────────────────────────────────────────
  return (
    <div style={{
      maxWidth: 720,
      margin: '0 auto',
      padding: '32px 16px 64px',
      fontFamily: T.fontBody,
    }}>
      {/* Back link */}
      <Link
        to="/skills"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 13, color: T.textMuted, textDecoration: 'none',
          marginBottom: 20, fontFamily: T.fontBody,
        }}
      >
        &larr; Zurück zu allen Skills
      </Link>

      {/* Title */}
      <h1 style={{
        fontFamily: T.fontHead, fontSize: 28, fontWeight: 700,
        color: T.navy, margin: '0 0 8px',
      }}>
        Serienbrief erstellen
      </h1>
      <p style={{ fontSize: 14, color: T.textMuted, margin: '0 0 28px', lineHeight: 1.5 }}>
        Schreiben Sie einen Prompt &mdash; Claude erstellt den Brief, Sie prüfen und versenden.
      </p>

      <StepIndicator current={step} />

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* STEP 1 — Eingabe                                              */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {step === 1 && (
        <div style={styles.card}>
          {/* Objekt */}
          <div style={{ marginBottom: 22 }}>
            <ObjektAutocomplete value={objekt} onChange={setObjekt} token={token} />
          </div>

          {/* Empfaenger */}
          <div style={{ marginBottom: 22 }}>
            <ToggleGroup
              label="Empfänger"
              options={[
                { value: 'mieter', label: 'Alle Mieter' },
                { value: 'eigentuemer', label: 'Alle Eigentümer' },
                { value: 'alle', label: 'Alle' },
              ]}
              value={empfaenger}
              onChange={setEmpfaenger}
            />
          </div>

          {/* Kanaele */}
          <div style={{ marginBottom: 22 }}>
            <label style={styles.label}>Versandkanäle</label>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <KanalCheckbox label="Portal" checked={kanaele.portal} disabled onChange={() => {}} />
              <KanalCheckbox
                label="E-Mail"
                checked={kanaele.email}
                disabled={false}
                onChange={(v) => setKanaele((k) => ({ ...k, email: v }))}
              />
              <KanalCheckbox
                label="E-POST"
                checked={kanaele.epost}
                disabled={false}
                onChange={(v) => setKanaele((k) => ({ ...k, epost: v }))}
              />
            </div>
          </div>

          {/* Prompt */}
          <div style={{ marginBottom: 22 }}>
            <label style={styles.label}>Ihr Anliegen / Prompt</label>
            <textarea
              style={styles.textarea}
              placeholder="Beschreiben Sie, worüber der Brief informieren soll..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
            />
          </div>

          {/* Error */}
          {draftError && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 16,
              background: '#fef2f2', border: `1px solid ${T.danger}`, color: T.danger,
              fontSize: 13,
            }}>
              {draftError}
            </div>
          )}

          {/* Submit */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              style={{
                ...styles.btnPrimary,
                opacity: canSubmitStep1 && !submitting ? 1 : 0.5,
                pointerEvents: canSubmitStep1 && !submitting ? 'auto' : 'none',
              }}
              onClick={() => handleDraft(null)}
              disabled={!canSubmitStep1 || submitting}
            >
              {submitting ? 'Wird erstellt...' : 'Entwurf generieren'}
            </button>
          </div>

          {/* Draft progress */}
          {submitting && job && (
            <ProgressBar progress={job.progress} message={job.message} />
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* STEP 2 — Entwurf (editierbar)                                 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {step === 2 && (
        <div style={styles.card}>
          {/* Objekt context badge */}
          {objektInfo && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.cream, padding: '6px 14px', borderRadius: 6,
              fontSize: 13, color: T.navy, marginBottom: 20, fontWeight: 500,
            }}>
              <span style={{ fontWeight: 700 }}>{objektInfo.objekt_nr}</span>
              <span style={{ color: T.textMuted }}>&mdash;</span>
              {objektInfo.adresse}
            </div>
          )}

          {/* Betreff */}
          <div style={{ marginBottom: 18 }}>
            <label style={styles.label}>Betreff</label>
            <input
              style={{ ...styles.input, background: 'var(--brand-cream, #faf8f4)' }}
              value={betreff}
              onChange={(e) => setBetreff(e.target.value)}
              placeholder="Betreff des Serienbriefs"
            
            />
            <p style={styles.hint}>
              Wird als Dokumenttitel und fette Überschrift im Brief verwendet
            </p>
          </div>

          {/* Portal-Ordner */}
          <div style={{ marginBottom: 18 }}>
            <label style={styles.label}>Portal-Ordner</label>
            <input
              style={{ ...styles.input, background: 'var(--brand-bg-tertiary, #f5f5f5)' }}
              value="/Posteingang"
              disabled
            />
            <p style={styles.hint}>
              Dokumente werden im Portal24-Posteingang abgelegt (Standardordner)
            </p>
          </div>

          {/* Brieftext */}
          <div style={{ marginBottom: 22 }}>
            <label style={styles.label}>Brieftext</label>
            <textarea
              style={{ ...styles.textarea, minHeight: 280, background: 'var(--brand-cream, #faf8f4)' }}
              value={brieftext}
              onChange={(e) => setBrieftext(e.target.value)}
            />
          </div>

          {/* Feedback loop */}
          {!showFeedback ? (
            <button
              type="button"
              onClick={() => setShowFeedback(true)}
              style={{
                background: 'none', border: 'none', color: T.textMuted,
                fontSize: 13, cursor: 'pointer', padding: 0,
                fontFamily: T.fontBody, textDecoration: 'underline',
                marginBottom: 20, display: 'block',
              }}
            >
              Nicht zufrieden? Neu generieren lassen
            </button>
          ) : (
            <div style={{
              background: T.cream, borderRadius: 8, padding: '16px 18px',
              marginBottom: 20,
            }}>
              <label style={{ ...styles.label, marginBottom: 8 }}>
                Was soll anders sein?
              </label>
              <div style={{ display: 'flex', gap: 10 }}>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  placeholder="z.B. formeller, kürzer, anderer Ton..."
                  value={feedbackInput}
                  onChange={(e) => setFeedbackInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && feedbackInput.trim()) handleRegenerate(); }}
                />
                <button
                  style={{
                    ...styles.btnSecondary,
                    opacity: feedbackInput.trim() && !submitting ? 1 : 0.5,
                    pointerEvents: feedbackInput.trim() && !submitting ? 'auto' : 'none',
                  }}
                  onClick={handleRegenerate}
                  disabled={!feedbackInput.trim() || submitting}
                >
                  {submitting ? 'Generiere...' : 'Neu generieren'}
                </button>
              </div>
            </div>
          )}

          {/* Regenerate progress */}
          {submitting && job && (
            <ProgressBar progress={job.progress} message={job.message} />
          )}

          {/* Error */}
          {draftError && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, marginBottom: 16,
              background: '#fef2f2', border: `1px solid ${T.danger}`, color: T.danger,
              fontSize: 13,
            }}>
              {draftError}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <button
              style={styles.btnSecondary}
              onClick={() => { setStep(1); setDraftError(null); }}
            >
              &larr; Zurück
            </button>
            <button
              style={{
                ...styles.btnPrimary,
                opacity: betreff.trim() && brieftext.trim() && !submitting ? 1 : 0.5,
                pointerEvents: betreff.trim() && brieftext.trim() && !submitting ? 'auto' : 'none',
              }}
              onClick={handleSend}
              disabled={!betreff.trim() || !brieftext.trim() || submitting}
            >
              {submitting ? 'Wird versendet...' : 'Freigeben & Versenden'}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* STEP 3 — Versand (Progress + Ergebnis)                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {step === 3 && (
        <div style={styles.card}>
          {!sendResult ? (
            <>
              <h2 style={{
                fontFamily: T.fontHead, fontSize: 20, fontWeight: 700,
                color: T.navy, margin: '0 0 8px',
              }}>
                Versand läuft...
              </h2>
              <p style={{ fontSize: 14, color: T.textMuted, margin: '0 0 20px' }}>
                Der Serienbrief wird über die gewählten Kanäle versendet.
              </p>
              {job && <ProgressBar progress={job.progress} message={job.message} />}
            </>
          ) : sendResult.error ? (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: '#fef2f2', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 20,
                }}>
                  &#10007;
                </div>
                <div>
                  <h2 style={{
                    fontFamily: T.fontHead, fontSize: 20, fontWeight: 700,
                    color: T.danger, margin: 0,
                  }}>
                    Versand fehlgeschlagen
                  </h2>
                  <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
                    {sendResult.message}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button style={styles.btnSecondary} onClick={() => { setSendResult(null); setStep(2); setSubmitting(false); }}>
                  &larr; Zurück zum Entwurf
                </button>
                <button style={styles.btnPrimary} onClick={() => { setSendResult(null); handleSend(); }}>
                  Erneut versuchen
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
              }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: '#e8f5e9', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 22, color: T.success,
                }}>
                  &#10003;
                </div>
                <div>
                  <h2 style={{
                    fontFamily: T.fontHead, fontSize: 20, fontWeight: 700,
                    color: T.success, margin: 0,
                  }}>
                    Versand erfolgreich
                  </h2>
                  <p style={{ fontSize: 13, color: T.textMuted, margin: '4px 0 0' }}>
                    Der Serienbrief wurde über alle gewählten Kanäle zugestellt.
                  </p>
                </div>
              </div>

              {/* Summary */}
              <div style={{
                background: T.cream, borderRadius: 8, padding: '16px 20px',
                marginBottom: 20, fontSize: 13, lineHeight: 1.8,
              }}>
                <div><strong>Objekt:</strong> {objektInfo?.objekt_nr} &mdash; {objektInfo?.adresse}</div>
                <div><strong>Betreff:</strong> {betreff}</div>
                <div><strong>Empfänger:</strong> {empfaenger === 'mieter' ? 'Alle Mieter' : empfaenger === 'eigentuemer' ? 'Alle Eigentümer' : 'Alle'}</div>
                <div>
                  <strong>Kanäle:</strong>{' '}
                  {[
                    kanaele.portal && 'Portal',
                    kanaele.email && 'E-Mail',
                    kanaele.epost && 'E-POST',
                  ].filter(Boolean).join(', ')}
                </div>
                <div><strong>Portal-Ordner:</strong> {portalCategory}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  style={styles.btnPrimary}
                  onClick={() => {
                    setStep(1);
                    setObjekt(null);
                    setPrompt('');
                    setBetreff('');
                    setBrieftext('');
                    setPortalCategory('/Posteingang');
                    setPortalCategories(['/Posteingang']);
                    setObjektInfo(null);
                    setDraftJobId(null);
                    setSendJobId(null);
                    setSendResult(null);
                    setDraftError(null);
                    setSubmitting(false);
                    setShowFeedback(false);
                    setFeedbackInput('');
                  }}
                >
                  Neuen Serienbrief erstellen
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}




// ── ETV-Tickets Skill Standalone-Komponente ──
function EtvTicketsForm() {
  const { token } = useAuth();
  const { jobs, startTracking } = useJobTracker();
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [analyseJobId, setAnalyseJobId] = useState(() => {
    // Aktive Analyse-Jobs aus localStorage wiederherstellen
    try { return localStorage.getItem('etv_tickets_active_job') || null; } catch { return null; }
  });
  const [currentAnalyse, setCurrentAnalyse] = useState(null);
  const [ticketsJson, setTicketsJson] = useState(null);
  const [uploadPaths, setUploadPaths] = useState([]);
  const [tops, setTops] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [feedbackText, setFeedbackText] = useState('');
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);
  const [refineCount, setRefineCount] = useState(0);
  const [result, setResult] = useState(null);
  const [progressMsg, setProgressMsg] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [isPreview, setIsPreview] = useState(false);  // true wenn nur Stufe-1-Daten da sind

  // Beobachtet den globalen Job-Tracker, reagiert auf partial_result (Preview) + result (Full)
  const myJob = analyseJobId ? jobs[analyseJobId] : null;
  React.useEffect(() => {
    if (!myJob) return;
    setProgressMsg(myJob.message || '');
    setProgressPct(myJob.progress || 0);

    // Stufe 1 (Preview): partial_result vorhanden, noch kein result
    if (!myJob.result && myJob.partial_result?.analyse && !currentAnalyse) {
      const prev = myJob.partial_result.analyse;
      setCurrentAnalyse(prev);
      setTops(prev.tops || []);
      setIsPreview(true);
      setStatus('review');
    }

    // Stufe 2 (Full): result komplett → upgrade
    if (myJob.status === 'done' && myJob.result) {
      setCurrentAnalyse(myJob.result.analyse);
      setTicketsJson(myJob.result.tickets_json);
      setUploadPaths(myJob.result.upload_paths || []);
      setTops(myJob.result.analyse.tops || []);
      setIsPreview(false);
      setStatus('review');
      try { localStorage.removeItem('etv_tickets_active_job'); } catch {}
    } else if (myJob.status === 'error') {
      setError(myJob.message);
      setStatus('idle');
      setAnalyseJobId(null);
      try { localStorage.removeItem('etv_tickets_active_job'); } catch {}
    }
  }, [myJob?.status, myJob?.progress, myJob?.partial_result, myJob?.result]);  // eslint-disable-line

  const handleFileDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) setFiles(dropped);
  };

  const handleAnalyse = async () => {
    if (!files.length) return;
    setStatus('analyzing');
    setError(null);
    setProgressMsg('ETV-Protokoll wird hochgeladen...');
    setProgressPct(5);

    const formData = new FormData();
    formData.append('data', JSON.stringify({}));
    files.forEach(f => formData.append('files', f));

    try {
      const res = await fetch(`${API_URL}/skills/etv-tickets/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler');
        setStatus('idle');
        return;
      }
      const jobId = data.result?.job_id;
      if (jobId) {
        // Job global registrieren — laeuft auch wenn User wegnavigiert
        startTracking(jobId, 'etv-tickets');
        setAnalyseJobId(jobId);
        try { localStorage.setItem('etv_tickets_active_job', jobId); } catch {}
      }
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  const handleRefine = async () => {
    if (!feedbackText.trim()) return;
    setRefining(true);
    setRefineError(null);
    try {
      const activeTops = tops.filter((_, i) => !excluded.has(i));
      const currentState = { ...currentAnalyse, tops: activeTops };
      const res = await fetch(`${API_URL}/skills/etv-tickets/refine`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ analyse: currentState, feedback: feedbackText, upload_paths: uploadPaths }),
      });
      const data = await res.json();
      if (!res.ok) { setRefineError(data.detail || 'Fehler'); setRefining(false); return; }
      if (data.result?.job_id) {
        const jobId = data.result.job_id;
        const poll = setInterval(async () => {
          try {
            const pRes = await fetch(`${API_URL}/skills/etv-tickets/progress/${jobId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const pData = await pRes.json();
            if (pData.status === 'done' && pData.result) {
              clearInterval(poll);
              setCurrentAnalyse(pData.result.analyse);
              setTicketsJson(pData.result.tickets_json);
              setTops(pData.result.analyse.tops || []);
              setExcluded(new Set());
              setFeedbackText('');
              setRefining(false);
              setRefineCount(c => c + 1);
            } else if (pData.status === 'error') {
              clearInterval(poll);
              setRefineError(pData.message || 'Fehler');
              setRefining(false);
            }
          } catch { /* poll weiter */ }
        }, 1500);
        setTimeout(() => { clearInterval(poll); setRefining(false); }, 60000);
      }
    } catch (err) { setRefineError(err.message); setRefining(false); }
  };

  const handleCreateTickets = async () => {
    setStatus('creating');
    setError(null);
    setProgressMsg('Tickets werden erstellt...');
    setProgressPct(5);
    const activeTops = tops.filter((_, i) => !excluded.has(i));
    const updatedAnalyse = { ...currentAnalyse, tops: activeTops };
    try {
      const res = await fetch(`${API_URL}/skills/etv-tickets/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickets_json: ticketsJson, updated_analyse: updatedAnalyse, upload_paths: uploadPaths }),
      });
      const data = await res.json();
      if (res.ok && data.result?.job_id) {
        startTracking(data.result.job_id, 'etv-tickets');
        const jobId = data.result.job_id;
        const poll = setInterval(async () => {
          try {
            const pRes = await fetch(`${API_URL}/skills/etv-tickets/progress/${jobId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const pData = await pRes.json();
            setProgressMsg(pData.message || '');
            setProgressPct(pData.progress || 0);
            if (pData.status === 'done') {
              clearInterval(poll);
              setResult(pData.result);
              setStatus('done');
            } else if (pData.status === 'error') {
              clearInterval(poll);
              setError(pData.message);
              setStatus('review');
            }
          } catch { /* poll */ }
        }, 2000);
        setTimeout(() => clearInterval(poll), 300000);
      } else {
        setError(data.detail || 'Fehler');
        setStatus('review');
      }
    } catch (err) { setError(err.message); setStatus('review'); }
  };

  const statusColors = {
    angenommen: { bg: '#e6f4ea', color: '#1b5e20' },
    abgelehnt: { bg: '#fce8e6', color: '#b71c1c' },
    vertagt: { bg: '#fef7e0', color: '#f57f17' },
    kein_beschluss: { bg: '#f5f5f5', color: '#616161' },
    umlaufbeschluss: { bg: '#e8eaf6', color: '#283593' },
  };

  const ticketCount = tops.filter((t, i) => !excluded.has(i) && t.ticket_typ !== 'kein_ticket').length;
  const disabled = status === 'analyzing' || status === 'creating';

  // Upload-Bereich
  if (status === 'idle' || status === 'analyzing') {
    const Step = ({ n, children }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700, background: 'var(--brand-gold)', color: 'var(--brand-navy)', flexShrink: 0,
        }}>{n}</span>
        <span>{children}</span>
      </div>
    );
    return (
      <div style={{ padding: 20 }}>
        {/* Hinweis-Box */}
        <div style={{
          background: 'var(--brand-cream)', borderRadius: 6, padding: 16, marginBottom: 16,
          border: '1px solid var(--brand-warm-gray)',
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
          }}>So funktioniert es</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--brand-text)' }}>
            <Step n={1}>ETV-Protokoll hochladen (PDF oder Foto, mehrere Seiten moeglich)</Step>
            <Step n={2}>Claude erkennt automatisch alle Tagesordnungspunkte mit Beschluss, Abstimmung und Folgeaufgabe</Step>
            <Step n={3}>TOPs pruefen, einzelne abwaehlen oder per Rueckfrage anpassen lassen</Step>
            <Step n={4}>Bestaetigen &rarr; Hauptticket "Nachbereitung ETV" + Untertickets pro TOP werden in Immoware24 erstellt, das PDF wird ans Hauptticket angehaengt</Step>
          </div>
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--brand-warm-gray)',
            fontSize: 11, color: 'var(--brand-text-muted)', lineHeight: 1.5,
          }}>
            <strong>Hinweis:</strong> Bei Bau-Tickets wird die Bausumme initial auf 0,00&nbsp;&euro; gesetzt &mdash; bitte spaeter im Ticket nachpflegen. Sachbearbeiter-Zuordnung erfolgt nach Standort.
          </div>
        </div>

        <div
          onDrop={handleFileDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => !disabled && document.getElementById('etv-file-input').click()}
          style={{
            border: '2px dashed var(--brand-warm-gray)', borderRadius: 8, padding: '32px 20px',
            textAlign: 'center', cursor: disabled ? 'not-allowed' : 'pointer',
            background: files.length ? 'var(--brand-cream)' : '#fff',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <input
            id="etv-file-input"
            type="file"
            multiple
            accept=".pdf,.jpg,.png"
            style={{ display: 'none' }}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
          {files.length ? (
            <div style={{ fontSize: 13, color: 'var(--brand-navy)' }}>
              {files.map(f => f.name).join(', ')}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
              ETV-Protokoll hier ablegen oder klicken (PDF oder Foto)
            </div>
          )}
        </div>

        {status === 'analyzing' && (
          <div style={{ marginTop: 16, padding: 12, background: 'var(--brand-cream)', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: 'var(--brand-navy)', marginBottom: 6 }}>{progressMsg}</div>
            <div style={{ height: 6, background: '#e0e0e0', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--brand-gold)', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {error && <div style={{ marginTop: 12, padding: 10, background: '#fce8e6', borderRadius: 6, fontSize: 12, color: '#b71c1c' }}>{error}</div>}

        <button
          onClick={handleAnalyse}
          disabled={!files.length || disabled}
          style={{
            marginTop: 16, width: '100%', padding: '10px 16px',
            background: files.length && !disabled ? 'var(--brand-navy)' : '#ccc',
            color: '#fff', border: 'none', borderRadius: 6, fontSize: 13,
            cursor: files.length && !disabled ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-body)',
          }}
        >
          {disabled ? 'Analyse läuft...' : 'ETV-Protokoll analysieren'}
        </button>
      </div>
    );
  }

  // Ergebnis
  if (status === 'done') {
    const r = result || {};
    const haupt = r.hauptticket || {};
    const unter = r.untertickets || [];
    const fehler = r.fehler || [];
    return (
      <div style={{ padding: 20 }}>
        <div style={{ padding: 16, background: '#e6f4ea', borderRadius: 8, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1b5e20', marginBottom: 8 }}>Tickets erstellt</div>
          {haupt.id && <div style={{ fontSize: 13 }}>Überticket: #{haupt.id} — {haupt.titel}</div>}
          {unter.map((t, i) => <div key={i} style={{ fontSize: 12, color: '#333', marginTop: 2 }}>#{t.id} — {t.titel}</div>)}
          {fehler.length > 0 && fehler.map((f, i) => <div key={i} style={{ fontSize: 12, color: '#b71c1c', marginTop: 4 }}>Fehler: {f.titel} — {f.fehler}</div>)}
        </div>
        <button onClick={() => { setStatus('idle'); setFiles([]); setResult(null); setCurrentAnalyse(null); setTops([]); }}
          style={{ padding: '8px 16px', background: 'var(--brand-navy)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
          Neues Protokoll
        </button>
      </div>
    );
  }

  // Creating-Phase mit Progress
  if (status === 'creating') {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ padding: 16, background: 'var(--brand-cream)', borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--brand-navy)' }}>Tickets werden in Immoware24 erstellt...</div>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>{progressMsg}</div>
          <div style={{ height: 6, background: '#e0e0e0', borderRadius: 3 }}>
            <div style={{ height: '100%', width: `${progressPct}%`, background: 'var(--brand-gold)', borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
        {error && <div style={{ marginTop: 12, padding: 10, background: '#fce8e6', borderRadius: 6, fontSize: 12, color: '#b71c1c' }}>{error}</div>}
      </div>
    );
  }

  // Review-Phase
  return (
    <div style={{ marginTop: 0 }}>
      <div style={{
        padding: '10px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
        fontSize: 13, fontWeight: 600, display: 'flex', justifyContent: 'space-between',
      }}>
        <span>TOP-Analyse prüfen ({ticketCount} Tickets){refineCount > 0 ? ` — ${refineCount}x angepasst` : ''}</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>
          {currentAnalyse?.objekt} — {currentAnalyse?.datum}
        </span>
      </div>

      {/* Live-Fortschrittsleiste bei Preview */}
      {isPreview && (
        <div style={{
          padding: '8px 20px', background: '#fff8e1', borderBottom: '1px solid #f5d879',
          fontSize: 12, color: '#5d4e1a', display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{
            width: 14, height: 14, borderRadius: '50%', border: '2px solid #5d4e1a',
            borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite',
          }} />
          <span style={{ flex: 1 }}>
            <strong>Kurzscan fertig.</strong> Beschlusstexte + Abstimmungsdaten werden im Hintergrund geladen
            ({progressPct}%). Du kannst schon Haken setzen oder eine andere Seite oeffnen.
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Kopf-Meta: Versammlungsleiter / Protokollführer / Teilnehmer */}
      {(currentAnalyse?.versammlungsleiter || currentAnalyse?.protokollfuehrer || currentAnalyse?.teilnehmer_count) && (
        <div style={{
          padding: '8px 20px', borderBottom: '1px solid #eee', background: '#fafafa',
          fontSize: 11, color: 'var(--brand-text-muted)', display: 'flex', gap: 16, flexWrap: 'wrap',
        }}>
          {currentAnalyse.versammlungsleiter && <span><strong>Leitung:</strong> {currentAnalyse.versammlungsleiter}</span>}
          {currentAnalyse.protokollfuehrer && <span><strong>Protokoll:</strong> {currentAnalyse.protokollfuehrer}</span>}
          {currentAnalyse.teilnehmer_count ? <span><strong>Teilnehmer:</strong> {currentAnalyse.teilnehmer_count}</span> : null}
          {currentAnalyse.mea_anwesend && <span><strong>MEA:</strong> {currentAnalyse.mea_anwesend}</span>}
          {currentAnalyse.versammlungstyp && <span><strong>Typ:</strong> {currentAnalyse.versammlungstyp}</span>}
        </div>
      )}

      <div style={{ padding: '4px 20px', maxHeight: 460, overflowY: 'auto' }}>
        {tops.map((top, idx) => {
          const isExcluded = excluded.has(idx);
          const isExpanded = expanded.has(idx);
          const sc = statusColors[top.beschluss_status] || statusColors.kein_beschluss;
          const hasTicket = top.ticket_typ !== 'kein_ticket';
          const assignee = top.zugewiesen_an || '';
          const assigneeDisplay = assignee === 'BUCHHALTER_ABFRAGEN'
            ? <span style={{ color: '#b36b00', fontStyle: 'italic' }}>Buchhalter (wird abgefragt)</span>
            : assignee || <span style={{ color: '#ccc' }}>—</span>;
          const hasDetail = top.beschluss_text || top.handlungsaufgabe || top.abstimmung || top.anmerkungen;
          return (
            <div key={idx} style={{
              borderBottom: '1px solid #eee', opacity: isExcluded ? 0.45 : 1,
              padding: '6px 0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={!isExcluded && hasTicket}
                  onChange={() => { const n = new Set(excluded); if (n.has(idx)) n.delete(idx); else n.add(idx); setExcluded(n); }}
                  disabled={!hasTicket}
                  style={{ margin: 0 }} />
                <span style={{
                  padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                  background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
                }}>TOP {top.top_nr}</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: hasTicket ? 500 : 400 }}>{top.titel}</span>
                <span style={{
                  padding: '1px 5px', borderRadius: 3, fontSize: 10,
                  background: sc.bg, color: sc.color,
                }}>{top.beschluss_status}</span>
                <span style={{ fontSize: 10, color: '#999', width: 90, textAlign: 'right' }}>
                  {hasTicket ? top.ticket_typ?.replace(/_/g, ' ') : '—'}
                </span>
                {hasDetail && (
                  <button
                    onClick={() => { const n = new Set(expanded); if (n.has(idx)) n.delete(idx); else n.add(idx); setExpanded(n); }}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: 11, color: 'var(--brand-navy)', padding: '0 4px', width: 20,
                    }}
                    title={isExpanded ? 'Einklappen' : 'Details'}
                  >{isExpanded ? '▾' : '▸'}</button>
                )}
                {!hasDetail && <span style={{ width: 20 }} />}
              </div>
              {/* Zweite Zeile: Zuweisung + Fälligkeit — immer sichtbar wenn Ticket erstellt wird */}
              {hasTicket && (
                <div style={{
                  display: 'flex', gap: 14, marginLeft: 32, marginTop: 2, fontSize: 11,
                  color: 'var(--brand-text-muted)', flexWrap: 'wrap',
                }}>
                  <span>👤 {assigneeDisplay}</span>
                  {top.faellig && <span>📅 {top.faellig}</span>}
                  {top.handlungsaufgabe && (
                    <span style={{ flex: 1, minWidth: 200, color: 'var(--brand-text)' }}>
                      ➜ {top.handlungsaufgabe}
                    </span>
                  )}
                </div>
              )}
              {/* Aufklappbare Details */}
              {isExpanded && hasDetail && (
                <div style={{
                  marginLeft: 32, marginTop: 6, marginBottom: 4, padding: 10,
                  background: '#fafafa', borderRadius: 4, fontSize: 11, lineHeight: 1.5,
                }}>
                  {top.beschluss_text && (
                    <div style={{ marginBottom: 4 }}>
                      <strong style={{ color: 'var(--brand-navy)' }}>Beschluss:</strong> {top.beschluss_text}
                    </div>
                  )}
                  {top.abstimmung && (
                    <div style={{ marginBottom: 4 }}>
                      <strong style={{ color: 'var(--brand-navy)' }}>Abstimmung:</strong> {top.abstimmung}
                    </div>
                  )}
                  {top.anmerkungen && (
                    <div style={{ color: 'var(--brand-text-muted)', fontStyle: 'italic' }}>
                      ℹ {top.anmerkungen}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Rückfrage */}
      <div style={{ padding: '8px 20px', borderTop: '1px solid #eee' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            placeholder="Rückfrage oder Korrektur (z.B. 'Buchhalter ist Daniel Mikhaylets')"
            disabled={refining}
            style={{ flex: 1, padding: '6px 10px', fontSize: 12, borderRadius: 4, border: '1px solid var(--brand-warm-gray)', fontFamily: 'var(--font-body)' }}
            onKeyDown={e => e.key === 'Enter' && handleRefine()}
          />
          <button onClick={handleRefine} disabled={!feedbackText.trim() || refining}
            style={{ padding: '6px 14px', fontSize: 12, borderRadius: 4, border: 'none', background: refining ? '#ccc' : 'var(--brand-navy)', color: '#fff', cursor: refining ? 'wait' : 'pointer' }}>
            {refining ? '...' : 'Anpassen'}
          </button>
        </div>
        {refineError && <div style={{ marginTop: 4, fontSize: 11, color: '#b71c1c' }}>{refineError}</div>}
      </div>

      {error && <div style={{ margin: '0 20px 8px', padding: 8, background: '#fce8e6', borderRadius: 6, fontSize: 12, color: '#b71c1c' }}>{error}</div>}

      {/* Erstellen-Button */}
      <div style={{ padding: '8px 20px 16px' }}>
        <button onClick={handleCreateTickets} disabled={ticketCount === 0 || isPreview}
          title={isPreview ? 'Bitte warten bis die Detail-Analyse abgeschlossen ist' : ''}
          style={{
            width: '100%', padding: '10px 16px', fontSize: 13,
            background: (ticketCount > 0 && !isPreview) ? 'var(--brand-navy)' : '#ccc',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: (ticketCount > 0 && !isPreview) ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--font-body)',
          }}>
          {isPreview
            ? `Details werden geladen... (${progressPct}%)`
            : `${ticketCount} Tickets in Immoware24 erstellen`}
        </button>
      </div>
    </div>
  );
}



// ── Aushang: Editierbare Vorschau vor PDF-Erzeugung ──
function AushangPreview({ result, token, setStatus, setResult, setError }) {
  const [betreff, setBetreff] = React.useState(result.betreff || '');
  const [inhaltHtml, setInhaltHtml] = React.useState(result.inhalt_html || '');
  const [generating, setGenerating] = React.useState(false);
  const editorRef = React.useRef(null);

  // Sync contentEditable -> state
  const handleEditorInput = React.useCallback(() => {
    if (editorRef.current) {
      setInhaltHtml(editorRef.current.innerHTML);
    }
  }, []);

  // Set initial HTML content once
  React.useEffect(() => {
    if (editorRef.current && result.inhalt_html) {
      editorRef.current.innerHTML = result.inhalt_html;
    }
  }, []);

  const handleGeneratePdf = async () => {
    setGenerating(true);
    setError(null);
    // Read latest content from contentEditable
    const finalHtml = editorRef.current ? editorRef.current.innerHTML : inhaltHtml;
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({
        _phase: 'generate_pdf',
        betreff: betreff,
        inhalt_html: finalHtml,
        adresse: result.adresse,
        objekt: result.objekt,
        datum: result.datum,
      }));
      const res = await fetch(`${API_URL}/skills/aushang/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('done');
        setResult(data.result);
      } else {
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler bei der PDF-Erzeugung');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      {/* Info Banner */}
      <div style={{
        padding: '12px 16px', background: 'var(--brand-cream)', border: '1px solid var(--brand-warm-gray)',
        borderRadius: 6, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 18 }}>&#9998;</span>
        <span style={{ fontSize: 13, color: 'var(--brand-navy)' }}>
          <strong>KI-Vorschlag</strong> &mdash; Text und Betreff k&ouml;nnen vor der PDF-Erzeugung angepasst werden.
        </span>
      </div>

      {/* Betreff */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 4 }}>
          Betreff
        </label>
        <input
          value={betreff}
          onChange={e => setBetreff(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 4, fontFamily: 'var(--font-body)', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Inhalt - contentEditable div fuer WYSIWYG HTML */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 4 }}>
          Aushang-Text (HTML)
        </label>
        <div
          ref={editorRef}
          contentEditable
          onInput={handleEditorInput}
          style={{
            width: '100%', minHeight: 200, padding: '12px 14px', fontSize: 13, lineHeight: 1.6,
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            fontFamily: "'Segoe UI', Calibri, Arial, sans-serif",
            background: '#fff', boxSizing: 'border-box', overflow: 'auto',
            outline: 'none',
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
          Tipp: Text direkt bearbeiten. Formatierungen (fett, kursiv, Listen) bleiben erhalten.
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          onClick={handleGeneratePdf}
          disabled={generating || !betreff.trim()}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 600, border: 'none', borderRadius: 6,
            background: generating || !betreff.trim() ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
            color: '#fff', cursor: generating || !betreff.trim() ? 'not-allowed' : 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          {generating ? 'PDF wird erstellt...' : 'PDF erstellen'}
        </button>
      </div>
    </div>
  );
}


// ── AuftragKvaWizard — Mehrstufiger Wizard für Auftragsvergabe + KVA ──

function AuftragKvaStepIndicator({ current, steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
      {steps.map((label, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === current;
        const isDone = stepNum < current;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div style={{ flex: 1, height: 2, background: isDone ? T.gold : T.warmGray, transition: 'background 0.3s' }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, fontFamily: T.fontBody,
                background: isActive || isDone ? T.gold : '#fff',
                color: isActive || isDone ? T.navy : T.textMuted,
                border: isActive || isDone ? `2px solid ${T.gold}` : `2px solid ${T.warmGray}`,
                transition: 'all 0.3s',
              }}>{isDone ? '✓' : stepNum}</div>
              <span style={{
                fontSize: 11, fontWeight: isActive ? 700 : 500,
                color: isActive ? T.navy : T.textMuted,
                fontFamily: T.fontBody, textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TierBadge({ tier }) {
  const colors = { A: '#c9a84c', B: '#9e9e9e', C: '#cd7f32', D: '#78909c' };
  const labels = { A: 'Tier A', B: 'Tier B', C: 'Tier C', D: 'Tier D' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: colors[tier] || '#ccc', color: tier === 'A' ? '#1a2744' : '#fff',
      fontFamily: T.fontBody, letterSpacing: '0.05em', textTransform: 'uppercase',
    }}>{labels[tier] || tier}</span>
  );
}

// ── Staffelmiete Wizard ──────────────────────────────────────────
function StaffelmietWizard() {
  const { token } = useAuth();
  const { startTracking, jobs } = useJobTracker();

  const [step, setStep] = useState(1);
  const stepLabels = ['Objektauswahl', 'Mieter', 'Konfiguration', 'Ergebnis'];

  // Step 1: Objekt
  const [objekte, setObjekte] = useState([]);
  const [objektSearch, setObjektSearch] = useState('');
  const [selectedObjekt, setSelectedObjekt] = useState(null);

  // Step 2: Mieter
  const [mieter, setMieter] = useState([]);
  const [selectedMieter, setSelectedMieter] = useState(null);
  const [fetchJobId, setFetchJobId] = useState(null);
  const [fetchStatus, setFetchStatus] = useState('idle');
  const [fetchMsg, setFetchMsg] = useState('');

  // Step 3: Konfiguration
  const [kaltmiete, setKaltmiete] = useState('');
  const [zahlungsart, setZahlungsart] = useState('Miete');
  const [intervall, setIntervall] = useState('12');
  const [prozent, setProzent] = useState('');
  const [startDatum, setStartDatum] = useState('');
  const [anzahl, setAnzahl] = useState('3');

  // Step 4: Ergebnis
  const [createJobId, setCreateJobId] = useState(null);
  const [createStatus, setCreateStatus] = useState('idle');
  const [createMsg, setCreateMsg] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Objekte laden
  useEffect(function() {
    fetch(API_URL + '/admin/objekte/aktiv', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(data) { if (data && data.objekte) setObjekte(data.objekte.filter(function(o) { return o.verwaltungsart === 'Mietverwaltung'; })); })
      .catch(function() {});
  }, [token]);

  // Fetch-Job Polling
  const fetchJob = fetchJobId && jobs ? jobs[fetchJobId] : null;
  useEffect(function() {
    if (!fetchJob) return;
    if (fetchJob.status === 'running') {
      setFetchStatus('running');
      setFetchMsg(fetchJob.message || 'Mieter werden geladen...');
    } else if (fetchJob.status === 'done') {
      setFetchStatus('done');
      var res = fetchJob.result || {};
      setMieter(res.mieter || []);
      if ((res.mieter || []).length === 0) setFetchMsg('Keine aktiven Mieter gefunden.');
      else setFetchMsg('');
    } else if (fetchJob.status === 'error') {
      setFetchStatus('error');
      setFetchMsg(fetchJob.message || 'Fehler beim Laden');
    }
  }, [fetchJob]);

  // Create-Job Polling
  const createJob = createJobId && jobs ? jobs[createJobId] : null;
  useEffect(function() {
    if (!createJob) return;
    if (createJob.status === 'running') {
      setCreateStatus('running');
      setCreateMsg(createJob.message || 'Staffelmieten werden erstellt...');
    } else if (createJob.status === 'done') {
      setCreateStatus('done');
      setResult(createJob.result);
    } else if (createJob.status === 'error') {
      setCreateStatus('error');
      setError(createJob.message || 'Fehler beim Erstellen');
    }
  }, [createJob]);

  // Mieter laden
  var handleFetchMieter = function() {
    if (!selectedObjekt) return;
    setFetchStatus('running');
    setFetchMsg('Starte...');
    setMieter([]);
    setSelectedMieter(null);

    var formData = new FormData();
    formData.append('data', JSON.stringify({ _phase: 'fetch_mieter', objekt_immoware_id: selectedObjekt.immoware_id }));

    fetch(API_URL + '/skills/staffelmiete/execute', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: formData,
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.result && data.result.job_id) {
          setFetchJobId(data.result.job_id);
          startTracking(data.result.job_id, 'staffelmiete');
          setStep(2);
        } else {
          setFetchStatus('error');
          setFetchMsg('Kein Job gestartet');
        }
      })
      .catch(function(err) {
        setFetchStatus('error');
        setFetchMsg('Fehler: ' + err.message);
      });
  };

  // Staffeln berechnen (Client-side)
  var berechneteStaffeln = useMemo(function() {
    // Tausenderpunkte entfernen, dann Komma → Punkt fuer parseFloat
    var km = parseFloat((kaltmiete || '0').replace(/\./g, '').replace(',', '.'));
    var pct = parseFloat((prozent || '0').replace(',', '.'));
    var count = parseInt(anzahl || '0');
    var start = startDatum;
    if (!km || !pct || !count || !start) return [];

    // Datum parsen (DD.MM.YYYY)
    var parts = start.split('.');
    if (parts.length !== 3) return [];
    var d = parseInt(parts[0]);
    var m = parseInt(parts[1]) - 1;
    var y = parseInt(parts[2]);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return [];

    var intervalMonths = parseInt(intervall || '12');
    var staffeln = [];
    var currentBetrag = km;

    for (var i = 0; i < count; i++) {
      var neuBetrag = Math.round(currentBetrag * (1 + pct / 100) * 100) / 100;
      var date = new Date(y, m + (i * intervalMonths), d);
      var dateStr = String(date.getDate()).padStart(2, '0') + '.' + String(date.getMonth() + 1).padStart(2, '0') + '.' + date.getFullYear();
      staffeln.push({
        nr: i + 1,
        datum: dateStr,
        betrag: neuBetrag.toFixed(2),
        erhoehung: (neuBetrag - currentBetrag).toFixed(2),
      });
      currentBetrag = neuBetrag;
    }
    return staffeln;
  }, [kaltmiete, prozent, anzahl, startDatum, intervall]);

  // Staffeln erstellen
  var handleCreateStaffeln = function() {
    if (berechneteStaffeln.length === 0 || !selectedMieter) return;
    setCreateStatus('running');
    setCreateMsg('Starte...');
    setError('');
    setResult(null);

    var staffelnForApi = berechneteStaffeln.map(function(s) {
      return { datum: s.datum, betrag: s.betrag.replace('.', ','), nr: s.nr };
    });

    // Zahlungsart aus dem Konfigurationsfeld (vom Nutzer ausgewaehlt)

    var formData = new FormData();
    formData.append('data', JSON.stringify({
      _phase: 'create_staffeln',
      tenancy_id: selectedMieter.tenancy_id,
      objekt_immoware_id: selectedObjekt ? selectedObjekt.immoware_id : '',
      rucr_id: selectedMieter.rucr_id || '',
      rent_unit_id: selectedMieter.rent_unit_id || selectedMieter.einheit_id || '',
      zahlungsart: zahlungsart,
      staffeln: staffelnForApi,
    }));

    fetch(API_URL + '/skills/staffelmiete/execute', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: formData,
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.result && data.result.job_id) {
          setCreateJobId(data.result.job_id);
          startTracking(data.result.job_id, 'staffelmiete');
          setStep(4);
        } else {
          setCreateStatus('error');
          setError('Kein Job gestartet');
        }
      })
      .catch(function(err) {
        setCreateStatus('error');
        setError('Fehler: ' + err.message);
      });
  };

  // Reset
  var handleReset = function() {
    setStep(1);
    setSelectedObjekt(null);
    setMieter([]);
    setSelectedMieter(null);
    setFetchJobId(null);
    setFetchStatus('idle');
    setKaltmiete('');
    setZahlungsart('Miete');
    setProzent('');
    setStartDatum('');
    setAnzahl('3');
    setIntervall('12');
    setCreateJobId(null);
    setCreateStatus('idle');
    setResult(null);
    setError('');
  };

  // Gefilterte Objekte
  var filteredObjekte = useMemo(function() {
    if (!objektSearch) return objekte;
    var s = objektSearch.toLowerCase();
    return objekte.filter(function(o) {
      return o.objekt_nr.indexOf(s) !== -1 || (o.adresse || '').toLowerCase().indexOf(s) !== -1;
    });
  }, [objekte, objektSearch]);

  // Styles
  var cardStyle = { background: '#fff', borderRadius: 12, border: '1px solid ' + T.warmGray, padding: 24, marginBottom: 20 };
  var btnPrimary = {
    padding: '10px 24px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
    background: T.navy, color: T.gold, border: 'none', fontFamily: T.fontBody,
  };
  var btnDisabled = { ...btnPrimary, opacity: 0.4, cursor: 'not-allowed' };
  var btnBack = { background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 12, marginBottom: 16, fontFamily: T.fontBody };

  return (
    <div style={{ padding: '16px 0' }}>
      <AuftragKvaStepIndicator current={step} steps={stepLabels} />

      {/* ═══ STEP 1: Objektauswahl ═══ */}
      {step === 1 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, fontFamily: T.fontHead, marginBottom: 16 }}>
            Objekt auswaehlen
          </div>
          <input
            value={objektSearch}
            onChange={function(e) { setObjektSearch(e.target.value); }}
            placeholder="Suche nach Nr. oder Adresse..."
            style={{
              width: '100%', padding: '10px 12px', fontSize: 13, borderRadius: 6, marginBottom: 12,
              border: '1px solid ' + T.warmGray, fontFamily: T.fontBody, boxSizing: 'border-box',
            }}
          />
          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid ' + T.warmGray, borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: T.fontBody }}>
              <thead>
                <tr style={{ background: T.cream, position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Nr.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Adresse</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>IW-ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredObjekte.map(function(o) {
                  var isSel = selectedObjekt && selectedObjekt.immoware_id === o.immoware_id;
                  return (
                    <tr key={o.immoware_id}
                      onClick={function() { setSelectedObjekt(o); }}
                      style={{
                        cursor: 'pointer', background: isSel ? 'rgba(201,168,76,0.15)' : 'transparent',
                        borderBottom: '1px solid ' + T.warmGray,
                      }}>
                      <td style={{ padding: '8px 10px', fontWeight: isSel ? 700 : 400 }}>{o.objekt_nr}</td>
                      <td style={{ padding: '8px 10px' }}>{o.adresse}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11 }}>{o.immoware_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>
            {filteredObjekte.length} Mietverwaltungs-Objekte{selectedObjekt ? ' — Ausgewaehlt: ' + selectedObjekt.adresse : ''}
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleFetchMieter}
              disabled={!selectedObjekt}
              style={selectedObjekt ? btnPrimary : btnDisabled}
            >Mieter laden</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: Mieterauswahl ═══ */}
      {step === 2 && (
        <div style={cardStyle}>
          <button onClick={function() { setStep(1); }} style={btnBack}>
            &larr; Zurueck zur Objektauswahl
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, fontFamily: T.fontHead, marginBottom: 4 }}>
            Mieter auswaehlen
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>
            {selectedObjekt ? selectedObjekt.adresse + ' (Nr. ' + selectedObjekt.objekt_nr + ')' : ''}
          </div>

          {fetchStatus === 'running' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 13, color: T.navy, marginBottom: 8 }}>{fetchMsg}</div>
              <div style={{ width: '100%', height: 4, background: T.cream, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: (fetchJob ? fetchJob.progress : 10) + '%', height: '100%', background: T.gold, borderRadius: 2, transition: 'width 0.5s' }} />
              </div>
            </div>
          )}

          {fetchStatus === 'error' && (
            <div style={{ padding: 16, background: 'var(--brand-danger-bg, #fef2f2)', borderRadius: 8, color: T.danger, fontSize: 13 }}>
              {fetchMsg}
            </div>
          )}

          {fetchStatus === 'done' && mieter.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: T.textMuted, fontSize: 13 }}>
              Keine aktiven Mieter in diesem Objekt gefunden.
            </div>
          )}

          {fetchStatus === 'done' && mieter.length > 0 && (
            <>
              <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid ' + T.warmGray, borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: T.fontBody }}>
                  <thead>
                    <tr style={{ background: T.cream, position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Name</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Einheit</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Typ</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: T.navy }}>Bruttomiete</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Mietbeginn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mieter.map(function(m) {
                      var isSel = selectedMieter && selectedMieter.tenancy_id === m.tenancy_id;
                      return (
                        <tr key={m.tenancy_id}
                          onClick={function() {
                            setSelectedMieter(m);
                            setKaltmiete(m.kaltmiete || '');
                            setZahlungsart(m.zahlungsart || 'Miete');
                          }}
                          style={{
                            cursor: 'pointer', background: isSel ? 'rgba(201,168,76,0.15)' : 'transparent',
                            borderBottom: '1px solid ' + T.warmGray,
                          }}>
                          <td style={{ padding: '8px 10px', fontWeight: isSel ? 700 : 400 }}>{m.name}</td>
                          <td style={{ padding: '8px 10px' }}>{m.einheit}</td>
                          <td style={{ padding: '8px 10px', fontSize: 11 }}>{m.zahlungsart || 'Miete'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{m.kaltmiete ? m.kaltmiete + ' EUR' : '-'}</td>
                          <td style={{ padding: '8px 10px' }}>{m.mietbeginn}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={function() { if (selectedMieter) setStep(3); }}
                  disabled={!selectedMieter}
                  style={selectedMieter ? btnPrimary : btnDisabled}
                >Weiter zur Konfiguration</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══ STEP 3: Konfiguration ═══ */}
      {step === 3 && (
        <div style={cardStyle}>
          <button onClick={function() { setStep(2); }} style={btnBack}>
            &larr; Zurueck zur Mieterauswahl
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, fontFamily: T.fontHead, marginBottom: 4 }}>
            Staffelmieten konfigurieren
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>
            {selectedMieter ? selectedMieter.name + ' — ' + selectedMieter.einheit : ''}
          </div>

          {/* Eingabefelder */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Aktuelle Bruttomiete (EUR)<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <input
                type="text" value={kaltmiete}
                onChange={function(e) { setKaltmiete(e.target.value); }}
                placeholder="z.B. 850,00"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: 'monospace', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Zahlungsart<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <select value={zahlungsart} onChange={function(e) { setZahlungsart(e.target.value); }}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: T.fontBody, background: '#fff', boxSizing: 'border-box',
                }}>
                <option value="Miete">Miete</option>
                <option value="Stellplatz">Stellplatz</option>
                <option value="Garage">Garage</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Erhoehungsintervall<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <select value={intervall} onChange={function(e) { setIntervall(e.target.value); }}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: T.fontBody, background: '#fff', boxSizing: 'border-box',
                }}>
                <option value="12">Alle 12 Monate</option>
                <option value="24">Alle 24 Monate</option>
                <option value="36">Alle 36 Monate</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Prozentuale Erhoehung (%)<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <input
                type="text" value={prozent}
                onChange={function(e) { setProzent(e.target.value); }}
                placeholder="z.B. 3,0"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: 'monospace', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Erste Staffel ab (TT.MM.JJJJ)<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <input
                type="text" value={startDatum}
                onChange={function(e) { setStartDatum(e.target.value); }}
                placeholder="z.B. 01.01.2027"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: 'monospace', boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: T.navy, display: 'block', marginBottom: 4, fontFamily: T.fontBody }}>
                Anzahl Staffeln (1-10)<span style={{ color: T.gold, marginLeft: 2 }}>*</span>
              </label>
              <input
                type="number" min="1" max="10" value={anzahl}
                onChange={function(e) { setAnzahl(e.target.value); }}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 6,
                  border: '1px solid ' + T.warmGray, fontFamily: 'monospace', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Vorschau-Tabelle */}
          {berechneteStaffeln.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, marginBottom: 8, fontFamily: T.fontBody }}>
                Vorschau der Staffelmieten
              </div>
              <div style={{ border: '1px solid ' + T.warmGray, borderRadius: 6, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: T.fontBody }}>
                  <thead>
                    <tr style={{ background: T.cream }}>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: T.navy }}>Nr.</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: T.navy }}>Gueltig ab</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: T.navy }}>Neue Kaltmiete</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: T.navy }}>Erhoehung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {berechneteStaffeln.map(function(s) {
                      return (
                        <tr key={s.nr} style={{ borderBottom: '1px solid ' + T.warmGray }}>
                          <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{s.nr}</td>
                          <td style={{ padding: '8px 10px' }}>{s.datum}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{s.betrag} EUR</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', color: T.success }}>+{s.erhoehung} EUR</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>
                Gesamterhoehung: {kaltmiete ? (parseFloat(berechneteStaffeln[berechneteStaffeln.length - 1].betrag) - parseFloat((kaltmiete || '0').replace(/\./g, '').replace(',', '.'))).toFixed(2) : '—'} EUR
                ({kaltmiete && berechneteStaffeln.length > 0 ? (((parseFloat(berechneteStaffeln[berechneteStaffeln.length - 1].betrag) / parseFloat((kaltmiete || '1').replace(/\./g, '').replace(',', '.'))) - 1) * 100).toFixed(1) : '0'}%)
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              onClick={handleCreateStaffeln}
              disabled={berechneteStaffeln.length === 0}
              style={berechneteStaffeln.length > 0 ? btnPrimary : btnDisabled}
            >Staffelmieten erstellen</button>
          </div>
        </div>
      )}

      {/* ═══ STEP 4: Ergebnis ═══ */}
      {step === 4 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, fontFamily: T.fontHead, marginBottom: 16 }}>
            Ergebnis
          </div>

          {createStatus === 'running' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 13, color: T.navy, marginBottom: 8 }}>{createMsg}</div>
              <div style={{ width: '100%', height: 4, background: T.cream, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: (createJob ? createJob.progress : 10) + '%', height: '100%', background: T.gold, borderRadius: 2, transition: 'width 0.5s' }} />
              </div>
            </div>
          )}

          {createStatus === 'error' && (
            <div style={{ padding: 16, background: 'var(--brand-danger-bg, #fef2f2)', borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.danger, marginBottom: 4 }}>Fehler</div>
              <div style={{ fontSize: 12, color: T.danger }}>{error}</div>
            </div>
          )}

          {createStatus === 'done' && result && (
            <div>
              <div style={{
                padding: 16, borderRadius: 8, marginBottom: 16,
                background: result.success ? 'var(--brand-success-bg, #f0fdf4)' : 'var(--brand-danger-bg, #fef2f2)',
                border: '1px solid ' + (result.success ? T.success : T.danger),
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: result.success ? T.success : T.danger, marginBottom: 4 }}>
                  {result.success ? 'Staffelmieten erfolgreich erstellt' : 'Fehler beim Erstellen'}
                </div>
                <div style={{ fontSize: 12, color: result.success ? T.success : T.danger }}>
                  {result.summary || result.error || ''}
                </div>
              </div>

              {result.aenderungen && result.aenderungen.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.navy, marginBottom: 6 }}>Details:</div>
                  {result.aenderungen.map(function(a, idx) {
                    return (
                      <div key={idx} style={{ fontSize: 12, color: 'var(--brand-text)', padding: '3px 0', borderBottom: '1px solid ' + T.cream }}>
                        {a}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {(createStatus === 'done' || createStatus === 'error') && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleReset} style={btnPrimary}>
                Neuer Vorgang
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function AuftragKvaWizard({ skill }) {
  const { token } = useAuth();
  const { startTracking, jobs } = useJobTracker();
  const isAuftrag = skill.id === 'auftragsvergabe';

  // Step state
  const [step, setStep] = useState(1);
  const stepLabels = isAuftrag
    ? ['Objekt & Ticket', 'Dienstleister', 'Zusammenfassung']
    : ['Objekt & Ticket', 'Dienstleister', 'Zusammenfassung'];

  // Step 1: Objekt
  const [objekte, setObjekte] = useState([]);
  const [objektSearch, setObjektSearch] = useState('');
  const [selectedObjekt, setSelectedObjekt] = useState(null);

  // Step 1: Ticket
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketMode, setTicketMode] = useState('neu'); // 'neu' or ticket id
  const [ticketTitel, setTicketTitel] = useState('');

  // Step 1: Vorlage
  const vorlagenOptionen = isAuftrag
    ? ['Auftragserteilung - Kostenvoranschlag', 'Auftragserteilung - ohne Kostenvoranschlag']
    : ['Anforderung Kostenvoranschlag'];
  const [vorlage, setVorlage] = useState(vorlagenOptionen[0]);

  // Step 1: Leistung
  const [leistungsart, setLeistungsart] = useState('');
  const [leistungsbeschreibung, setLeistungsbeschreibung] = useState('');

  // Step 1: Auftrag-spezifisch
  const [betrag, setBetrag] = useState('');
  const [betragTyp, setBetragTyp] = useState('net');
  const [fristBeginn, setFristBeginn] = useState('7');
  const [fristFertig, setFristFertig] = useState('30');
  const [fristTage, setFristTage] = useState('14');
  const [angebotsnummer, setAngebotsnummer] = useState('');

  // Step 2: DL
  const [gewerke, setGewerke] = useState([]);
  const [selectedGewerk, setSelectedGewerk] = useState('');
  const [dlList, setDlList] = useState([]);
  const [dlSearch, setDlSearch] = useState('');
  const [dlSearchResults, setDlSearchResults] = useState([]);
  const [selectedDl, setSelectedDl] = useState(null);
  const [selectedDlsKva, setSelectedDlsKva] = useState([]); // Multi-Select für KVA: [{id, name, tier, ...}]
  const [dlLoading, setDlLoading] = useState(false);
  const [dlByTier, setDlByTier] = useState({ A: [], B: [], C: [], D: [] });

  // Job tracking
  const [status, setStatus] = useState('idle');
  const [activeJobId, setActiveJobId] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const myJob = activeJobId && jobs ? jobs[activeJobId] : null;

  useEffect(() => {
    if (!myJob) return;
    if (myJob.status === 'running') setStatus('running');
    else if (myJob.status === 'done') { setStatus('done'); setResult(myJob.result); }
    else if (myJob.status === 'error') { setStatus('error'); setError(myJob.message); }
  }, [myJob]);

  // Load Objekte
  useEffect(() => {
    fetch(`${API_URL}/admin/objekte/aktiv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.objekte) setObjekte(data.objekte); })
      .catch(() => {});
  }, [token]);

  // Load Tickets when object selected
  useEffect(() => {
    if (!selectedObjekt) { setTickets([]); return; }
    setTicketsLoading(true);
    fetch(`${API_URL}/skills/auftragsvergabe/tickets?objekt_nr=${selectedObjekt.objekt_nr}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setTickets(data || []); setTicketsLoading(false); })
      .catch(() => setTicketsLoading(false));
  }, [selectedObjekt, token]);

  // Load Gewerke
  useEffect(() => {
    fetch(`${API_URL}/skills/auftragsvergabe/gewerke`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setGewerke(data || []))
      .catch(() => {});
  }, [token]);

  // Load DL when gewerk selected
  useEffect(() => {
    if (!selectedGewerk) { setDlList([]); setDlByTier({ A: [], B: [], C: [], D: [] }); return; }
    setDlLoading(true);
    fetch(`${API_URL}/skills/auftragsvergabe/dienstleister?gewerk=${selectedGewerk}&limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const list = data || [];
        setDlList(list);
        // Gruppiere nach Tier
        const byTier = { A: [], B: [], C: [], D: [] };
        list.forEach(d => { if (byTier[d.tier]) byTier[d.tier].push(d); });
        setDlByTier(byTier);
        setDlLoading(false);
      })
      .catch(() => setDlLoading(false));
  }, [selectedGewerk, token]);

  // DL Freitext-Suche
  useEffect(() => {
    if (dlSearch.length < 2) { setDlSearchResults([]); return; }
    const timer = setTimeout(() => {
      fetch(`${API_URL}/skills/auftragsvergabe/dl-search?q=${encodeURIComponent(dlSearch)}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => setDlSearchResults(data || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [dlSearch, token]);

  // Filtered Objekte
  const filteredObjekte = useMemo(() => {
    if (!objektSearch) return objekte;
    const s = objektSearch.toLowerCase();
    return objekte.filter(o => o.objekt_nr.includes(s) || (o.adresse || '').toLowerCase().includes(s));
  }, [objekte, objektSearch]);

  // DL display list
  const displayDl = dlSearch.length >= 2 ? dlSearchResults : dlList;

  // Validation
  const step1Valid = selectedObjekt && (ticketMode !== 'neu' || ticketTitel.trim()) && vorlage && leistungsart.trim() && leistungsbeschreibung.trim();
  const step2Valid = isAuftrag ? !!selectedDl : (selectedDlsKva.length > 0);

  // Submit
  const handleSubmit = async () => {
    setStatus('running');
    setError('');
    const payload = {
      objekt_nr: selectedObjekt.objekt_nr,
      objekt: selectedObjekt.adresse,
      ticket_id: ticketMode !== 'neu' ? ticketMode : '',
      titel: ticketMode === 'neu' ? ticketTitel : '',
      vorlage,
      leistungsart,
      leistungsbeschreibung,
      dienstleister: isAuftrag ? selectedDl.name : selectedDlsKva.map(d => d.name).join(', '),
      dienstleister_id: isAuftrag ? String(selectedDl.id) : selectedDlsKva.map(d => d.id).join(','),
      dienstleister_liste: isAuftrag ? null : selectedDlsKva.map(d => ({id: d.id, name: d.name, tier: d.tier})),
      ...(isAuftrag ? {
        betrag,
        betrag_typ: betragTyp,
        frist_beginn: fristBeginn,
        frist_fertig: fristFertig,
        angebotsnummer,
      } : {
        frist_tage: fristTage,
      }),
    };
    const fd = new FormData();
    fd.append('data', JSON.stringify(payload));
    try {
      const res = await fetch(`${API_URL}/skills/${skill.id}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.result?.job_id) {
        setActiveJobId(data.result.job_id);
        startTracking(data.result.job_id, skill.id);
      } else if (res.ok) {
        setStatus('done');
        setResult(data.result || data);
      } else {
        setStatus('error');
        setError(data.detail || 'Fehler beim Absenden');
      }
    } catch (e) {
      setStatus('error');
      setError(e.message);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  if (status === 'done' && result) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2e7d32', fontFamily: T.fontBody }}>{result.message || 'Erfolgreich erstellt'}</div>
          {result.ticket_id && <div style={{ marginTop: 8, fontSize: 13 }}>Ticket #{result.ticket_id}</div>}
          {result.ticket_url && <a href={result.ticket_url} target="_blank" rel="noreferrer" style={{ color: T.navy, fontSize: 13 }}>In Immoware24 ansehen</a>}
        </div>
        <button onClick={() => { setStatus('idle'); setStep(1); setResult(null); setSelectedDl(null); setSelectedDlsKva([]); }}
          style={{ ...styles.input, width: 'auto', cursor: 'pointer', background: T.navy, color: '#fff', fontWeight: 600 }}>
          Neuen {isAuftrag ? 'Auftrag' : 'KVA'} erstellen
        </button>
      </div>
    );
  }

  if (status === 'running') {
    const progress = myJob?.progress || 0;
    const message = myJob?.message || 'Verarbeite...';
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600, color: T.navy, fontFamily: T.fontBody }}>{message}</div>
        <div style={{ height: 6, background: T.warmGray, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: T.gold, width: `${progress}%`, transition: 'width 0.5s', borderRadius: 3 }} />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#c62828' }}>Fehler</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>{error}</div>
        </div>
        <button onClick={() => setStatus('idle')}
          style={{ ...styles.input, width: 'auto', cursor: 'pointer', background: T.navy, color: '#fff', fontWeight: 600 }}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <AuftragKvaStepIndicator current={step} steps={stepLabels} />

      {/* ═══ STEP 1: Objekt, Ticket, Leistung ═══ */}
      {step === 1 && (
        <div>
          {/* Objekt-Auswahl (Single Select) */}
          <div style={{ marginBottom: 24 }}>
            <label style={styles.label}>Objekt</label>
            <input
              type="text" placeholder="Objekt suchen..." value={objektSearch}
              onChange={e => setObjektSearch(e.target.value)}
              style={{ ...styles.input, marginBottom: 8 }}
            />
            <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${T.warmGray}`, borderRadius: 6 }}>
              {filteredObjekte.slice(0, 50).map(o => (
                <label key={o.immoware_id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
                  background: selectedObjekt?.immoware_id === o.immoware_id ? 'var(--brand-gold-20, #f5f0e0)' : 'transparent',
                  borderBottom: `1px solid ${T.warmGray}20`,
                }}>
                  <input type="radio" name="objekt" checked={selectedObjekt?.immoware_id === o.immoware_id}
                    onChange={() => { setSelectedObjekt(o); setTicketMode('neu'); }} />
                  <span style={{ fontSize: 13, fontFamily: T.fontBody }}>
                    <strong>{o.objekt_nr}</strong> {o.adresse}
                    <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 8 }}>{o.verwaltungsart === 'Mietverwaltung' ? 'Miete' : o.verwaltungsart === 'WEG-Verwaltung' ? 'WEG' : o.verwaltungsart}</span>
                  </span>
                </label>
              ))}
            </div>
            {selectedObjekt && (
              <div style={{ marginTop: 6, fontSize: 12, color: T.navy, fontWeight: 600 }}>
                Ausgewählt: {selectedObjekt.objekt_nr} {selectedObjekt.adresse}
              </div>
            )}
          </div>

          {/* Ticket-Auswahl */}
          {selectedObjekt && (
            <div style={{ marginBottom: 24 }}>
              <label style={styles.label}>Ticket</label>
              {ticketsLoading ? (
                <div style={{ fontSize: 13, color: T.textMuted }}>Lade Tickets...</div>
              ) : (
                <div style={{ border: `1px solid ${T.warmGray}`, borderRadius: 6 }}>
                  {/* Neues Ticket Option */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer',
                    background: ticketMode === 'neu' ? 'var(--brand-gold-20, #f5f0e0)' : 'transparent',
                    borderBottom: `1px solid ${T.warmGray}30`, fontWeight: 600,
                  }}>
                    <input type="radio" name="ticket" checked={ticketMode === 'neu'} onChange={() => setTicketMode('neu')} />
                    <span style={{ fontSize: 13, fontFamily: T.fontBody, color: T.navy }}>+ Neues Ticket erstellen</span>
                  </label>
                  {/* Bestehende Tickets */}
                  {tickets.map(t => (
                    <label key={t.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
                      background: ticketMode === String(t.id) ? 'var(--brand-gold-20, #f5f0e0)' : 'transparent',
                      borderBottom: `1px solid ${T.warmGray}20`,
                    }}>
                      <input type="radio" name="ticket" checked={ticketMode === String(t.id)}
                        onChange={() => setTicketMode(String(t.id))} />
                      <span style={{ fontSize: 13, fontFamily: T.fontBody }}>
                        <strong>#{t.id}</strong> {t.title?.substring(0, 60)}
                        <span style={{
                          marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 3,
                          background: t.status === 'neu' ? '#e3f2fd' : t.status === 'wartend' ? '#fff3e0' : '#f3e5f5',
                          color: t.status === 'neu' ? '#1565c0' : t.status === 'wartend' ? '#e65100' : '#6a1b9a',
                        }}>{t.status}</span>
                      </span>
                    </label>
                  ))}
                  {tickets.length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: T.textMuted }}>Keine offenen Tickets für dieses Objekt</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Ticket-Titel (nur bei neuem Ticket) */}
          {ticketMode === 'neu' && selectedObjekt && (
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Ticket-Titel</label>
              <input type="text" value={ticketTitel} onChange={e => setTicketTitel(e.target.value)}
                placeholder="z.B. Treppenhausreinigung beauftragen"
                style={styles.input} />
            </div>
          )}

          {/* Vorlage */}
          {selectedObjekt && (
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>{isAuftrag ? 'Auftragsvorlage' : 'Angebotsvorlage'}</label>
              <select value={vorlage} onChange={e => setVorlage(e.target.value)}
                style={{ ...styles.input, cursor: 'pointer' }}>
                {vorlagenOptionen.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          )}

          {/* Leistungsart + Beschreibung */}
          {selectedObjekt && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Leistungsart</label>
                <input type="text" value={leistungsart} onChange={e => setLeistungsart(e.target.value)}
                  placeholder="z.B. Treppenhausreinigung, Elektroinstallation"
                  style={styles.input} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Leistungsbeschreibung</label>
                <textarea value={leistungsbeschreibung} onChange={e => setLeistungsbeschreibung(e.target.value)}
                  placeholder="Detaillierte Beschreibung der gewünschten Leistung..."
                  rows={4} style={{ ...styles.input, resize: 'vertical' }} />
              </div>
            </>
          )}

          {/* Auftrag-spezifische Felder */}
          {isAuftrag && selectedObjekt && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={styles.label}>Betrag (EUR, optional)</label>
                <input type="text" value={betrag} onChange={e => setBetrag(e.target.value)}
                  placeholder="350,00" style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Betragsart</label>
                <select value={betragTyp} onChange={e => setBetragTyp(e.target.value)} style={{ ...styles.input, cursor: 'pointer' }}>
                  <option value="net">Netto</option>
                  <option value="gross">Brutto</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Frühester Beginn (Tage)</label>
                <input type="text" value={fristBeginn} onChange={e => setFristBeginn(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Fertigstellung (Tage)</label>
                <input type="text" value={fristFertig} onChange={e => setFristFertig(e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Angebotsnummer (optional)</label>
                <input type="text" value={angebotsnummer} onChange={e => setAngebotsnummer(e.target.value)} style={styles.input} />
              </div>
            </div>
          )}

          {/* KVA: Frist */}
          {!isAuftrag && selectedObjekt && (
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Angebotsfrist (Tage ab heute)</label>
              <input type="text" value={fristTage} onChange={e => setFristTage(e.target.value)} style={{ ...styles.input, maxWidth: 120 }} />
            </div>
          )}

          {/* Weiter-Button */}
          <button disabled={!step1Valid} onClick={() => setStep(2)}
            style={{
              ...styles.input, width: 'auto', padding: '12px 32px', cursor: step1Valid ? 'pointer' : 'not-allowed',
              background: step1Valid ? T.navy : T.warmGray, color: '#fff', fontWeight: 700, fontSize: 14,
              border: 'none', borderRadius: 6, marginTop: 8,
            }}>
            Weiter zur Dienstleister-Auswahl
          </button>
        </div>
      )}

      {/* ═══ STEP 2: Dienstleister-Auswahl ═══ */}
      {step === 2 && (
        <div>
          <button onClick={() => setStep(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 12, marginBottom: 16, fontFamily: T.fontBody }}>
            ← Zurück zu Schritt 1
          </button>

          {/* Gewerk-Filter */}
          <div style={{ marginBottom: 20 }}>
            <label style={styles.label}>Gewerk filtern</label>
            <select value={selectedGewerk} onChange={e => { setSelectedGewerk(e.target.value); setDlSearch(''); setSelectedDl(null); }}
              style={{ ...styles.input, cursor: 'pointer' }}>
              <option value="">Alle Gewerke</option>
              {gewerke.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>

          {/* Freitext-Suche */}
          <div style={{ marginBottom: 20 }}>
            <label style={styles.label}>Oder Dienstleister suchen</label>
            <input type="text" value={dlSearch} onChange={e => setDlSearch(e.target.value)}
              placeholder="Name eingeben (mind. 2 Zeichen)..."
              style={styles.input} />
          </div>

          {/* KVA: Empfehlungstext */}
          {!isAuftrag && (
            <div style={{ background: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, fontFamily: T.fontBody, color: T.navy }}>
              <strong>Empfehlung:</strong> Bitte aus Tier A, B und C jeweils mindestens einen Dienstleister für die Ausschreibung wählen.
            </div>
          )}

          {/* Auftrag: Single-Select DL-Liste */}
          {isAuftrag && (
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>
                {dlLoading ? 'Lade...' : `${displayDl.length} Dienstleister`}
                {selectedGewerk && ` — ${gewerke.find(g => g.id === selectedGewerk)?.name || selectedGewerk}`}
              </label>
              <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${T.warmGray}`, borderRadius: 6 }}>
                {displayDl.map(dl => (
                  <label key={dl.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer',
                    background: selectedDl?.id === dl.id ? 'var(--brand-gold-20, #f5f0e0)' : 'transparent',
                    borderBottom: `1px solid ${T.warmGray}20`,
                  }}>
                    <input type="radio" name="dl" checked={selectedDl?.id === dl.id}
                      onChange={() => setSelectedDl(dl)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.navy, fontFamily: T.fontBody }}>{dl.name}</div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                        {dl.gewerk_name || dl.gewerk || ''} {dl.plz ? `• ${dl.plz} ${dl.ort || ''}` : ''}
                      </div>
                    </div>
                    <TierBadge tier={dl.tier} />
                  </label>
                ))}
                {displayDl.length === 0 && !dlLoading && (
                  <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
                    {selectedGewerk || dlSearch.length >= 2 ? 'Keine Dienstleister gefunden' : 'Gewerk wählen oder Name suchen'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* KVA: Tier-Gruppen Multi-Select */}
          {!isAuftrag && selectedGewerk && !dlLoading && (
            <>
              {['A', 'B', 'C', 'D'].map(tier => {
                const dls = dlByTier[tier] || [];
                if (dls.length === 0) return null;
                return (
                  <div key={tier} style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <TierBadge tier={tier} />
                      <span style={{ fontSize: 12, color: T.textMuted, fontFamily: T.fontBody }}>
                        {dls.length} Dienstleister {selectedDlsKva.filter(d => d.tier === tier).length > 0 && `— ${selectedDlsKva.filter(d => d.tier === tier).length} ausgewählt`}
                      </span>
                    </div>
                    <div style={{ border: `1px solid ${T.warmGray}`, borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
                      {dls.map(dl => {
                        const isChecked = selectedDlsKva.some(d => d.id === dl.id);
                        return (
                          <label key={dl.id} style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer',
                            background: isChecked ? 'var(--brand-gold-20, #f5f0e0)' : 'transparent',
                            borderBottom: `1px solid ${T.warmGray}20`,
                          }}>
                            <input type="checkbox" checked={isChecked}
                              onChange={() => {
                                if (isChecked) setSelectedDlsKva(selectedDlsKva.filter(d => d.id !== dl.id));
                                else setSelectedDlsKva([...selectedDlsKva, dl]);
                              }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: T.navy, fontFamily: T.fontBody }}>{dl.name}</div>
                              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                                {dl.plz ? `${dl.plz} ${dl.ort || ''}` : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {selectedDlsKva.length > 0 && (
                <div style={{ background: T.cream, padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13, fontFamily: T.fontBody }}>
                  <strong>{selectedDlsKva.length} Dienstleister ausgewählt:</strong> {selectedDlsKva.map(d => d.name).join(', ')}
                </div>
              )}
            </>
          )}

          {/* KVA: Fallback Message wenn kein Gewerk */}
          {!isAuftrag && !selectedGewerk && (
            <div style={{ padding: 16, textAlign: 'center', color: T.textMuted, fontSize: 13, border: `1px dashed ${T.warmGray}`, borderRadius: 6 }}>
              Bitte ein Gewerk auswählen, um Dienstleister nach Tier gruppiert anzuzeigen.
            </div>
          )}

          {/* Weiter-Button */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setStep(1)}
              style={{ ...styles.input, width: 'auto', padding: '12px 24px', cursor: 'pointer', background: '#fff', color: T.navy, fontWeight: 600, border: `1px solid ${T.warmGray}` }}>
              Zurück
            </button>
            <button disabled={!step2Valid} onClick={() => setStep(3)}
              style={{
                ...styles.input, width: 'auto', padding: '12px 32px', cursor: step2Valid ? 'pointer' : 'not-allowed',
                background: step2Valid ? T.navy : T.warmGray, color: '#fff', fontWeight: 700, fontSize: 14, border: 'none',
              }}>
              Weiter zur Zusammenfassung
            </button>
          </div>
        </div>
      )}

      {/* ═══ STEP 3: Zusammenfassung ═══ */}
      {step === 3 && (
        <div>
          <button onClick={() => setStep(2)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 12, marginBottom: 16, fontFamily: T.fontBody }}>
            ← Zurück zu Schritt 2
          </button>

          <div style={{ background: T.cream, border: `1px solid ${T.warmGray}`, borderRadius: 8, padding: 20, marginBottom: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.navy, fontFamily: T.fontHead, marginBottom: 16 }}>
              {isAuftrag ? 'Auftrag' : 'KVA-Anforderung'} — Zusammenfassung
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 16px', fontSize: 13, fontFamily: T.fontBody }}>
              <div style={{ fontWeight: 600, color: T.textMuted }}>Objekt:</div>
              <div>{selectedObjekt?.objekt_nr} {selectedObjekt?.adresse}</div>

              <div style={{ fontWeight: 600, color: T.textMuted }}>Ticket:</div>
              <div>{ticketMode === 'neu' ? `Neu: "${ticketTitel}"` : `#${ticketMode}`}</div>

              <div style={{ fontWeight: 600, color: T.textMuted }}>Vorlage:</div>
              <div>{vorlage}</div>

              <div style={{ fontWeight: 600, color: T.textMuted }}>Leistungsart:</div>
              <div>{leistungsart}</div>

              <div style={{ fontWeight: 600, color: T.textMuted }}>Dienstleister:</div>
              <div>
                {isAuftrag ? (
                  <>{selectedDl?.name} <TierBadge tier={selectedDl?.tier} /></>
                ) : (
                  <div>
                    {selectedDlsKva.map(d => (
                      <div key={d.id} style={{ marginBottom: 4 }}>
                        {d.name} <TierBadge tier={d.tier} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isAuftrag && (
                <>
                  <div style={{ fontWeight: 600, color: T.textMuted }}>Betrag:</div>
                  <div>{betrag} EUR ({betragTyp === 'net' ? 'netto' : 'brutto'})</div>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setStep(2)}
              style={{ ...styles.input, width: 'auto', padding: '12px 24px', cursor: 'pointer', background: '#fff', color: T.navy, fontWeight: 600, border: `1px solid ${T.warmGray}` }}>
              Zurück
            </button>
            <button onClick={handleSubmit}
              style={{
                ...styles.input, width: 'auto', padding: '12px 32px', cursor: 'pointer',
                background: T.gold, color: T.navy, fontWeight: 700, fontSize: 14, border: 'none',
              }}>
              {isAuftrag ? 'Auftrag erstellen' : 'KVA anfordern'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LocalAgentForm({ skill }) {
  const [agentUrl, setAgentUrl] = useState(() => localStorage.getItem('one_immo_local_agent_url') || 'http://127.0.0.1:18765');
  const [token, setToken] = useState(() => localStorage.getItem('one_immo_local_agent_token') || '');
  const [tool, setTool] = useState(() => localStorage.getItem('one_immo_local_agent_tool') || 'codex');
  const [cwd, setCwd] = useState(() => localStorage.getItem('one_immo_local_agent_cwd') || '');
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState('idle');
  const [health, setHealth] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const baseUrl = agentUrl.replace(/\/+$/, '');
  const RUN_TIMEOUT_SECONDS = 300;

  const persistSettings = () => {
    localStorage.setItem('one_immo_local_agent_url', agentUrl);
    localStorage.setItem('one_immo_local_agent_token', token);
    localStorage.setItem('one_immo_local_agent_tool', tool);
    localStorage.setItem('one_immo_local_agent_cwd', cwd);
  };

  // Abort the fetch after ms so a hung agent (or sleeping laptop) can't leave the UI stuck.
  const fetchWithTimeout = (url, opts, ms) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(t));
  };

  const checkHealth = async () => {
    if (!baseUrl) { setError('Bitte eine Agent-Adresse eingeben.'); return; }
    setStatus('checking');
    setError(null);
    try {
      const res = await fetchWithTimeout(`${baseUrl}/health`, {}, 10_000);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'Agent nicht erreichbar');
      setHealth(data);
      setStatus('idle');
      persistSettings();
    } catch (err) {
      setHealth(null);
      setStatus('error');
      const msg = err.name === 'AbortError' ? 'Zeitüberschreitung beim Verbindungsaufbau' : err.message;
      setError(`${msg}. Starte den lokalen Agent oder installiere den Login-Service.`);
    }
  };

  const runLocalAgent = async (e) => {
    e.preventDefault();
    if (!baseUrl) { setError('Bitte eine Agent-Adresse eingeben.'); return; }
    if (!prompt.trim()) {
      setError('Bitte eine Aufgabe eingeben.');
      return;
    }
    setStatus('running');
    setError(null);
    setResult(null);
    persistSettings();
    try {
      const res = await fetchWithTimeout(`${baseUrl}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-One-Immo-Agent-Token': token },
        body: JSON.stringify({
          tool,
          prompt: prompt.trim(),
          cwd: cwd.trim() || undefined,
          timeout_seconds: RUN_TIMEOUT_SECONDS,
        }),
      }, (RUN_TIMEOUT_SECONDS + 15) * 1000);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Lokale Ausführung fehlgeschlagen');
      setResult(data);
      setStatus(data.ok ? 'done' : 'error');
      if (!data.ok) setError(data.stderr || `Exit-Code ${data.exit_code}`);
    } catch (err) {
      setStatus('error');
      const msg = err.name === 'AbortError' ? 'Zeitüberschreitung der lokalen Ausführung' : err.message;
      setError(`${msg}. Prüfe, ob der lokale Agent läuft.`);
    }
  };

  const fieldStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border-light)',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'Inter, system-ui, sans-serif',
    boxSizing: 'border-box',
    background: '#fff',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
    marginBottom: 6,
  };
  const buttonStyle = {
    padding: '11px 18px',
    borderRadius: 9999,
    border: 'none',
    background: '#00a87e',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: status === 'running' ? 'wait' : 'pointer',
  };
  const mutedButtonStyle = {
    ...buttonStyle,
    background: '#fff',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-light)',
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '8px 0 32px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>{skill.name}</h1>
        <p style={{ margin: '8px 0 0', fontSize: 15, lineHeight: 1.5, color: 'var(--text-muted)' }}>
          Startet Codex oder Claude Code auf deinem Rechner. Der Server bekommt keinen LLM-API-Key.
        </p>
      </div>

      <div style={{
        marginBottom: 18,
        padding: 14,
        border: '1px solid var(--border-light)',
        borderRadius: 8,
        background: '#fff',
        fontSize: 13,
        color: 'var(--text-muted)',
        lineHeight: 1.5,
      }}>
        <strong style={{ color: 'var(--text-primary)' }}>Einrichtung:</strong>{' '}
        im geklonten Platform-Repo einmal ausführen:{' '}
        <code style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-primary)' }}>
          python3 scripts/one-immo-local-agent.py --install-launch-agent
        </code>{' '}
        Der Befehl gibt einen Token aus, den du unten einträgst.
      </div>

      <form onSubmit={runLocalAgent} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(160px, 0.6fr)', gap: 14 }}>
          <div>
            <label style={labelStyle}>Agent-Adresse</label>
            <input value={agentUrl} onChange={e => setAgentUrl(e.target.value)} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Werkzeug</label>
            <select value={tool} onChange={e => setTool(e.target.value)} style={fieldStyle}>
              <option value="codex">Codex</option>
              <option value="claude">Claude Code</option>
            </select>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Token</label>
          <input
            value={token}
            onChange={e => setToken(e.target.value)}
            type="password"
            placeholder="vom Einrichtungsbefehl"
            style={{ ...fieldStyle, fontFamily: 'JetBrains Mono, monospace' }}
          />
        </div>

        <div>
          <label style={labelStyle}>Arbeitsordner optional</label>
          <input
            value={cwd}
            onChange={e => setCwd(e.target.value)}
            placeholder="Unterordner deines Platform-Checkouts"
            style={{ ...fieldStyle, fontFamily: 'JetBrains Mono, monospace' }}
          />
        </div>

        <div>
          <label style={labelStyle}>Aufgabe</label>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Prüfe den PR-Kontext und erstelle einen knappen Review-Entwurf."
            rows={8}
            style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.45 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="submit" disabled={status === 'running'} style={buttonStyle}>
            {status === 'running' ? 'Läuft...' : 'Lokal ausführen'}
          </button>
          <button type="button" onClick={checkHealth} style={mutedButtonStyle}>
            Verbindung prüfen
          </button>
          {health?.ok && (
            <span style={{ fontSize: 13, color: '#047857', fontWeight: 600 }}>
              Verbunden
            </span>
          )}
        </div>
      </form>

      {error && (
        <div style={{
          marginTop: 18,
          padding: 14,
          borderRadius: 8,
          border: '1px solid #fecaca',
          background: '#fef2f2',
          color: '#991b1b',
          fontSize: 14,
          lineHeight: 1.45,
        }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{
          marginTop: 22,
          border: '1px solid var(--border-light)',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}>
          <div style={{
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            borderBottom: '1px solid var(--border-light)',
            fontSize: 13,
            color: 'var(--text-muted)',
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>Ergebnis</strong>
            <span>{result.tool} in {result.duration_seconds}s, Exit-Code {result.exit_code}</span>
          </div>
          <pre style={{
            margin: 0,
            padding: 16,
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--text-primary)',
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            {result.stdout || result.stderr || 'Keine Ausgabe.'}
          </pre>
        </div>
      )}
    </div>
  );
}


function SkillForm({ skill }) {
  const { token, user } = useAuth();
  const { jobs, startTracking } = useJobTracker();
  const isAdmin = user?.role === 'admin';
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [selectedObjekte, setSelectedObjekte] = useState([]);
  const [objekteMeta, setObjekteMeta] = useState([]);
  const [mahnHistory, setMahnHistory] = useState(null);
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [editData, setEditData] = useState(null); // editierbares Datenblatt
  const [reviewedData, setReviewedData] = useState(null); // gespeicherte geprüfte Daten
  const [executionId, setExecutionId] = useState(null);

  // Serienbrief: eigene Komponente
  if (skill.id === 'serienbrief') return <SerienbriefForm />;
  if (skill.id === 'local-agent') return <LocalAgentForm skill={skill} />;
  if (skill.ui === 'auto') return <AutoSkillCard skill={skill} />;
  if (skill.id === 'etv-tickets') return <EtvTicketsForm />;
  if (skill.id === 'auftragsvergabe' || skill.id === 'angebotseinholung') return <AuftragKvaWizard skill={skill} />;
  if (skill.id === 'staffelmiete') return <StaffelmietWizard />;
  const [maSelectedDocTypes, setMaSelectedDocTypes] = useState(['mietvertrag', 'datenblatt', 'sepa', 'datenschutz', 'zustellweg']);
  const [freigabeStatus, setFreigabeStatus] = useState(null); // null | angefragt | genehmigt | abgelehnt
  const [freigabeGrund, setFreigabeGrund] = useState('');
  // sbActiveTab removed — Vorlagen moved to Admin.js

  // Mieterdaten-Formular State (vor Dokumentenerstellung)
  const [tenantFormStatus, setTenantFormStatus] = useState(null);
  const [tenantFormFilledBy, setTenantFormFilledBy] = useState(null);
  const [tenantFormUrl, setTenantFormUrl] = useState('');
  const [tenantFormData, setTenantFormData] = useState(null);
  const [tenantFormLoading, setTenantFormLoading] = useState(false);
  const [tenantLinkCopied, setTenantLinkCopied] = useState(false);
  const [showTenantInlineForm, setShowTenantInlineForm] = useState(false);
  const [tenantInlineSubmitting, setTenantInlineSubmitting] = useState(false);
  // Tenant inline form fields
  const [tfEmailMieter1, setTfEmailMieter1] = useState('');
  const [tfTelefonMieter1, setTfTelefonMieter1] = useState('');
  const [tfNotfallName1, setTfNotfallName1] = useState('');
  const [tfNotfallTelefon1, setTfNotfallTelefon1] = useState('');
  const [tfIban1, setTfIban1] = useState('');
  const [tfBic1, setTfBic1] = useState('');
  const [tfKontoinhaber1, setTfKontoinhaber1] = useState('');
  const [tfEmailMieter2, setTfEmailMieter2] = useState('');
  const [tfTelefonMieter2, setTfTelefonMieter2] = useState('');
  const [tfNotfallName2, setTfNotfallName2] = useState('');
  const [tfNotfallTelefon2, setTfNotfallTelefon2] = useState('');
  const [tfIban2, setTfIban2] = useState('');
  const [tfBic2, setTfBic2] = useState('');
  const [tfKontoinhaber2, setTfKontoinhaber2] = useState('');
  const [tfKlingelschild, setTfKlingelschild] = useState('');
  const [tfAnmerkungen, setTfAnmerkungen] = useState('');
  const [tfErrors, setTfErrors] = useState({});

  // Verlauf laden (nur Mietvertrag) — einmal beim Mount + nach neuer Prüfung
  const [historyVersion, setHistoryVersion] = useState(0);
  useEffect(() => {
    if (skill.id === 'mietvertrag' || skill.id === 'begehung-tickets' || skill.id === 'sepa') {
      fetch(`${API_URL}/skills/executions/?skill_id=${skill.id}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then(setHistory)
        .catch(() => {});
    }
  }, [skill.id, token, historyVersion]);

  const loadHistoryItem = async (execId) => {
    try {
      const res = await fetch(`${API_URL}/skills/executions/${execId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.result) {
          // Wenn Phase nicht gesetzt, setze "analyzed" damit der "Dokumente erstellen" Button erscheint
          if (data.result.report && !data.result.phase) {
            data.result.phase = 'analyzed';
          }
          setResult(data.result);
          setExecutionId(execId);
          setFreigabeStatus(null);
          setReviewedData(null);
          setTenantFormStatus(null);
          setTenantFormData(null);
          setShowTenantInlineForm(false);
          setStatus('done');
          setError(null);
          // Tenant-Form-Status laden
          fetch(`${API_URL}/tenant-form/status/${execId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).then(d => {
            if (!d) return;
            setTenantFormStatus(d.status);
            setTenantFormFilledBy(d.filled_by || null);
            if (d.form_url) setTenantFormUrl(d.form_url);
            if (d.submitted_data) setTenantFormData(d.submitted_data);
          }).catch(() => {});
          // Bei ABSAGE: Freigabe-Status prüfen
          if (data.result.extracted_data?.empfehlung === 'ABSAGE') {
            fetch(`${API_URL}/freigabe/check/mietvertrag_absage/${execId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).then(d => setFreigabeStatus(d.status || null)).catch(() => {});
          }
        } else {
          setError('Dieser Vorgang hat keinen gespeicherten Prüfbericht (älterer Eintrag).');
        }
      }
    } catch (err) {
      setError('Fehler beim Laden des Vorgangs.');
    }
  };

  const deleteHistoryItem = async (execId) => {
    try {
      const res = await fetch(`${API_URL}/skills/executions/${execId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setHistory(prev => prev.filter(h => h.id !== execId));
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'Fehler beim Loeschen');
      }
    } catch (err) {
      setError('Fehler beim Loeschen: ' + err.message);
    }
  };

  // State zurücksetzen wenn Skill gewechselt wird ODER neu gemountet wird
  const mountCountRef = React.useRef(0);
  const [lastMountedSkill, setLastMountedSkill] = useState(null);
  useEffect(() => {
    mountCountRef.current += 1;
    // Beim ersten Mount oder bei Skill-Wechsel: immer sauber starten
    if (skill.id !== lastMountedSkill) {
      setLastMountedSkill(skill.id);
      setStatus('idle');
      setResult(null);
      setError(null);
      setEditData(null);
      setExecutionId(null);
      setActiveJobId(null);
    }
  }, [skill.id, lastMountedSkill]);

  // Mahnwesen: letzte Mahnläufe pro Objekt laden
  useEffect(() => {
    if (skill.id === 'mahnwesen') {
      fetch(`${API_URL}/skills/mahnwesen/history`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setMahnHistory(data); })
        .catch(() => {});
    } else {
      setMahnHistory(null);
    }
  }, [skill.id, token]);

  // Ref um zu tracken ob der aktuelle Job von dieser Session stammt
  const [activeJobId, setActiveJobId] = useState(null);

  // Globalen Job-Status synchronisieren (Multi-Job: direkt über activeJobId)
  const myJob = activeJobId && jobs ? jobs[activeJobId] : null;
  const job = myJob; // Alias für UI-Referenzen
  useEffect(() => {
    if (!myJob || myJob.skillType !== skill.id) return;

    if (myJob.status === 'running') {
      setStatus('running');
    }
    else if (myJob.status === 'done') {
      setStatus('done');
      if (skill.id === 'sepa' && myJob.result?.phase === 'done') {
        setResult(prev => ({ ...prev, ...myJob.result }));
      } else {
        setResult(myJob.result);
      }
      setHistoryVersion(v => v + 1);
      if (skill.id === 'mahnwesen') {
        fetch(`${API_URL}/skills/mahnwesen/history`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setMahnHistory(data); })
          .catch(() => {});
      }
      if (myJob.result?.extracted_data?.empfehlung === 'ABSAGE' && executionId) {
        fetch(`${API_URL}/freigabe/check/mietvertrag_absage/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.json()).then(d => setFreigabeStatus(d.status || null)).catch(() => {});
      }
    }
    else if (myJob.status === 'error') {
      setStatus('error'); setError(myJob.message);
    }
  }, [skill.id, myJob, token, executionId]);

  // UStVA Multi-Job: Status auf 'done' setzen wenn alle Jobs fertig
  useEffect(() => {
    if (skill.id !== 'ustva' || !jobs) return;
    const ustvarJobs = Object.values(jobs).filter(j => j.skillType === 'ustva');
    if (ustvarJobs.length === 0) return;
    const allDone = ustvarJobs.every(j => j.status === 'done' || j.status === 'error');
    if (allDone) {
      const successJobs = ustvarJobs.filter(j => j.status === 'done');
      const allFiles = successJobs.flatMap(j => j.result?.files || []);
      const summaries = successJobs.map(j => j.result?.summary || '').filter(Boolean);
      setStatus('done');
      setResult({
        files: allFiles,
        summary: summaries.length > 1
          ? `${successJobs.length} USt-Voranmeldungen erstellt.`
          : summaries[0] || 'Fertig',
      });
    }
  }, [skill.id, jobs]);

  // Cooldown-Timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e, overrideIds) => {
    if (e) e.preventDefault();
    if (cooldown > 0) return;

    const ids = overrideIds || selectedObjekte;
    if ((skill.id === 'op-liste' || skill.id === 'mahnwesen') && ids.length === 0) {
      setError('Bitte mindestens ein Objekt auswählen.');
      return;
    }

    setStatus('running');
    setError(null);
    setResult(null);
    setProgress(null);

    let submitData;
    if (skill.id === 'eigentuemer-report') {
      submitData = {
        objekt_nr: selectedObjekte[0] || '',
        objekt_id: selectedObjekte[0] || '',
        sanierung: formData.sanierung || '',
        erneuerungen: formData.erneuerungen || '',
      };
    } else if (skill.id === 'ustva') {
      // Multi-Objekt: pro Objekt einen parallelen Request absenden
      const objektIds = selectedObjekte.length > 0 ? selectedObjekte : [];
      if (objektIds.length === 0) {
        setError('Bitte mindestens ein Objekt auswählen.');
        setStatus('error');
        return;
      }
      const promises = objektIds.map(async (objektId) => {
        const singleData = {
          objekt_id: objektId,
          monat_von: formData.monat_von || '',
          monat_bis: formData.monat_bis || '',
          jahr: formData.jahr || '',
          versteuerungsart: formData.versteuerungsart || '',
        };
        const singleFd = new FormData();
        singleFd.append('data', JSON.stringify(singleData));
        const res = await fetch(`${API_URL}/skills/ustva/execute`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: singleFd,
        });
        const respData = await res.json();
        if (res.ok && respData.result?.job_id) {
          startTracking(respData.result.job_id, 'ustva');
        }
        return respData;
      });
      try {
        const results = await Promise.all(promises);
        const firstJobId = results.find(r => r.result?.job_id)?.result?.job_id;
        if (firstJobId) setActiveJobId(firstJobId);
      } catch (err) {
        setStatus('error');
        setError(err.message);
      }
      return;
    } else if (skill.id === 'op-liste') {
      submitData = { objekt_ids: ids.join(',') };
    } else if (skill.id === 'mahnwesen') {
      // Verwaltungsart pro Objekt mitschicken (für korrekte URLs + Mahnstufen)
      const typesStr = ids.map(id => {
        const obj = objekteMeta.find(o => o.immoware_id === id);
        return `${id}:${obj?.verwaltungsart || 'Mietverwaltung'}`;
      }).join(',');
      submitData = { objekt_ids: ids.join(','), objekt_types: typesStr };
    } else {
      submitData = formData;
    }

    const fd = new FormData();
    fd.append('data', JSON.stringify(submitData));
    Object.entries(files).forEach(([fieldName, fileList]) => {
      Array.from(fileList).forEach(f => {
        // Prefix field name to filename so backend can route files to correct fields
        const prefixedFile = new File([f], `${fieldName}__${f.name}`, { type: f.type });
        fd.append('files', prefixedFile);
      });
    });

    try {
      const res = await fetch(`${API_URL}/skills/${skill.id}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        if (data.execution_id) setExecutionId(data.execution_id);
        if (data.result?.job_id) {
          // Async Job — globalen Tracker starten (OP-Liste oder Mietvertrag)
          setActiveJobId(data.result.job_id);
          startTracking(data.result.job_id, skill.id);
        } else {
          setStatus('done');
          setResult(data.result);
        }
      } else {
        setStatus('error');
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler bei der Ausführung');
      }
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  const handleGenerateDocsClick = () => {
    if (!result?.extracted_data) return;
    setEditData({ ...result.extracted_data });
  };

  // EditDataSheet "Weiter" — speichert Daten, oeffnet Mieterdaten-Schritt
  const handleDataReviewed = () => {
    if (!editData) return;
    setResult(prev => ({ ...prev, extracted_data: { ...prev?.extracted_data, ...editData } }));
    setReviewedData(editData);
    setEditData(null);
    setTfKlingelschild(editData.mieter_name || '');
    // Tenant form status laden falls schon vorhanden
    if (executionId) {
      fetch(`${API_URL}/tenant-form/status/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (!data) return;
        setTenantFormStatus(data.status);
        setTenantFormFilledBy(data.filled_by || null);
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (data.submitted_data) setTenantFormData(data.submitted_data);
      }).catch(() => {});
    }
  };

  // Tenant data form handlers (in SkillForm, vor Dokumentenerstellung)
  const handleCreateTenantFormForDocs = async (mode) => {
    setTenantFormLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/tenant-form/create/${executionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus(data.status || 'pending');
        setTenantFormFilledBy(data.filled_by || mode.replace('_link', ''));
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (mode === 'sachbearbeiter') setShowTenantInlineForm(true);
      } else {
        setError(data.detail || 'Fehler');
      }
    } catch (err) { setError(err.message); }
    setTenantFormLoading(false);
  };

  const handleTenantInlineSubmit = async () => {
    const errs = {};
    if (!tfEmailMieter1.trim()) errs.e1 = 'Erforderlich';
    if (!tfTelefonMieter1.trim()) errs.t1 = 'Erforderlich';
    if (!tfNotfallName1.trim()) errs.n1 = 'Erforderlich';
    if (!tfNotfallTelefon1.trim()) errs.nt1 = 'Erforderlich';
    if (!tfKlingelschild.trim()) errs.ks = 'Erforderlich';
    const hasMieter2 = !!(result?.extracted_data?.mieter2_name || '').trim();
    if (hasMieter2) {
      if (!tfEmailMieter2.trim()) errs.e2 = 'Erforderlich';
      if (!tfTelefonMieter2.trim()) errs.t2 = 'Erforderlich';
    }
    setTfErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setTenantInlineSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/tenant-form/submit/${executionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_mieter1: tfEmailMieter1.trim(), telefon_mieter1: tfTelefonMieter1.trim(),
          notfallkontakt_name_mieter1: tfNotfallName1.trim(), notfallkontakt_telefon_mieter1: tfNotfallTelefon1.trim(),
          name_klingelschild: tfKlingelschild.trim(), iban_mieter1: tfIban1.trim() || null,
          bic_mieter1: tfBic1.trim() || null, kontoinhaber_mieter1: tfKontoinhaber1.trim() || null,
          anmerkungen: tfAnmerkungen.trim() || null,
          email_mieter2: tfEmailMieter2.trim() || null, telefon_mieter2: tfTelefonMieter2.trim() || null,
          notfallkontakt_name_mieter2: tfNotfallName2.trim() || null, notfallkontakt_telefon_mieter2: tfNotfallTelefon2.trim() || null,
          iban_mieter2: tfIban2.trim() || null, bic_mieter2: tfBic2.trim() || null, kontoinhaber_mieter2: tfKontoinhaber2.trim() || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus('submitted');
        setTenantFormData(data.submitted_data);
        setShowTenantInlineForm(false);
      } else { setError(data.detail || 'Fehler'); }
    } catch (err) { setError(err.message); }
    setTenantInlineSubmitting(false);
  };

  const handleRefreshTenantFormForDocs = async () => {
    try {
      const res = await fetch(`${API_URL}/tenant-form/status/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setTenantFormStatus(data.status);
        setTenantFormFilledBy(data.filled_by || null);
        if (data.form_url) setTenantFormUrl(data.form_url);
        if (data.submitted_data) setTenantFormData(data.submitted_data);
      }
    } catch (err) {
      console.warn('Tenant-Form Status-Abfrage fehlgeschlagen:', err.message);
    }
  };

  // Auto-Polling: Alle 10s prüfen ob Mieter das Formular ausgefüllt hat
  useEffect(() => {
    if (tenantFormStatus !== 'pending' || tenantFormFilledBy !== 'mieter' || !executionId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/tenant-form/status/${executionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'submitted') {
          setTenantFormStatus('submitted');
          setTenantFormFilledBy(data.filled_by || 'mieter');
          if (data.submitted_data) setTenantFormData(data.submitted_data);
        } else if (data.status === 'expired') {
          setTenantFormStatus('expired');
        }
      } catch (err) { /* Netzwerkfehler ignorieren, nächster Poll versucht es erneut */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [tenantFormStatus, tenantFormFilledBy, executionId, token]);

  const doGenerateDocs = async () => {
    const dataToSend = reviewedData || result?.extracted_data;
    if (!dataToSend) return;
    setStatus('running');
    setError(null);

    const docSteps = [
      { percent: 10, message: 'Kautionskonto wird bei Getmomo erstellt...' },
      { percent: 40, message: 'GVH-Export wird generiert...' },
      { percent: 70, message: 'Anlagen-Serienbrief wird erstellt...' },
      { percent: 90, message: 'Dokumente werden fertiggestellt...' },
    ];
    let stepIdx = 0;
    setProgress(docSteps[0]);
    const docTimer = setInterval(() => {
      stepIdx++;
      if (stepIdx < docSteps.length) setProgress(docSteps[stepIdx]);
    }, 2000);

    const fd = new FormData();
    fd.append('data', JSON.stringify({
      _phase: 'generate_docs',
      _execution_id: executionId,
      extracted_data: dataToSend,
      mietkonto_iban: dataToSend.mietkonto_iban || '',
      mietkonto_bic: dataToSend.mietkonto_bic || '',
      mietkonto_inhaber: dataToSend.mietkonto_inhaber || '',
    }));

    try {
      const res = await fetch(`${API_URL}/skills/mietvertrag/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      clearInterval(docTimer);
      const data = await res.json();
      if (res.ok) {
        setProgress({ percent: 100, message: 'Fertig!' });
        setTimeout(() => setProgress(null), 1000);
        setStatus('done');
        setResult(prev => ({ ...prev, ...data.result, phase: 'documents_created' }));
      } else {
        setProgress(null);
        setStatus('error');
        setError(data.detail?.errors?.join(', ') || data.detail || 'Fehler bei der Dokumentenerstellung');
      }
    } catch (err) {
      clearInterval(docTimer);
      setProgress(null);
      setStatus('error');
      setError(err.message);
    }
  };

  const isDisabled = status === 'running';

  return (
    <div style={{ maxWidth: 720 }}>
      <Link to="/skills" style={{
        color: 'var(--brand-gold)', fontSize: 13, textDecoration: 'none',
        fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
      }}>
        ← Zurück zu allen Skills
      </Link>
      <h2 style={{
        fontFamily: 'var(--font-headline)', fontSize: 22, fontWeight: 400,
        color: 'var(--brand-navy)', marginTop: 12, marginBottom: 4,
      }}>
        {skill.icon} {skill.name}
      </h2>
      <p style={{ color: 'var(--brand-text-muted)', fontSize: 14, marginBottom: 24 }}>{skill.description}</p>

      {/* OP-Liste: Objekt-Auswahl */}
      {skill.id === 'op-liste' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ObjektSelector
            token={token}
            selected={selectedObjekte}
            onSelect={setSelectedObjekte}
            disabled={isDisabled}
          />

          <button
            onClick={() => handleSubmit(null)}
            disabled={isDisabled || selectedObjekte.length === 0}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || selectedObjekte.length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || selectedObjekte.length === 0) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || selectedObjekte.length === 0) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px',
              fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running'
              ? 'Wird verarbeitet...'
              : `OP-Listen generieren (${selectedObjekte.length} Objekte)`
            }
          </button>
        </div>
      ) : skill.id === 'mahnwesen' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ObjektSelector
            token={token}
            selected={selectedObjekte}
            onSelect={setSelectedObjekte}
            onObjekteLoaded={setObjekteMeta}
            mahnHistory={mahnHistory}
            disabled={isDisabled}
          />

          <button
            onClick={() => handleSubmit(null)}
            disabled={isDisabled || selectedObjekte.length === 0}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || selectedObjekte.length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || selectedObjekte.length === 0) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || selectedObjekte.length === 0) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px',
              fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running'
              ? 'Wird verarbeitet...'
              : `Mahnlauf starten (${selectedObjekte.length} Objekte)`
            }
          </button>

          {/* Fortschrittsbalken (echtes Polling) */}
          {job && job.skillType === 'mahnwesen' && job.status === 'running' && (
            <div style={{
              padding: 16, background: '#fff',
              border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${job.progress}%`, height: '100%',
                  background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                }} />
              </div>

              {/* Zwischenergebnisse */}
              {job.partial_result && job.partial_result.eintraege && job.partial_result.eintraege.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  {/* Mini-Zusammenfassung */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    {[
                      { label: 'Gemahnt', value: job.partial_result.zusammenfassung?.gemahnt, bg: '#c6efce', color: '#006100' },
                      { label: 'Lastschrift', value: job.partial_result.zusammenfassung?.lastschrift, bg: '#d6e4f0', color: '#1f4e79' },
                      { label: 'Verrechnen', value: job.partial_result.zusammenfassung?.verrechnen, bg: '#fce4d6', color: '#974706' },
                      { label: 'Nicht gemahnt', value: job.partial_result.zusammenfassung?.nichtGemahnt, bg: '#ffc7ce', color: '#9c0006' },
                      { label: 'Kein Bedarf', value: job.partial_result.zusammenfassung?.keinBedarf, bg: '#ffeb9c', color: '#9c6500' },
                    ].filter(b => b.value > 0).map(box => (
                      <div key={box.label} style={{
                        padding: '4px 10px', borderRadius: 4, background: box.bg,
                        fontSize: 11, fontWeight: 600, color: box.color,
                      }}>
                        {box.value} {box.label}
                      </div>
                    ))}
                    <div style={{ padding: '4px 10px', borderRadius: 4, background: '#f0f0f0', fontSize: 11, fontWeight: 600, color: '#333' }}>
                      {job.partial_result.zusammenfassung?.gesamt || 0} Gesamt
                    </div>
                  </div>

                  {/* Teilergebnis-Tabelle */}
                  <div style={{
                    border: '1px solid var(--brand-warm-gray)', borderRadius: 6, overflow: 'hidden',
                    maxHeight: 300, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: 'var(--brand-cream)', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ padding: '6px 6px', textAlign: 'left', fontSize: 9, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Debitor</th>
                          <th style={{ padding: '6px 6px', textAlign: 'right', fontSize: 9, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Schuld</th>
                          <th style={{ padding: '6px 6px', textAlign: 'left', fontSize: 9, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {job.partial_result.eintraege.map((e, i) => {
                          const statusColors = {
                            GEMAHNT: '#c6efce', LASTSCHRIFT: '#d6e4f0', VERRECHNEN: '#fce4d6',
                            NICHT_GEMAHNT: '#ffc7ce', KEIN_BEDARF: '#ffeb9c', FEHLER: '#f4cccc',
                          };
                          const schuld = typeof e.schuld === 'number'
                            ? e.schuld.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC'
                            : (e.schuld ?? '\u2014');
                          return (
                            <tr key={i} style={{ background: statusColors[e.status] || '#fff', borderTop: '1px solid var(--brand-warm-gray)' }}>
                              <td style={{ padding: '4px 6px', fontWeight: 500 }}>{e.name}</td>
                              <td style={{ padding: '4px 6px', textAlign: 'right' }}>{schuld}</td>
                              <td style={{ padding: '4px 6px', fontWeight: 600, fontSize: 10 }}>{e.status}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : skill.id === 'begehung-tickets' ? (
        /* Begehungs-Tickets: Drag & Drop Protokoll-Upload */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Verlauf */}
          {history.length > 0 && !result && (
            <div style={{
              background: '#fff', borderRadius: 6, border: '1px solid var(--brand-warm-gray)',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px', background: 'var(--brand-cream)',
                borderBottom: '1px solid var(--brand-warm-gray)',
              }}>Letzte Begehungen</div>
              {history.slice(0, 5).map(h => (
                <div key={h.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 16px', borderBottom: '1px solid var(--brand-warm-gray)',
                  fontSize: 12,
                }}>
                  <div
                    onClick={() => loadHistoryItem(h.id)}
                    style={{ flex: 1, cursor: 'pointer', color: 'var(--brand-navy)' }}
                  >
                    <span style={{ fontWeight: 500 }}>{h.summary || 'Begehung'}</span>
                    <span style={{ color: 'var(--brand-text-light)', marginLeft: 8, fontSize: 11 }}>
                      {new Date(h.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => loadHistoryItem(h.id)} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-gold)', fontFamily: 'var(--font-body)', fontWeight: 600,
                    }}>Oeffnen</button>
                    <button onClick={() => { if (window.confirm('Vorgang loeschen?')) deleteHistoryItem(h.id); }} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-light)', fontFamily: 'var(--font-body)',
                    }}
                      onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                      onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                    >x</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <BegehungUpload files={files} onFilesChange={setFiles} disabled={isDisabled} />
          <button onClick={handleSubmit}
            disabled={isDisabled || !files['protokoll'] || Array.from(files['protokoll'] || []).length === 0}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || !files['protokoll'] || Array.from(files['protokoll'] || []).length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || !files['protokoll'] || Array.from(files['protokoll'] || []).length === 0) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || !files['protokoll'] || Array.from(files['protokoll'] || []).length === 0) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running' ? 'Wird verarbeitet...' : `Tickets erstellen (${Array.from(files['protokoll'] || []).length} Dateien)`}
          </button>

          {/* Fortschrittsbalken (echtes Polling) */}
          {job && job.skillType === 'begehung-tickets' && job.status === 'running' && (
            <div style={{
              padding: 16, background: '#fff',
              border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${job.progress}%`, height: '100%',
                  background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      ) : skill.id === 'mietvertrag' ? (
        /* Mietvertragsprüfung: Drag & Drop + Verlauf */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Verlauf */}
          {history.length > 0 && !result && (
            <div style={{
              background: '#fff', borderRadius: 6, border: '1px solid var(--brand-warm-gray)',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px', background: 'var(--brand-cream)',
                borderBottom: '1px solid var(--brand-warm-gray)',
              }}>Letzte Prüfungen</div>
              {history.slice(0, 5).map(h => (
                <div key={h.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 16px', borderBottom: '1px solid var(--brand-warm-gray)',
                  fontSize: 12,
                }}>
                  <div
                    onClick={() => loadHistoryItem(h.id)}
                    style={{ flex: 1, cursor: 'pointer', color: 'var(--brand-navy)' }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {h.summary || 'Prüfung'}
                    </span>
                    <span style={{ color: 'var(--brand-text-light)', marginLeft: 8, fontSize: 11 }}>
                      {new Date(h.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => loadHistoryItem(h.id)} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-gold)', fontFamily: 'var(--font-body)', fontWeight: 600,
                    }}>Öffnen</button>
                    <button onClick={() => { if (window.confirm('Prüfung löschen?')) deleteHistoryItem(h.id); }} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-light)', fontFamily: 'var(--font-body)',
                    }}
                      onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                      onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                    >×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <MietvertragUpload files={files} onFilesChange={setFiles} disabled={isDisabled} />
          <button onClick={handleSubmit}
            disabled={isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running' ? 'Wird verarbeitet...' : `Bewerbermappe prüfen (${Array.from(files['files'] || []).length} Dateien)`}
          </button>

          {/* Fortschrittsbalken Mietvertrag (aus globalem Job) */}
          {job && job.skillType === 'mietvertrag' && job.status === 'running' && (
            <div style={{
              marginTop: 16, padding: 16, background: '#fff',
              border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${job.progress}%`, height: '100%',
                  background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      ) : skill.id === 'sepa' ? (
        /* SEPA / Kontaktdaten / Datenschutz: Vollständiger Workflow */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Verlauf */}
          {history.length > 0 && !result && status !== 'running' && (
            <div style={{
              background: '#fff', borderRadius: 6, border: '1px solid var(--brand-warm-gray)',
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 16px', fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px', background: 'var(--brand-cream)',
                borderBottom: '1px solid var(--brand-warm-gray)',
              }}>Letzte Einpflegungen</div>
              {history.slice(0, 5).map(h => (
                <div key={h.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 16px', borderBottom: '1px solid var(--brand-warm-gray)',
                  fontSize: 12,
                }}>
                  <div onClick={() => loadHistoryItem(h.id)}
                    style={{ flex: 1, cursor: 'pointer', color: 'var(--brand-navy)' }}>
                    <span style={{ fontWeight: 500 }}>{h.summary || 'Einpflege'}</span>
                    <span style={{ color: 'var(--brand-text-light)', marginLeft: 8, fontSize: 11 }}>
                      {new Date(h.created_at).toLocaleDateString('de-DE')}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => loadHistoryItem(h.id)} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-gold)', fontFamily: 'var(--font-body)', fontWeight: 600,
                    }}>Öffnen</button>
                    <button onClick={() => { if (window.confirm('Eintrag löschen?')) deleteHistoryItem(h.id); }} style={{
                      padding: '3px 10px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-light)', fontFamily: 'var(--font-body)',
                    }}
                      onMouseEnter={e => e.target.style.color = 'var(--brand-danger)'}
                      onMouseLeave={e => e.target.style.color = 'var(--brand-text-light)'}
                    >×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload-Bereich (nur wenn kein Ergebnis und nicht running) */}
          {!result && status !== 'running' && (<>
            <SEPAUpload files={files} onFilesChange={setFiles} disabled={isDisabled} />
            <button onClick={handleSubmit}
              disabled={isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0}
              style={{
                padding: '14px 24px', fontSize: 13, fontWeight: 600,
                background: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
                color: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? '#fff' : 'var(--brand-navy)',
                border: 'none', borderRadius: 4,
                cursor: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
              }}
            >
              {`Dokumente analysieren (${Array.from(files['files'] || []).length} Dateien)`}
            </button>
          </>)}

          {/* Fortschrittsbalken Phase 1 (Analyse) — NUR wenn Phase 1 aktiv ist */}
          {job && job.skillType === 'sepa' && job.status === 'running' && status === 'running' && (!result || result.phase === 'parsing') && (
            <div style={{
              padding: 16, background: '#fff',
              border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${job.progress}%`, height: '100%',
                  background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                }} />
              </div>
              {/* Schritte-Anzeige */}
              <div style={{ display: 'flex', gap: 0, marginTop: 12 }}>
                {[
                  { key: 'reading', label: 'Dateien lesen', pct: 10 },
                  { key: 'analyzing', label: 'Analyse', pct: 40 },
                  { key: 'parsing', label: 'Daten extrahieren', pct: 75 },
                  { key: 'review', label: 'Prüfung', pct: 100 },
                ].map((step, i) => {
                  const active = job.progress >= step.pct;
                  const current = job.phase === step.key;
                  return (
                    <div key={step.key} style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', margin: '0 auto 4px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                        background: active ? 'var(--brand-gold)' : 'var(--brand-cream)',
                        color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                        border: current ? '2px solid var(--brand-navy)' : '2px solid transparent',
                        transition: 'all 0.3s',
                      }}>{i + 1}</div>
                      <div style={{
                        fontSize: 10, fontWeight: current ? 600 : 400,
                        color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                      }}>{step.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : skill.id === 'mieteranlage' ? (
        /* Mieteranlage: Drag & Drop wie Mietvertragserstellung */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <MieteranlageUpload files={files} onFilesChange={setFiles} disabled={isDisabled}
            selectedDocTypes={maSelectedDocTypes} onDocTypesChange={setMaSelectedDocTypes} />
          <button onClick={handleSubmit}
            disabled={isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || !files['files'] || Array.from(files['files'] || []).length === 0) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running' ? 'Wird verarbeitet...' : `Mieteranlage starten (${Array.from(files['files'] || []).length} Dateien)`}
          </button>

          {/* Fortschrittsbalken Mieteranlage (aus globalem Job) */}
          {job && job.skillType === 'mieteranlage' && job.status === 'running' && (
            <div style={{
              marginTop: 16, padding: 16, background: '#fff',
              border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${job.progress}%`, height: '100%',
                  background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                }} />
              </div>
            </div>
          )}
        </div>

      ) : skill.id === 'eigentuemer-report' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Objekt-Auswahl (Einzelauswahl) */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 8 }}>
              Objekt auswählen <span style={{ color: 'var(--brand-danger)' }}>*</span>
            </label>
            <ObjektSelector
              token={token}
              selected={selectedObjekte}
              onSelect={ids => setSelectedObjekte(ids.length > 0 ? [ids[ids.length - 1]] : [])}
              disabled={isDisabled}
              filterMieteOnly={true}
            />
          </div>

          {/* PDF Drag & Drop */}
          <EigentuemerReportUpload files={files} onFilesChange={setFiles} disabled={isDisabled} />

          {/* Vorjahres-PDF (optional) */}
          <ERPdfDropzone id="er-vorjahr-input" fieldKey="vorjahr_pdfs"
            label="Vorjahres-Abrechnung (optional, zum Vergleich)"
            description="Abrechnung des Vorjahres hochladen — erzeugt automatisch eine Vergleichstabelle"
            files={files} onFilesChange={setFiles} disabled={isDisabled} />

          {/* Sanierungsmaßnahmen */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
              Geplante Sanierungsmaßnahmen (optional)
            </label>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
              Eine Maßnahme pro Zeile. Format: Beschreibung | Kosten | Zeitraum
            </div>
            <textarea
              value={formData.sanierung || ''}
              onChange={e => setFormData(prev => ({ ...prev, sanierung: e.target.value }))}
              rows={3}
              placeholder="z.B. Dachsanierung | 45.000 EUR | Q3 2026"
              disabled={isDisabled}
              style={{
                width: '100%', padding: 10, border: '1px solid var(--brand-warm-gray)',
                borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)',
                resize: 'vertical', minHeight: 60,
              }}
            />
          </div>

          {/* Erneuerungen */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
              Geplante Erneuerungen nächstes Jahr (optional)
            </label>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>
              Eine Maßnahme pro Zeile. Format: Beschreibung | Kosten | Zeitraum
            </div>
            <textarea
              value={formData.erneuerungen || ''}
              onChange={e => setFormData(prev => ({ ...prev, erneuerungen: e.target.value }))}
              rows={3}
              placeholder="z.B. Heizungsanlage erneuern | 25.000 EUR | Q1 2027"
              disabled={isDisabled}
              style={{
                width: '100%', padding: 10, border: '1px solid var(--brand-warm-gray)',
                borderRadius: 6, fontSize: 13, fontFamily: 'var(--font-body)',
                resize: 'vertical', minHeight: 60,
              }}
            />
          </div>

          {/* Submit */}
          {(() => {
            const pdfCount = Array.from(files['pdfs'] || []).length;
            const canSubmit = !isDisabled && selectedObjekte.length > 0 && pdfCount > 0;
            return (
              <button onClick={() => handleSubmit(null)} disabled={!canSubmit} style={{
                padding: '14px 24px', fontSize: 13, fontWeight: 600,
                background: canSubmit ? 'var(--brand-gold)' : 'var(--brand-text-muted)',
                color: canSubmit ? 'var(--brand-navy)' : '#fff',
                border: 'none', borderRadius: 4,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
              }}>
                {status === 'running' ? 'Wird verarbeitet...' : `Report erstellen (${pdfCount} PDF${pdfCount !== 1 ? 's' : ''})`}
              </button>
            );
          })()}

          {/* Fortschrittsbalken — zeigt bei status=running, unabhängig vom globalen Job */}
          {status === 'running' && (
            <div style={{ padding: 16, background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
                  {(job && job.skillType === 'eigentuemer-report' && job.message) || 'PDFs werden analysiert — kann 1-2 Minuten dauern...'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>
                  {(job && job.skillType === 'eigentuemer-report') ? `${job.progress}%` : '...'}
                </div>
              </div>
              <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${(job && job.skillType === 'eigentuemer-report') ? job.progress : 20}%`,
                  height: '100%', background: 'var(--brand-gold)', borderRadius: 3,
                  transition: 'width 0.8s ease',
                  animation: !(job && job.skillType === 'eigentuemer-report') ? 'none' : undefined,
                }} />
              </div>
            </div>
          )}
        </div>

      ) : skill.id === 'schwarzes-brett' ? (
        /* Schwarzes Brett: nur E-Mail analysieren (Vorlagen jetzt unter Verwaltung) */
        <div>
          <SchwarzesBrettInput
            token={token}
            selectedObjekte={selectedObjekte}
            setSelectedObjekte={setSelectedObjekte}
            onObjekteLoaded={setObjekteMeta}
            status={status}
            setStatus={setStatus}
            setResult={setResult}
            setError={setError}
            startTracking={startTracking}
            setActiveJobId={setActiveJobId}
            setExecutionId={setExecutionId}
            isDisabled={status === 'running'}
          />
        </div>

      ) : skill.id === 'ustva' ? (
        /* Umsatzsteuervoranmeldung: Objekt + Zeitraum + Versteuerungsart */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Objekte auswählen
            </label>
            <ObjektSelector
              token={token}
              selected={selectedObjekte}
              onSelect={ids => setSelectedObjekte(ids)}
              disabled={isDisabled}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
                Monat von <span style={{ color: 'var(--brand-danger)' }}>*</span>
              </label>
              <select
                value={formData.monat_von || ''}
                onChange={e => setFormData(prev => ({ ...prev, monat_von: e.target.value }))}
                disabled={isDisabled}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)',
                  borderRadius: 6, background: '#fff', fontFamily: 'var(--font-body)',
                }}
              >
                <option value="">— Monat —</option>
                <option value="1">Januar</option>
                <option value="2">Februar</option>
                <option value="3">März</option>
                <option value="4">April</option>
                <option value="5">Mai</option>
                <option value="6">Juni</option>
                <option value="7">Juli</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">Oktober</option>
                <option value="11">November</option>
                <option value="12">Dezember</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
                Monat bis <span style={{ color: 'var(--brand-danger)' }}>*</span>
              </label>
              <select
                value={formData.monat_bis || ''}
                onChange={e => setFormData(prev => ({ ...prev, monat_bis: e.target.value }))}
                disabled={isDisabled}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)',
                  borderRadius: 6, background: '#fff', fontFamily: 'var(--font-body)',
                }}
              >
                <option value="">— Monat —</option>
                <option value="1">Januar</option>
                <option value="2">Februar</option>
                <option value="3">März</option>
                <option value="4">April</option>
                <option value="5">Mai</option>
                <option value="6">Juni</option>
                <option value="7">Juli</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">Oktober</option>
                <option value="11">November</option>
                <option value="12">Dezember</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
                Jahr <span style={{ color: 'var(--brand-danger)' }}>*</span>
              </label>
              <select
                value={formData.jahr || ''}
                onChange={e => setFormData(prev => ({ ...prev, jahr: e.target.value }))}
                disabled={isDisabled}
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)',
                  borderRadius: 6, background: '#fff', fontFamily: 'var(--font-body)',
                }}
              >
                <option value="">— Jahr —</option>
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
              Versteuerungsart <span style={{ color: 'var(--brand-danger)' }}>*</span>
            </label>
            <select
              value={formData.versteuerungsart || ''}
              onChange={e => setFormData(prev => ({ ...prev, versteuerungsart: e.target.value }))}
              disabled={isDisabled}
              style={{
                width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid var(--brand-warm-gray)',
                borderRadius: 6, background: '#fff', fontFamily: 'var(--font-body)',
              }}
            >
              <option value="">— Versteuerungsart wählen —</option>
              <option value="Ist-Versteuerung">Ist-Versteuerung</option>
              <option value="Soll-Versteuerung">Soll-Versteuerung</option>
            </select>
          </div>

          {/* Zusammenfassung */}
          {selectedObjekte.length > 0 && formData.monat_von && formData.monat_bis && formData.jahr && formData.versteuerungsart && (
            <div style={{
              padding: 12, background: 'var(--brand-gold-10)', border: '1px solid var(--brand-gold-20)',
              borderRadius: 6, fontSize: 12, color: 'var(--brand-navy)',
            }}>
              USt-VA für <strong>{selectedObjekte.length} {selectedObjekte.length === 1 ? 'Objekt' : 'Objekte'}</strong> | Zeitraum: <strong>
                {['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][parseInt(formData.monat_von)]}
                {formData.monat_von !== formData.monat_bis && ` – ${['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'][parseInt(formData.monat_bis)]}`}
                {' '}{formData.jahr}
              </strong> | {formData.versteuerungsart}
            </div>
          )}

          <button
            onClick={() => handleSubmit(null)}
            disabled={isDisabled || selectedObjekte.length === 0 || !formData.monat_von || !formData.monat_bis || !formData.jahr || !formData.versteuerungsart}
            style={{
              padding: '14px 24px', fontSize: 13, fontWeight: 600,
              background: (isDisabled || selectedObjekte.length === 0 || !formData.monat_von || !formData.monat_bis || !formData.jahr || !formData.versteuerungsart) ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: (isDisabled || selectedObjekte.length === 0 || !formData.monat_von || !formData.monat_bis || !formData.jahr || !formData.versteuerungsart) ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4,
              cursor: (isDisabled || selectedObjekte.length === 0 || !formData.monat_von || !formData.monat_bis || !formData.jahr || !formData.versteuerungsart) ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running' ? 'Wird erstellt...' : selectedObjekte.length > 1 ? `USt-Voranmeldung für ${selectedObjekte.length} Objekte erstellen` : 'USt-Voranmeldung erstellen'}
          </button>

          {/* Progress-Anzeige: alle laufenden UStVA-Jobs */}
          {status === 'running' && jobs && (() => {
            const ustvarJobs = Object.values(jobs).filter(j => j.skillType === 'ustva');
            if (ustvarJobs.length === 0) return null;
            const doneCount = ustvarJobs.filter(j => j.status === 'done').length;
            const errorCount = ustvarJobs.filter(j => j.status === 'error').length;
            const totalCount = ustvarJobs.length;
            const avgProgress = Math.round(ustvarJobs.reduce((sum, j) => sum + (j.progress || 0), 0) / totalCount);
            return (
              <div style={{
                padding: 16, background: '#fff', border: '1px solid var(--brand-warm-gray)',
                borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>
                    {doneCount + errorCount < totalCount
                      ? `${doneCount} von ${totalCount} Objekten fertig...`
                      : errorCount > 0
                        ? `${doneCount} von ${totalCount} erfolgreich (${errorCount} fehlgeschlagen)`
                        : `Alle ${totalCount} Objekte fertig!`}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>
                    {avgProgress}%
                  </div>
                </div>
                <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    width: `${avgProgress}%`,
                    height: '100%', background: 'var(--brand-gold)', borderRadius: 3,
                    transition: 'width 0.8s ease',
                  }} />
                </div>
                {ustvarJobs.map(j => (
                  <div key={j.jobId} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                    color: j.status === 'done' ? 'var(--brand-success)' : j.status === 'error' ? 'var(--brand-danger)' : 'var(--brand-text-muted)',
                  }}>
                    <span>{j.status === 'done' ? '✓' : j.status === 'error' ? '✗' : '⏳'}</span>
                    <span style={{ flex: 1 }}>{j.message || 'Wird verarbeitet...'}</span>
                    <span style={{ fontWeight: 600 }}>{j.progress}%</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

      ) : (
        /* Andere Skills: normales Formular */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {skill.fields?.map(field => (
            <div key={field.name}>
              <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', display: 'block', marginBottom: 6 }}>
                {field.label} {field.required && <span style={{ color: 'var(--brand-danger)' }}>*</span>}
              </label>
              {field.description && (
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 6 }}>{field.description}</div>
              )}
              {field.type === 'file' ? (
                <input type="file" accept={field.accept} multiple
                  onChange={e => setFiles(prev => ({ ...prev, [field.name]: e.target.files }))}
                  style={{
                    width: '100%', padding: 10, border: '2px dashed var(--brand-warm-gray)',
                    borderRadius: 6, fontSize: 14, background: 'var(--brand-cream)', fontFamily: 'var(--font-body)',
                  }}
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  value={formData[field.name] || ''}
                  onChange={e => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                  rows={4}
                  placeholder={field.description || ''}
                  style={{
                    width: '100%', padding: 10, border: '1px solid var(--brand-warm-gray)',
                    borderRadius: 6, fontSize: 14, fontFamily: 'var(--font-body)',
                    resize: 'vertical', minHeight: 80,
                  }}
                />
              ) : (
                <input
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={formData[field.name] || ''}
                  onChange={e => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                  style={{
                    width: '100%', padding: 10, border: '1px solid var(--brand-warm-gray)',
                    borderRadius: 6, fontSize: 14, fontFamily: 'var(--font-body)',
                  }}
                />
              )}
            </div>
          ))}
          <button onClick={handleSubmit} disabled={isDisabled}
            style={{
              padding: '12px 24px', fontSize: 13, fontWeight: 600,
              background: isDisabled ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
              color: isDisabled ? '#fff' : 'var(--brand-navy)',
              border: 'none', borderRadius: 4, cursor: isDisabled ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
            }}
          >
            {status === 'running' ? 'Wird verarbeitet...' : `${skill.name} starten`}
          </button>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 20, padding: 16, background: 'var(--brand-danger-bg)',
          border: '1px solid var(--brand-danger)', borderRadius: 6,
          color: 'var(--brand-danger)', fontSize: 14,
        }}>{error}</div>
      )}

      {result && (<>
        {/* Neue Analyse starten Button */}
        {(skill.id === 'mietvertrag' || skill.id === 'begehung-tickets' || skill.id === 'sepa' || skill.id === 'mieteranlage' || skill.id === 'schwarzes-brett') && (
          <div style={{ marginTop: 20, marginBottom: -12 }}>
            <button onClick={() => { setResult(null); setStatus('idle'); setError(null); setFiles({}); setEditData(null); setMaSelectedDocTypes(['mietvertrag', 'datenblatt', 'sepa', 'datenschutz', 'zustellweg']); }}
              style={{
                padding: '8px 16px', fontSize: 12, fontWeight: 500,
                background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
              onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-gold)'; e.target.style.color = 'var(--brand-gold)'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
            >{skill.id === 'sepa' ? '← Neues Dokument analysieren' : skill.id === 'begehung-tickets' ? '← Neue Begehung analysieren' : skill.id === 'mieteranlage' ? '← Neue Mieteranlage starten' : skill.id === 'schwarzes-brett' ? '← Neue E-Mail analysieren' : '← Neue Prüfung starten'}</button>
          </div>
        )}

        {/* SEPA Review: Extrahierte Daten zur Bestätigung */}
        {skill.id === 'sepa' && result.phase === 'review' && (
          <SEPAReviewPanel
            result={result}
            editData={editData}
            setEditData={setEditData}
            onConfirm={async (confirmedData, mode, gruppe) => {
              setEditData(null);
              setResult(prev => ({ ...prev, phase: 'einpflege' }));
              setStatus('running');
              // Phase 2: confirm (Bestandskontakt) ODER create-contact (Neuanlage)
              try {
                const isCreate = mode === 'create';
                const payload = isCreate
                  ? {
                      _phase: 'create-contact',
                      new_contact_data: { ...confirmedData, kontakt_gruppe: gruppe || 'Mieter' },
                      _execution_id: executionId,
                    }
                  : {
                      _phase: 'confirm',
                      confirmed_data: confirmedData,
                      current_data: result.contact_lookup?.current_data || {},
                      _execution_id: executionId,
                    };
                const fd = new FormData();
                fd.append('data', JSON.stringify(payload));
                const res = await fetch(`${API_URL}/skills/sepa/execute`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: fd,
                });
                const data = await res.json();
                if (data.result?.job_id) {
                  setActiveJobId(data.result.job_id);
                  startTracking(data.result.job_id, 'sepa');
                }
              } catch (err) {
                setError('Fehler beim Starten der Einpflege: ' + err.message);
                setStatus('error');
              }
            }}
          />
        )}

        {/* SEPA Einpflege läuft */}
        {skill.id === 'sepa' && result.phase === 'einpflege' && job && job.skillType === 'sepa' && job.status === 'running' && (
          <div style={{
            marginTop: 20, padding: 16, background: '#fff',
            border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>Daten werden eingepflegt...</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
            </div>
            <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{
                width: `${job.progress}%`, height: '100%',
                background: 'var(--brand-gold)', borderRadius: 3,
                transition: 'width 0.8s ease',
              }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
            {/* Schritte */}
            <div style={{ display: 'flex', gap: 0, marginTop: 12 }}>
              {[
                { key: 'login', label: 'Login', pct: 10 },
                { key: 'search', label: 'Kontakt suchen', pct: 20 },
                { key: 'edit', label: 'Daten eintragen', pct: 55 },
                { key: 'save', label: 'Speichern', pct: 80 },
                { key: 'done', label: 'Fertig', pct: 100 },
              ].map((step, i) => {
                const active = job.progress >= step.pct;
                const current = job.phase === step.key;
                return (
                  <div key={step.key} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', margin: '0 auto 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      background: active ? 'var(--brand-gold)' : 'var(--brand-cream)',
                      color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                      border: current ? '2px solid var(--brand-navy)' : '2px solid transparent',
                      transition: 'all 0.3s',
                    }}>{i + 1}</div>
                    <div style={{
                      fontSize: 9, fontWeight: current ? 600 : 400,
                      color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                    }}>{step.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SEPA Einpflege abgeschlossen */}
        {skill.id === 'sepa' && result.phase === 'done' && (
          <div style={{
            marginTop: 20, padding: 20, background: 'var(--brand-success-bg)',
            border: '1px solid var(--brand-success)', borderRadius: 6,
          }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brand-success)', marginBottom: 8 }}>
              Erfolgreich eingepflegt
            </div>
            <div style={{ fontSize: 13, color: 'var(--brand-text)', marginBottom: 8 }}>
              {result.summary || result.contact_name}
            </div>
            {result.aenderungen && result.aenderungen.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Durchgeführte Änderungen
                </div>
                {result.aenderungen.map((a, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--brand-text)', padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--brand-success)', fontSize: 14 }}>✓</span> {a}
                  </div>
                ))}
              </div>
            )}
            {result.sepa_hinweis && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 6,
                background: 'var(--brand-warning-bg)', border: '1px solid var(--brand-warning)',
                fontSize: 12, color: 'var(--brand-warning)',
              }}>
                {result.sepa_hinweis}
              </div>
            )}
          </div>
        )}

        {/* ═══ Mieteranlage: Review → Bestätigung → Einpflege ═══ */}
        {skill.id === 'mieteranlage' && result.phase === 'review' && result.extracted_data && (
          <MieteranlageReview
            result={result}
            executionId={executionId}
            token={token}
            selectedDocTypes={maSelectedDocTypes}
            onConfirm={async (confirmedData) => {
              setResult(prev => ({ ...prev, phase: 'einpflege' }));
              setStatus('running');
              try {
                const fd = new FormData();
                fd.append('data', JSON.stringify({
                  _phase: 'confirm',
                  confirmed_data: confirmedData,
                  _execution_id: executionId,
                }));
                const res = await fetch(`${API_URL}/skills/mieteranlage/execute`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: fd,
                });
                const data = await res.json();
                if (data.result?.job_id) {
                  setActiveJobId(data.result.job_id);
                  startTracking(data.result.job_id, 'mieteranlage');
                }
              } catch (err) {
                setError('Fehler beim Starten der Einpflege: ' + err.message);
                setStatus('error');
              }
            }}
          />
        )}

        {/* Mieteranlage: Einpflege läuft */}
        {skill.id === 'mieteranlage' && result.phase === 'einpflege' && job && job.skillType === 'mieteranlage' && job.status === 'running' && (
          <div style={{
            marginTop: 20, padding: 16, background: '#fff',
            border: '1px solid var(--brand-warm-gray)', borderRadius: 8,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>Mieter wird in Immoware24 angelegt...</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{job.progress}%</div>
            </div>
            <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{
                width: `${job.progress}%`, height: '100%',
                background: 'var(--brand-gold)', borderRadius: 3,
                transition: 'width 0.8s ease',
              }} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{job.message}</div>
            <div style={{ display: 'flex', gap: 0, marginTop: 12 }}>
              {[
                { key: 'login', label: 'Login', pct: 5 },
                { key: 'kontakt', label: 'Kontakt', pct: 20 },
                { key: 'mietverhaeltnis', label: 'Mietverh.', pct: 45 },
                { key: 'sepa-mandat', label: 'SEPA', pct: 75 },
                { key: 'done', label: 'Fertig', pct: 100 },
              ].map((step, i) => {
                const active = job.progress >= step.pct;
                const current = job.phase === step.key;
                return (
                  <div key={step.key} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', margin: '0 auto 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      background: active ? 'var(--brand-gold)' : 'var(--brand-cream)',
                      color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                      border: current ? '2px solid var(--brand-navy)' : '2px solid transparent',
                      transition: 'all 0.3s',
                    }}>{i + 1}</div>
                    <div style={{
                      fontSize: 9, fontWeight: current ? 600 : 400,
                      color: active ? 'var(--brand-navy)' : 'var(--brand-text-light)',
                    }}>{step.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Mieteranlage: Abgeschlossen */}
        {skill.id === 'mieteranlage' && result.phase === 'done' && (
          <div style={{
            marginTop: 20, padding: 20, background: 'var(--brand-success-bg)',
            border: '1px solid var(--brand-success)', borderRadius: 6,
          }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brand-success)', marginBottom: 8 }}>
              Mieteranlage abgeschlossen
            </div>
            <div style={{ fontSize: 13, color: 'var(--brand-text)', marginBottom: 8 }}>
              {result.summary}
            </div>
            {result.aenderungen && result.aenderungen.length > 0 && (
              <ul style={{ fontSize: 12, color: 'var(--brand-text)', margin: '8px 0 0 16px', padding: 0 }}>
                {result.aenderungen.map((a, i) => <li key={i} style={{ marginBottom: 2 }}>{a}</li>)}
              </ul>
            )}
            {result.dokumente_abgelegt && result.dokumente_abgelegt.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--brand-text-muted)' }}>
                {result.dokumente_abgelegt.length} Dokument{result.dokumente_abgelegt.length !== 1 ? 'e' : ''} in Mieterakte abgelegt
              </div>
            )}
          </div>
        )}

        {/* Mahnwesen: Ergebnis-Anzeige */}
        {skill.id === 'mahnwesen' && result.zusammenfassung && (() => {
          const z = result.zusammenfassung;
          const eintraege = result.eintraege || [];
          const pdfFehler = eintraege.filter(e => e.pdf_status === 'fehler');
          const pdfErstellt = eintraege.filter(e => e.pdf_status === 'erstellt');
          const statusColors = {
            GEMAHNT: '#c6efce', LASTSCHRIFT: '#d6e4f0', VERRECHNEN: '#fce4d6',
            NICHT_GEMAHNT: '#ffc7ce', KEIN_BEDARF: '#ffeb9c', FEHLER: '#f4cccc',
          };
          const statusLabels = {
            GEMAHNT: 'Gemahnt', LASTSCHRIFT: 'Lastschrift', VERRECHNEN: 'Verrechnen',
            NICHT_GEMAHNT: 'Nicht gemahnt', KEIN_BEDARF: 'Kein Bedarf', FEHLER: 'Fehler',
          };
          const thStyle = { padding: '8px 8px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' };
          const tdStyle = { padding: '7px 8px', borderTop: '1px solid #e8e4df' };
          // Objekt-Gruppen bilden
          const objekte = {};
          for (const e of eintraege) {
            const key = e.anschrift || 'Unbekannt';
            if (!objekte[key]) objekte[key] = [];
            objekte[key].push(e);
          }
          return (
          <div style={{ marginTop: 20 }}>
            {/* Zusammenfassungs-Boxen */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              {[
                { label: 'Gemahnt', value: z.gemahnt, bg: '#c6efce', color: '#006100', icon: '\u2705' },
                { label: 'Lastschrift', value: z.lastschrift, bg: '#d6e4f0', color: '#1f4e79', icon: '\uD83D\uDD35' },
                { label: 'Verrechnen', value: z.verrechnen, bg: '#fce4d6', color: '#974706', icon: '\uD83D\uDFE0' },
                { label: 'Nicht gemahnt', value: z.nichtGemahnt, bg: '#ffc7ce', color: '#9c0006', icon: '\uD83D\uDD34' },
                { label: 'Kein Bedarf', value: z.keinBedarf, bg: '#ffeb9c', color: '#9c6500', icon: '\uD83D\uDFE1' },
              ].filter(b => b.value > 0).map(box => (
                <div key={box.label} style={{
                  padding: '8px 14px', borderRadius: 6, background: box.bg,
                  fontSize: 12, fontWeight: 600, color: box.color,
                  minWidth: 100, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 1 }}>{box.value}</div>
                  {box.label}
                </div>
              ))}
              <div style={{
                padding: '8px 14px', borderRadius: 6, background: '#f0f0f0',
                fontSize: 12, fontWeight: 600, color: '#333', minWidth: 80, textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 1 }}>{z.gesamt}</div>
                Gesamt
              </div>
            </div>

            {/* Warnhinweise */}
            {pdfFehler.length > 0 && (
              <div style={{
                padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107',
                borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#856404',
              }}>
                <strong>Dokumente fehlen:</strong> Bei {pdfFehler.length} Debitor(en) konnten die Mahndokumente (PDFs) nicht generiert werden.
                Bitte in Immoware manuell nachholen.
                <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                  {pdfFehler.slice(0, 5).map((e, i) => (
                    <li key={i}>{e.name} ({e.anschrift}) — {e.grund}</li>
                  ))}
                  {pdfFehler.length > 5 && <li>...und {pdfFehler.length - 5} weitere</li>}
                </ul>
              </div>
            )}

            {pdfErstellt.length > 0 && (
              <div style={{
                padding: '10px 14px', background: '#d4edda', border: '1px solid #28a745',
                borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#155724',
              }}>
                <strong>{pdfErstellt.length} Mahndokument(e) erfolgreich generiert.</strong>
              </div>
            )}

            {/* Protokoll-Download */}
            {result.protokoll_dateiname && (
              <div style={{
                padding: '10px 14px', background: '#fff', border: '1px solid var(--brand-warm-gray)',
                borderRadius: 6, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{ fontSize: 12, color: 'var(--brand-text)' }}>Mahnprotokoll: {result.protokoll_dateiname}</span>
                <a
                  href={`${API_URL}/skills/mahnwesen/protokoll/${encodeURIComponent(result.protokoll_dateiname)}?token=${encodeURIComponent(token)}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{
                    padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 4,
                    background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                    textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}
                >Protokoll anzeigen</a>
              </div>
            )}

            {/* Debitoren-Tabelle — gruppiert nach Objekt */}
            {eintraege.length > 0 && (
              <div style={{
                border: '1px solid var(--brand-warm-gray)', borderRadius: 6, overflow: 'hidden',
                maxHeight: 600, overflowY: 'auto',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--brand-cream)', position: 'sticky', top: 0, zIndex: 1 }}>
                      <th style={thStyle}>Debitor</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Schuld</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Saldo</th>
                      <th style={{ ...thStyle, textAlign: 'center', width: 40 }}>Anz.</th>
                      <th style={{ ...thStyle, width: 110 }}>Status</th>
                      <th style={{ ...thStyle, width: 24 }}>PDF</th>
                      <th style={thStyle}>Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(objekte).map(([anschrift, debitoren]) => (
                      <React.Fragment key={anschrift}>
                        {/* Objekt-Header */}
                        <tr style={{ background: 'var(--brand-navy)' }}>
                          <td colSpan={7} style={{
                            padding: '6px 8px', color: '#fff', fontWeight: 600, fontSize: 11,
                            letterSpacing: '0.3px',
                          }}>
                            {anschrift} ({debitoren.length} Debitor{debitoren.length !== 1 ? 'en' : ''})
                          </td>
                        </tr>
                        {debitoren.map((e, i) => {
                          const schuld = typeof e.schuld === 'number'
                            ? e.schuld.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC'
                            : (e.schuld ?? '\u2014');
                          const saldo = typeof e.saldo === 'number'
                            ? e.saldo.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' \u20AC'
                            : (e.saldo ?? '\u2014');
                          const pdfIcon = e.pdf_status === 'erstellt' ? '\u2705'
                            : e.pdf_status === 'fehler' ? '\u274C'
                            : e.pdf_status === 'uebersprungen' ? '\u23ED'
                            : '\u2014';
                          return (
                            <tr key={i} style={{ background: statusColors[e.status] || '#fff' }}>
                              <td style={{ ...tdStyle, fontWeight: 500 }}>{e.name}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{schuld}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{saldo}</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{e.anz}</td>
                              <td style={{ ...tdStyle }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
                                  background: 'rgba(255,255,255,0.5)',
                                }}>{statusLabels[e.status] || e.status}</span>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'center', fontSize: 13 }}>{pdfIcon}</td>
                              <td style={{ ...tdStyle, fontSize: 11, color: e.pdf_status === 'fehler' ? '#9c0006' : 'var(--brand-text-muted)' }}>{e.grund}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          );
        })()}

        {/* Generische Erfolgs-Box (nicht bei Begehung/SEPA/Mieteranlage/Mahnwesen review) */}
        {/* Eigentümer-Report Ergebnis */}
        {skill.id === 'eigentuemer-report' && result.output_files && (
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Zeitraum-Header */}
            {result.summary && (result.summary.zeitraum_von || result.summary.zeitraum_bis) && (
              <div style={{ padding: '12px 18px', background: 'var(--brand-navy)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Abrechnungszeitraum</span>
                <span style={{ fontSize: 15, color: '#fff', fontWeight: 600, fontFamily: 'var(--font-headline)' }}>
                  {result.summary.zeitraum_von} – {result.summary.zeitraum_bis}
                </span>
                {result.summary.anzahl_monate && (
                  <span style={{ fontSize: 12, color: 'var(--brand-gold)', fontWeight: 500 }}>({result.summary.anzahl_monate} Monate)</span>
                )}
              </div>
            )}
            {result.claude_summary && (
              <div style={{ padding: 16, background: 'var(--brand-cream)', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, fontSize: 13, color: 'var(--brand-text)', lineHeight: 1.6 }}>
                {result.claude_summary}
              </div>
            )}
            {result.summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                {[
                  { label: 'VE gesamt', value: result.summary.anzahl_ve },
                  { label: 'Vermietet', value: result.summary.anzahl_vermietet },
                  { label: 'Leer', value: result.summary.anzahl_leer },
                  { label: 'Leerstandsquote', value: result.summary.leerstandsquote },
                  { label: 'SOLL-Miete/Monat', value: result.summary.soll_miete_monat },
                  { label: 'IST-Einnahmen', value: result.summary.ist_einnahmen_gesamt },
                ].filter(x => x.value !== undefined && x.value !== null && x.value !== '' && x.value !== '0' && x.value !== 0).map((kpi, i) => (
                  <div key={i} style={{ padding: '14px 16px', background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{kpi.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 400, fontFamily: 'var(--font-headline)', color: 'var(--brand-navy)' }}>{kpi.value}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ padding: 16, background: 'var(--brand-success-bg)', border: '1px solid var(--brand-success)', borderRadius: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-success)', marginBottom: 10 }}>Report erstellt — Downloads</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {result.output_files.map((f, i) => {
                  const ext = f.split('.').pop().toUpperCase();
                  const colors = { HTML: ['#1d9e75', '#d1fae5'], XLSX: ['#217346', '#d1fae5'], PDF: ['#c0392b', '#fee2e2'] };
                  const [col, bg] = colors[ext] || ['var(--brand-gold)', 'var(--brand-gold-10)'];
                  return (
                    <a key={i} href={`/outputs/${f}?token=${token}`} download
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                        background: bg, borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600, color: col }}>
                      <span style={{ fontSize: 10, padding: '1px 5px', background: col, color: '#fff', borderRadius: 3 }}>{ext}</span>
                      {f.split('/').pop()}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Schwarzes Brett: Vorschlag zur Bestaetigung */}
        {skill.id === 'schwarzes-brett' && result.kategorie && (
          <SchwarzesBrettConfirm
            vorschlag={result}
            status={status}
            setStatus={setStatus}
            setError={setError}
            setResult={setResult}
            token={token}
            startTracking={startTracking}
            setActiveJobId={setActiveJobId}
          />
        )}

        {/* Schwarzes Brett: Posting-Ergebnis */}
        {skill.id === 'schwarzes-brett' && result.total_objects !== undefined && (
          <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', background: result.success ? 'var(--brand-success-bg)' : '#fee2e2', borderBottom: '1px solid var(--brand-warm-gray)' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: result.success ? 'var(--brand-success)' : '#c0392b' }}>
                {result.successful}/{result.total_objects} Einträge erstellt
              </span>
            </div>
            <div style={{ padding: '12px 20px' }}>
              {result.results?.map((r, i) => (
                <div key={i} style={{ padding: '8px 0', borderBottom: i < result.results.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700, background: r.success ? 'var(--brand-success-bg)' : '#fee2e2', color: r.success ? 'var(--brand-success)' : '#c0392b' }}>
                    {r.success ? 'OK' : 'FEHLER'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Objekt {r.object_id}</span>
                  {r.entry_id && (<>
                    <a href={`https://iris.awi-rems.de/objectdata/notice-board/show/o/${r.object_id}/go/${r.object_id}/dateCriteria/currentMessages/id/${r.entry_id}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, color: 'var(--brand-gold)', textDecoration: 'none', fontWeight: 600 }}>
                      Eintrag #{r.entry_id}
                    </a>
                    {r.portal_freigabe?.freigabe_url && (
                      <a href={r.portal_freigabe.freigabe_url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: 11, color: 'var(--brand-info)', textDecoration: 'none', fontWeight: 500, padding: '1px 6px', background: 'var(--brand-info-bg)', borderRadius: 3 }}>
                        Portal-Freigabe
                      </a>
                    )}
                  </>)}
                  {r.error && <span style={{ fontSize: 12, color: '#c0392b' }}>{r.error}</span>}
                </div>
              ))}
            </div>

            {/* WhatsApp-Ergebnis */}
            {result.whatsapp && result.whatsapp.total > 0 && (
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--brand-warm-gray)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14 }}>WhatsApp</span>
                  <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: result.whatsapp.sent > 0 ? '#25D366' : '#fee2e2', color: result.whatsapp.sent > 0 ? '#fff' : '#c0392b' }}>
                    {result.whatsapp.sent}/{result.whatsapp.total} gesendet
                  </span>
                </div>
                {result.whatsapp.results?.map((w, i) => (
                  <div key={i} style={{ fontSize: 11, padding: '3px 0', color: w.success ? 'var(--brand-success)' : '#c0392b' }}>
                    {w.success ? '✓' : '\u2717'} {w.name} ({w.telefon}) {w.error || ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Kontobestände: Erfolg + Download */}
        {skill.id === 'kontobestaende' && (
        <div style={{
          marginTop: 20, padding: 20, background: 'var(--brand-success-bg)',
          border: '1px solid var(--brand-success)', borderRadius: 6,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brand-success)' }}>
              Erfolgreich abgeschlossen
            </div>
            <div style={{ fontSize: 14, color: 'var(--brand-text)', marginTop: 4 }}>{result.summary}</div>
          </div>
          {result.files?.length > 0 && (
            <a href={`/outputs/${result.files[0]}?token=${token}`} download style={{
              padding: '8px 16px', background: 'var(--brand-gold)', color: 'var(--brand-navy)',
              borderRadius: 4, fontSize: 12, fontWeight: 600, textDecoration: 'none',
              textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
            }}>HTML herunterladen</a>
          )}
        </div>
        )}

        {/* Aushang: Preview-Phase mit editierbarem Text */}
        {skill.id === 'aushang' && result.phase === 'preview' && (
          <AushangPreview
            result={result}
            token={token}
            setStatus={setStatus}
            setResult={setResult}
            setError={setError}
          />
        )}

        {!(skill.id === 'begehung-tickets' && (result.phase === 'analyzed' || result.phase === 'created')) && !(skill.id === 'sepa') && !(skill.id === 'mieteranlage') && !(skill.id === 'mahnwesen') && !(skill.id === 'eigentuemer-report') && !(skill.id === 'schwarzes-brett') && !(skill.id === 'kontobestaende') && !(skill.id === 'aushang' && result.phase === 'preview') && (
        <div style={{
          marginTop: 20, padding: 20, background: 'var(--brand-success-bg)',
          border: '1px solid var(--brand-success)', borderRadius: 6,
        }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--brand-success)', marginBottom: 8 }}>
            Erfolgreich abgeschlossen
          </div>
          <div style={{ fontSize: 14, color: 'var(--brand-text)' }}>{result.summary}</div>
          {result.files?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong style={{ fontSize: 13 }}>Downloads:</strong>
              {result.files.map((f, i) => {
                const isObj = typeof f === 'object' && f !== null;
                const fileUrl = isObj ? `${f.url}?token=${token}` : `/outputs/${f}?token=${token}`;
                const fileName = isObj ? f.name : f.split('/').pop();
                return (
                  <a key={i} href={fileUrl} download style={{
                    display: 'block', marginTop: 6, color: 'var(--brand-gold)', fontSize: 13, fontWeight: 500,
                  }}>{fileName}</a>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Begehungs-Tickets: Phase "analyzed" → Editierbare Bestätigung */}
        {skill.id === 'begehung-tickets' && result.phase === 'analyzed' && result.analyse && (
          <BegehungConfirm
            analyse={result.analyse}
            ticketsJson={result.tickets_json}
            uploadPaths={result.upload_paths}
            status={status}
            setStatus={setStatus}
            setError={setError}
            setResult={setResult}
            setFiles={setFiles}
            token={token}
            startTracking={startTracking}
            job={job}
            setHistoryVersion={setHistoryVersion}
          />
        )}

        {/* Begehungs-Tickets: Phase "created" → Ergebnis mit Ticket-Links */}
        {skill.id === 'begehung-tickets' && result.phase === 'created' && (
          <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden' }}>
            <div style={{
              padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
              fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>Erstellte Tickets</div>
            <div style={{ padding: '12px 20px' }}>
              {result.hauptticket && (
                <div style={{ padding: '8px 0', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                  <a href={`https://athene.awi-rems.de/order/ticket/view/id/${result.hauptticket.id}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-gold)', textDecoration: 'none' }}>
                    #{result.hauptticket.id}
                  </a>
                  <span style={{ fontSize: 12, color: 'var(--brand-text)', marginLeft: 8 }}>{result.hauptticket.titel}</span>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--brand-success-bg)', color: 'var(--brand-success)', fontWeight: 600, marginLeft: 8 }}>HAUPTTICKET</span>
                </div>
              )}
              {result.untertickets?.map((t, i) => (
                <div key={i} style={{ padding: '8px 0', borderBottom: i < result.untertickets.length - 1 ? '1px solid var(--brand-warm-gray)' : 'none' }}>
                  <a href={`https://athene.awi-rems.de/order/ticket/view/id/${t.id}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-gold)', textDecoration: 'none' }}>
                    #{t.id}
                  </a>
                  <span style={{ fontSize: 12, color: 'var(--brand-text)', marginLeft: 8 }}>{t.titel}</span>
                </div>
              ))}
              {result.fehler?.length > 0 && result.fehler.map((f, i) => (
                <div key={i} style={{ padding: '8px 0', fontSize: 12, color: 'var(--brand-danger)' }}>
                  Fehler: {f.titel} — {f.fehler}
                </div>
              ))}
            </div>
            <div style={{
              padding: '10px 20px', background: 'var(--brand-cream)',
              borderTop: '1px solid var(--brand-warm-gray)',
              fontSize: 12, color: 'var(--brand-text-muted)',
            }}>
              Begehungsprotokoll-PDF bitte manuell beim Hauptticket hochladen.
            </div>
          </div>
        )}

        {/* Scoring (Mietvertragsprüfung) */}
        {result.scoring && (
          <ScoringDisplay scoring={result.scoring} extractedData={result.extracted_data} />
        )}

        {/* Dokumente erstellen — Button oder Freigabe-Anforderung bei ABSAGE (nur Mietvertrag) */}
        {skill.id === 'mietvertrag' && result.extracted_data && !editData && (() => {
          const isAbsage = result.extracted_data.empfehlung === 'ABSAGE';
          const needsFreigabe = isAbsage && freigabeStatus !== 'genehmigt' && !isAdmin;
          const handleFreigabeRequest = async () => {
            const res = await fetch(`${API_URL}/freigabe/request`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ resource_type: 'mietvertrag_absage', resource_id: executionId, grund: freigabeGrund }),
            });
            if (res.ok) setFreigabeStatus('angefragt');
          };
          const handleFreigabeRefresh = async () => {
            const res = await fetch(`${API_URL}/freigabe/check/mietvertrag_absage/${executionId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const d = await res.json();
              setFreigabeStatus(d.status || null);
            }
          };

          if (needsFreigabe) return (
            <div style={{
              marginTop: 16, padding: 20, background: '#fff',
              border: '1px solid var(--brand-danger)', borderRadius: 8,
            }}>
              {freigabeStatus === 'angefragt' ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-gold)', marginBottom: 8 }}>
                    Freigabe angefragt
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 12 }}>
                    Warte auf Genehmigung durch Teamleitung / Admin...
                  </div>
                  <button onClick={handleFreigabeRefresh} style={{
                    padding: '8px 20px', fontSize: 12, fontWeight: 500,
                    background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                    color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                  }}>Status aktualisieren</button>
                </div>
              ) : freigabeStatus === 'abgelehnt' ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-danger)', marginBottom: 4 }}>
                    Freigabe abgelehnt
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
                    Die Dokumentenerstellung wurde von der Teamleitung abgelehnt.
                  </div>
                </div>
              ) : (
                <>
                  <div style={{
                    padding: '10px 14px', background: 'var(--brand-danger-bg)', borderRadius: 6,
                    border: '1px solid var(--brand-danger)', marginBottom: 14,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-danger)' }}>
                      Empfehlung: ABSAGE
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text)', marginTop: 4 }}>
                      Dokumentenerstellung erfordert eine Freigabe durch Teamleitung / Admin.
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Begründung (optional)
                    </label>
                    <textarea
                      value={freigabeGrund}
                      onChange={e => setFreigabeGrund(e.target.value)}
                      placeholder="Warum soll trotz Absage ein Mietvertrag erstellt werden?"
                      rows={3}
                      style={{
                        width: '100%', padding: '8px 10px', border: '1px solid var(--brand-warm-gray)',
                        borderRadius: 4, fontSize: 13, fontFamily: 'var(--font-body)',
                        resize: 'vertical', minHeight: 60,
                      }}
                    />
                  </div>
                  <button onClick={handleFreigabeRequest} style={{
                    padding: '12px 28px', fontSize: 13, fontWeight: 600, width: '100%',
                    background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                  }}>Freigabe anfordern</button>
                </>
              )}
            </div>
          );

          // Normal: Dokumente erstellen (kein ABSAGE, oder ABSAGE mit Freigabe, oder Admin)
          return (
            <div style={{
              marginTop: 16, padding: 20, background: '#fff',
              border: `1px solid ${result.phase === 'analyzed' ? 'var(--brand-gold)' : 'var(--brand-warm-gray)'}`,
              borderRadius: 8, textAlign: 'center',
            }}>
              {isAbsage && (
                <div style={{
                  fontSize: 11, padding: '4px 10px', background: isAdmin ? 'var(--brand-gold-10)' : 'var(--brand-success-bg)',
                  color: isAdmin ? 'var(--brand-gold)' : 'var(--brand-success)',
                  borderRadius: 4, display: 'inline-block', marginBottom: 10, fontWeight: 600,
                }}>
                  {isAdmin ? 'Admin-Zugriff' : 'Freigabe erteilt'}
                </div>
              )}
              <div style={{ fontSize: 14, color: 'var(--brand-navy)', marginBottom: 12 }}>
                {result.phase === 'analyzed'
                  ? 'Analyse abgeschlossen. Daten pruefen und Mieterdaten ergaenzen?'
                  : 'Daten erneut pruefen und Dokumente generieren?'
                }
              </div>
              <button onClick={handleGenerateDocsClick} disabled={status === 'running'}
                style={{
                  padding: '12px 28px', fontSize: 13, fontWeight: 600,
                  background: status === 'running' ? 'var(--brand-text-muted)' : 'var(--brand-navy)',
                  color: status === 'running' ? '#fff' : 'var(--brand-gold)',
                  border: 'none', borderRadius: 4, cursor: status === 'running' ? 'wait' : 'pointer',
                  textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                }}
              >
                {status === 'running' ? 'Wird erstellt...'
                  : result.phase === 'analyzed' ? 'Daten pruefen & weiter' : 'Dokumente erneut generieren'}
              </button>
              {progress && (
                <div style={{ marginTop: 12, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{progress.message}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{progress.percent}%</span>
                  </div>
                  <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${progress.percent}%`, height: '100%', background: 'var(--brand-gold)', borderRadius: 3, transition: 'width 0.8s ease' }} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Editierbares Datenblatt vor Dokumentenerstellung (nur Mietvertrag) */}
        {editData && skill.id === 'mietvertrag' && (
          <EditDataSheet data={editData} onChange={setEditData} onSubmit={handleDataReviewed} onCancel={() => setEditData(null)} submitLabel="Weiter zu Mieterdaten" />
        )}

        {/* ═══ Mieterdaten ergänzen (nur Mietvertrag, nach Datenprüfung, vor Dokumentenerstellung) ═══ */}
        {skill.id === 'mietvertrag' && (reviewedData || tenantFormStatus) && !editData && result.phase !== 'documents_created' && (() => {
          const hasMieter2 = !!(result.extracted_data?.mieter2_name || '').trim();
          const mieter1Name = result.extracted_data?.mieter_name || 'Mieter 1';
          const mieter2Name = result.extracted_data?.mieter2_name || '';
          const tenantDataComplete = tenantFormStatus === 'submitted';

          return (
            <div style={{ marginTop: 16, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-gold)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Schritt 2: Mieterdaten ergaenzen
              </div>
              <div style={{ padding: 20 }}>
                {/* Noch keine Daten: Zwei Optionen */}
                {!tenantDataComplete && !showTenantInlineForm && (!tenantFormStatus || tenantFormStatus === 'not_created') && (
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
                      Die Mieterdaten (E-Mail, Telefon, Notfallkontakt, IBAN etc.) werden in die Dokumente uebernommen und mitunterschrieben.
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={() => handleCreateTenantFormForDocs('sachbearbeiter')} disabled={tenantFormLoading} style={{
                        flex: 1, padding: '14px 16px', fontSize: 13, fontWeight: 600,
                        background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                        border: 'none', borderRadius: 4, cursor: tenantFormLoading ? 'wait' : 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'var(--font-body)',
                        opacity: tenantFormLoading ? 0.6 : 1,
                      }}>Selbst ausfuellen</button>
                      <button onClick={() => handleCreateTenantFormForDocs('mieter_link')} disabled={tenantFormLoading} style={{
                        flex: 1, padding: '14px 16px', fontSize: 13, fontWeight: 600,
                        background: '#fff', color: 'var(--brand-navy)',
                        border: '2px solid var(--brand-navy)', borderRadius: 4,
                        cursor: tenantFormLoading ? 'wait' : 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.3px', fontFamily: 'var(--font-body)',
                        opacity: tenantFormLoading ? 0.6 : 1,
                      }}>Link an Mieter senden</button>
                    </div>
                  </div>
                )}

                {/* Sachbearbeiter pending — Formular Button */}
                {!tenantDataComplete && !showTenantInlineForm && tenantFormStatus === 'pending' && tenantFormFilledBy === 'sachbearbeiter' && (
                  <button onClick={() => setShowTenantInlineForm(true)} style={{
                    padding: '12px 24px', fontSize: 13, fontWeight: 600, width: '100%',
                    background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                  }}>Mieterdaten eintragen</button>
                )}

                {/* Mieter-Link warte */}
                {!tenantDataComplete && !showTenantInlineForm && tenantFormStatus === 'pending' && tenantFormFilledBy === 'mieter' && (
                  <div>
                    <div style={{ padding: '12px 16px', background: 'var(--brand-cream)', borderRadius: 6, border: '1px solid var(--brand-warm-gray)', marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Formular-Link</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="text" value={tenantFormUrl} readOnly style={{ flex: 1, padding: '8px 10px', fontSize: 11, fontFamily: 'monospace', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff' }} />
                        <button onClick={() => { navigator.clipboard.writeText(tenantFormUrl); setTenantLinkCopied(true); setTimeout(() => setTenantLinkCopied(false), 2000); }} style={{
                          padding: '8px 16px', fontSize: 11, fontWeight: 600,
                          background: tenantLinkCopied ? 'var(--brand-success)' : 'var(--brand-gold)',
                          color: tenantLinkCopied ? '#fff' : 'var(--brand-navy)',
                          border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)', minWidth: 100,
                        }}>{tenantLinkCopied ? 'Kopiert!' : 'Link kopieren'}</button>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, background: 'rgba(196,151,90,0.15)', color: 'var(--brand-gold)', marginBottom: 8 }}>Ausstehend</div>
                      <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginBottom: 12 }}>Der Mieter hat den Formular-Link noch nicht ausgefuellt.</div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        <button onClick={handleRefreshTenantFormForDocs} style={{ padding: '8px 20px', fontSize: 12, fontWeight: 500, background: 'transparent', border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)', borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Aktualisieren</button>
                        <button onClick={() => { setShowTenantInlineForm(true); setTfKlingelschild(result.extracted_data?.mieter_name || ''); }} style={{ padding: '8px 20px', fontSize: 12, fontWeight: 500, background: 'transparent', border: '1px solid var(--brand-gold)', color: 'var(--brand-gold)', borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Doch selbst ausfuellen</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Inline-Formular */}
                {showTenantInlineForm && !tenantDataComplete && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                      Mieter 1 {mieter1Name ? `\u2014 ${mieter1Name}` : ''}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                      <InlineField label="E-Mail" required type="email" value={tfEmailMieter1} onChange={e => { setTfEmailMieter1(e.target.value); setTfErrors(p => ({...p, e1: undefined})); }} placeholder="email@beispiel.de" error={tfErrors.e1} />
                      <InlineField label="Telefon" required type="tel" value={tfTelefonMieter1} onChange={e => { setTfTelefonMieter1(e.target.value); setTfErrors(p => ({...p, t1: undefined})); }} placeholder="+49 170 1234567" error={tfErrors.t1} />
                      <InlineField label="Notfallkontakt Name" required value={tfNotfallName1} onChange={e => { setTfNotfallName1(e.target.value); setTfErrors(p => ({...p, n1: undefined})); }} placeholder="Name" error={tfErrors.n1} />
                      <InlineField label="Notfallkontakt Telefon" required type="tel" value={tfNotfallTelefon1} onChange={e => { setTfNotfallTelefon1(e.target.value); setTfErrors(p => ({...p, nt1: undefined})); }} placeholder="+49 170 1234567" error={tfErrors.nt1} />
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8, marginBottom: 4 }}>Bankverbindung (SEPA)</div>
                    <InlineField label="IBAN" value={tfIban1} onChange={e => setTfIban1(e.target.value.toUpperCase())} placeholder="DE..." maxLength={27} mono />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                      <InlineField label="Kontoinhaber" value={tfKontoinhaber1} onChange={e => setTfKontoinhaber1(e.target.value)} placeholder="Vor- und Nachname" />
                      <InlineField label="BIC" value={tfBic1} onChange={e => setTfBic1(e.target.value.toUpperCase())} placeholder="z.B. COBADEFFXXX" maxLength={11} mono />
                    </div>

                    {hasMieter2 && (<>
                      <div style={{ height: 1, background: 'var(--brand-warm-gray)', margin: '8px 0 16px' }} />
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
                        Mieter 2 \u2014 {mieter2Name}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                        <InlineField label="E-Mail" required type="email" value={tfEmailMieter2} onChange={e => { setTfEmailMieter2(e.target.value); setTfErrors(p => ({...p, e2: undefined})); }} placeholder="email@beispiel.de" error={tfErrors.e2} />
                        <InlineField label="Telefon" required type="tel" value={tfTelefonMieter2} onChange={e => { setTfTelefonMieter2(e.target.value); setTfErrors(p => ({...p, t2: undefined})); }} placeholder="+49 170 1234567" error={tfErrors.t2} />
                        <InlineField label="Notfallkontakt Name" value={tfNotfallName2} onChange={e => setTfNotfallName2(e.target.value)} placeholder="optional" />
                        <InlineField label="Notfallkontakt Telefon" type="tel" value={tfNotfallTelefon2} onChange={e => setTfNotfallTelefon2(e.target.value)} placeholder="optional" />
                      </div>
                      <InlineField label="IBAN (SEPA-Lastschrift)" value={tfIban2} onChange={e => setTfIban2(e.target.value.toUpperCase())} placeholder="DE..." maxLength={27} mono />
                    </>)}

                    <div style={{ height: 1, background: 'var(--brand-warm-gray)', margin: '8px 0 16px' }} />
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>Allgemein</div>
                    <InlineField label="Name Klingelschild" required value={tfKlingelschild} onChange={e => { setTfKlingelschild(e.target.value); setTfErrors(p => ({...p, ks: undefined})); }} placeholder="Name auf dem Klingelschild" error={tfErrors.ks} />
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', display: 'block', marginBottom: 3 }}>Anmerkungen</label>
                      <textarea value={tfAnmerkungen} onChange={e => setTfAnmerkungen(e.target.value)} placeholder="optional" style={{ width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 4, border: '1px solid var(--brand-warm-gray)', fontFamily: 'var(--font-body)', resize: 'vertical', minHeight: 50, boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleTenantInlineSubmit} disabled={tenantInlineSubmitting} style={{
                        flex: 1, padding: '12px 20px', fontSize: 13, fontWeight: 600,
                        background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                        border: 'none', borderRadius: 4, cursor: tenantInlineSubmitting ? 'wait' : 'pointer',
                        textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                        opacity: tenantInlineSubmitting ? 0.6 : 1,
                      }}>{tenantInlineSubmitting ? 'Wird gespeichert...' : 'Daten speichern'}</button>
                      <button onClick={() => { setShowTenantInlineForm(false); setTfErrors({}); }} style={{
                        padding: '12px 16px', fontSize: 13, background: 'transparent',
                        border: '1px solid var(--brand-warm-gray)', color: 'var(--brand-text-muted)',
                        borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                      }}>Zurueck</button>
                    </div>
                  </div>
                )}

                {/* Daten eingegangen — Zusammenfassung + Dokumente erstellen */}
                {tenantDataComplete && tenantFormData && (
                  <div>
                    <div style={{ padding: '12px 16px', background: 'var(--brand-success-bg, #F0FDF4)', border: '1px solid var(--brand-success)', borderRadius: 6, marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: 'var(--brand-success-bg)', color: 'var(--brand-success)', border: '1px solid var(--brand-success)' }}>Eingegangen</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 11, color: 'var(--brand-text)' }}>
                        {tenantFormData.name_klingelschild && <div><strong>Klingelschild:</strong> {tenantFormData.name_klingelschild}</div>}
                        {tenantFormData.email_mieter1 && <div><strong>E-Mail 1:</strong> {tenantFormData.email_mieter1}</div>}
                        {tenantFormData.telefon_mieter1 && <div><strong>Telefon 1:</strong> {tenantFormData.telefon_mieter1}</div>}
                        {tenantFormData.notfallkontakt_name_mieter1 && <div><strong>Notfall 1:</strong> {tenantFormData.notfallkontakt_name_mieter1}</div>}
                        {tenantFormData.email_mieter2 && <div><strong>E-Mail 2:</strong> {tenantFormData.email_mieter2}</div>}
                        {tenantFormData.telefon_mieter2 && <div><strong>Telefon 2:</strong> {tenantFormData.telefon_mieter2}</div>}
                      </div>
                    </div>
                    <button onClick={doGenerateDocs} disabled={status === 'running'} style={{
                      width: '100%', padding: '14px 28px', fontSize: 14, fontWeight: 600,
                      background: status === 'running' ? 'var(--brand-text-muted)' : 'var(--brand-gold)',
                      color: status === 'running' ? '#fff' : 'var(--brand-navy)',
                      border: 'none', borderRadius: 4, cursor: status === 'running' ? 'wait' : 'pointer',
                      textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: 'var(--font-body)',
                    }}>{status === 'running' ? 'Wird erstellt...' : 'Dokumente erstellen'}</button>
                    {progress && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>{progress.message}</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--brand-gold)' }}>{progress.percent}%</span>
                        </div>
                        <div style={{ width: '100%', height: 6, background: 'var(--brand-cream)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${progress.percent}%`, height: '100%', background: 'var(--brand-gold)', borderRadius: 3, transition: 'width 0.8s ease' }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Kautionskonto-Ergebnis */}
        {result.kaution?.success && (
          <div style={{
            marginTop: 16, padding: 16, background: '#fff', borderRadius: 8,
            border: '1px solid var(--brand-success)', fontSize: 13,
          }}>
            <div style={{ fontWeight: 600, color: 'var(--brand-success)', marginBottom: 8 }}>Kautionskonto erstellt</div>
            <div>IBAN: <strong>{result.kaution.iban_formatted}</strong></div>
            <div>BIC: {result.kaution.bic}</div>
            <div>Inhaber: {result.kaution.inhaber}</div>
            {result.kaution.app_link && (
              <a href={result.kaution.app_link} target="_blank" rel="noreferrer"
                style={{ color: 'var(--brand-gold)', fontWeight: 500, marginTop: 8, display: 'inline-block' }}>
                In Getmomo öffnen →
              </a>
            )}
          </div>
        )}
        {result.kaution && !result.kaution.success && (
          <div style={{
            marginTop: 16, padding: 16, background: 'var(--brand-warning-bg)',
            border: '1px solid var(--brand-warning)', borderRadius: 8, fontSize: 13,
            color: 'var(--brand-warning)',
          }}>
            Kautionskonto: {result.kaution.error}
          </div>
        )}

        {/* Digitale Signatur (Phase 3 — nach Dokumentenerstellung) */}
        {skill.id === 'mietvertrag' && result.phase === 'documents_created' && executionId && (
          <SigningSection
            executionId={executionId}
            extractedData={result.extracted_data}
            token={token}
          />
        )}

        {/* Warnungen (z.B. fehlende Mietkonto-IBAN) */}
        {result.warnings?.length > 0 && (
          <div style={{
            marginTop: 16, padding: 16, background: 'var(--brand-warning-bg)',
            border: '1px solid var(--brand-warning)', borderRadius: 8,
          }}>
            {result.warnings.map((w, i) => (
              <div key={i} style={{ fontSize: 13, color: 'var(--brand-warning)', marginBottom: i < result.warnings.length - 1 ? 6 : 0 }}>
                ⚠ {w}
              </div>
            ))}
          </div>
        )}

        {/* Prüfbericht (Mietvertragserstellung) */}
        {result.report && (
          <div style={{
            marginTop: 16, background: '#fff', borderRadius: 8,
            border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
          }}>
            <div style={{
              padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
              fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>Prüfbericht</div>
            <div style={{ padding: 20 }}>
              <style>{`
                .pruefbericht table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
                .pruefbericht th { background: var(--brand-cream); padding: 8px 12px; text-align: left; font-weight: 600; color: var(--brand-navy); border: 1px solid var(--brand-warm-gray); font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
                .pruefbericht td { padding: 8px 12px; border: 1px solid var(--brand-warm-gray); color: var(--brand-text); }
                .pruefbericht h3 { font-family: var(--font-headline); font-size: 18px; color: var(--brand-navy); margin: 24px 0 8px; font-weight: 400; }
                .pruefbericht h4 { font-size: 14px; color: var(--brand-navy); margin: 20px 0 8px; font-weight: 600; border-bottom: 1px solid var(--brand-warm-gray); padding-bottom: 6px; }
                .pruefbericht ul { margin: 8px 0; padding-left: 20px; }
                .pruefbericht li { margin: 4px 0; }
                .pruefbericht strong { color: var(--brand-navy); }
              `}</style>
              <MarkdownReport text={result.report} />
            </div>
          </div>
        )}
      </>)}
    </div>
  );
}

// ── Skills Übersicht ──

// ── Skill Icon SVG Map (monochrome Fleet Blue) ──
const SKILL_SVG = {
  // Mietverwaltung
  '📋': '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>',
  '🏠': '<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/>',
  '📊': '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
  // WEG
  '🏗️': '<path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 14l2 2 4-4"/>',
  // Buchhaltung
  '⚠️': '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
  '🏦': '<rect x="3" y="10" width="18" height="11" rx="1"/><path d="M12 2L2 10h20L12 2z"/><line x1="7" y1="14" x2="7" y2="17"/><line x1="12" y1="14" x2="12" y2="17"/><line x1="17" y1="14" x2="17" y2="17"/>',
  // Allgemein
  '🔐': '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>',
  '📢': '<path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/>',
  '✉️': '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>',
  '✉': '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>',
  '📈': '<polyline points="22 12 18 8 13 13 9 9 2 16"/><polyline points="16 8 22 8 22 14"/>',
};
// Fallback: generic document icon
const SKILL_SVG_DEFAULT = '<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>';

function SkillSvgIcon({ emoji }) {
  const paths = SKILL_SVG[emoji] || SKILL_SVG_DEFAULT;
  return (
    <div className="skill-card__icon">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00a87e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: paths }} />
    </div>
  );
}


export default function Skills() {
  const { skillId } = useParams();
  const { token } = useAuth();
  const [skills, setSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/skills/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setSkills)
      .catch(() => setLoadError(true));
  }, [token]);

  const navigate = useNavigate();

  useEffect(() => {
    if (skillId) {
      fetch(`${API_URL}/skills/${skillId}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(setSelectedSkill)
        .catch(() => setSelectedSkill(null));
    } else {
      setSelectedSkill(null);
    }
  }, [skillId, token]);

  if (selectedSkill) return <SkillForm skill={selectedSkill} />;

  return (
    <div>
      <h1 className="skill-page__title">Skills</h1>
      <p className="skill-page__subtitle">
        Wählen Sie einen Workflow aus, um ihn zu starten.
      </p>
      {loadError && skills.length === 0 ? (
        <div style={{
          background: 'var(--one-surface)', border: '1px solid var(--border-light)',
          borderRadius: 16, padding: '2.5rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚡</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, fontFamily: 'Inter, system-ui, sans-serif' }}>
            Keine Skills verfügbar
          </div>
        </div>
      ) : (() => {
        const categoryOrder = [
          { key: 'mietverwaltung', label: 'Mietverwaltung' },
          { key: 'weg_verwaltung', label: 'WEG-Verwaltung' },
          { key: 'buchhaltung', label: 'Buchhaltung' },
          { key: 'allgemein', label: 'Allgemein' },
        ];
        const grouped = {};
        skills.forEach(s => {
          const cat = s.category || 'allgemein';
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(s);
        });
        return categoryOrder
          .filter(cat => grouped[cat.key]?.length > 0)
          .map(cat => (
          <div key={cat.key} style={{ marginBottom: 40 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 20 }}>
              <h2 className="skill-category__heading">{cat.label}</h2>
              <span className="skill-category__count">{grouped[cat.key].length}</span>
            </div>
            <div className="skill-grid">
              {grouped[cat.key].map(skill => (
                <Link key={skill.id} to={'/skills/' + skill.id} className="skill-card">
                  <SkillSvgIcon emoji={skill.icon} />
                  <div className="skill-card__title">{skill.name}</div>
                  <div className="skill-card__body">{skill.description}</div>
                  <div className="skill-card__link">
                    Starten <span className="skill-card__arrow">→</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ));
      })()}
    </div>
  );
}
